import React, { useState, useEffect } from "react";
import "./App.css";
import "./animations.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import * as animations from "./utils/animations";
import FileUpload from "./components/FileUpload";
import ChallengeProgressModal from "./components/ChallengeProgressModal";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);
import CampaignForm from "./components/CampaignForm";
import NotificationBadge from "./components/NotificationBadge";
import Footer from "./components/Footer";
import WeeklyCheckInPrompt from "./components/WeeklyCheckInPrompt";
import SharedGoalsBoard from "./components/SharedGoalsBoard";
import BusinessHabitTracker from "./components/HabitTrackerClean";
import MatchingPreferencesModal from "./components/MatchingPreferencesModal";
import PartnerHabitsSummary from "./components/PartnerHabitsSummary";

// Public Pages
import FeaturesPage from "./pages/FeaturesPage";
import EventsPage from "./pages/EventsPage";
import MagazinePage from "./pages/MagazinePage";
import Pricing from "./pages/Pricing";
import Security from "./pages/Security";
import SuccessStories from "./pages/SuccessStories";
import HelpCenter from "./pages/HelpCenter";
import ContactUs from "./pages/ContactUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import { 
  BarChart3, 
  Trophy, 
  MessageCircle, 
  Calendar, 
  Megaphone, 
  Target, 
  Users,
  User,
  Upload,
  RefreshCw,
  BookOpen, 
  Settings,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  Award,
  Clock,
  MapPin,
  Heart,
  Share2,
  ThumbsUp,
  UserPlus,
  Instagram,
  Twitter,
  Facebook,
  Linkedin,
  Plus,
  Edit,
  Edit3,
  Trash2,
  DollarSign,
  Crown,
  BarChart2,
  Activity,
  Calendar as CalendarIcon,
  ToggleLeft,
  ToggleRight,
  ArrowRight,
  Sparkles,
  Shield,
  Globe,
  Key,
  Home,
  CheckCircle,
  XCircle,
  X,
  PieChart as PieChartIcon,
  TrendingDown, // Added analytics icons
  Download, // Added Download icon for analytics
  Eye,
  Mail,
  Phone,
  ExternalLink,
  Copy,
  Search, // Added user management icons
  Filter,
  UserCheck,
  UserX,
  UserMinus,
  Pause,
  Play,
  Ban,
  Send, // Added bulk operations icons  
  FileText,
  Users as UsersIcon,
  CheckSquare,
  Video, // Added for video calls
  AlertCircle, // Added for alerts
  Info, // Added for info notifications
  Shuffle,
  Star,
  Square,
  Zap,
  MessageSquare,
  Menu // Added for collapsible sidebar
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Progress } from "./components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Button } from "./components/ui/button";

// Import Recharts for analytics
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

// Import new auth components
import RoleSelection from "./components/auth/RoleSelection";
import LoginScreen from "./components/auth/LoginScreen";
import MemberSignup from "./components/auth/MemberSignup";
import HostSignup from "./components/auth/HostSignup";
import EmailVerification from "./components/auth/EmailVerification";
import PaymentSuccess from "./components/auth/PaymentSuccess";
import PaymentCancelled from "./components/auth/PaymentCancelled";
import PaymentTestPage from "./components/PaymentTestPage";
import VerifyEmail from "./components/auth/VerifyEmail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Home Screen Component

// Accountability Rules Carousel Component
const AccountabilityRulesCarousel = () => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  
  const rules = [
    {
      number: 1,
      title: "Choose or Be Matched with a Partner",
      description: "Pick someone with similar goals or let the app connect you with a random partner. This is your teammate to help you push towards your goals!"
    },
    {
      number: 2,
      title: "Set Your Goals",
      description: "Work together to define clear goals. Be honest, ambitious, and make sure the goals reflect the best version of yourself."
    },
    {
      number: 3,
      title: "Check-in Regularly",
      description: "Keep each other updated on progress. Check in daily or weekly to stay on track. This isn't just about points—it's about owning your progress!"
    },
    {
      number: 4,
      title: "Support Each Other",
      description: "Encourage your partner. The stronger they get, the stronger you get. Motivation goes both ways!"
    },
    {
      number: 5,
      title: "Earn Points for Staying on Track",
      description: "Hit your goals and check in consistently to earn points. If you both reach your goals, you'll unlock extra points and recognition."
    },
    {
      number: 6,
      title: "New Partner Every 2 Weeks",
      description: "Every 2 weeks, you'll be paired with a new partner. Use this chance to connect, grow, and challenge yourself even more."
    },
    {
      number: 7,
      title: "Resolve Issues Quickly",
      description: "If something isn't working, use the app to find a solution or switch partners. Don't let anything hold you back from reaching your potential."
    },
    {
      number: 8,
      title: "Review Your Partner",
      description: "At the end of each 2-week period, you can rate and review your partner. Give feedback on their support, encouragement, and accountability. Reviews help everyone improve and find better partnerships."
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % rules.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + rules.length) % rules.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 mb-6">
      <CardHeader>
        <CardTitle className="text-amber-900 text-2xl font-bold text-center">Accountability Partner Rules</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Carousel Content */}
          <div className="text-center min-h-[250px] flex flex-col items-center justify-center px-8 py-6">
            <div className="w-16 h-16 rounded-full bg-amber-600 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
              {rules[currentSlide].number}
            </div>
            <h3 className="text-xl font-bold text-amber-900 mb-4">
              {rules[currentSlide].title}
            </h3>
            <p className="text-amber-800 text-base leading-relaxed max-w-2xl">
              {rules[currentSlide].description}
            </p>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots Navigation */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {rules.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="transition-all duration-300 rounded-full"
                style={{
                  width: currentSlide === index ? '28px' : '10px',
                  height: '10px',
                  background: currentSlide === index ? '#d97706' : 'rgba(217, 119, 6, 0.3)',
                }}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


const HomeScreen = () => {
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = React.useState('elysian-modern'); // midnight-elegance, daylight-gold, modern-noir, elysian-gold, elysian-dark, ethereal-gold, editorial-luxe, emerald-editorial, balanced-modern, elysian-modern
  const [showIntro, setShowIntro] = React.useState(currentTheme === 'modern-noir');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  console.log('🔴 HomeScreen loaded - NEW VERSION with carousel!');

  // Load theme CSS dynamically
  React.useEffect(() => {
    const link = document.getElementById('theme-stylesheet') || document.createElement('link');
    link.id = 'theme-stylesheet';
    link.rel = 'stylesheet';
    link.href = `/themes/${currentTheme}.css`;
    
    if (!document.getElementById('theme-stylesheet')) {
      document.head.appendChild(link);
    }
    
    // Reset intro animation if switching to modern-noir
    if (currentTheme === 'modern-noir') {
      setShowIntro(true);
      setTimeout(() => setShowIntro(false), 2000);
    }
    
    // Load GSAP for Elysian themes
    if ((currentTheme === 'elysian-gold' || currentTheme === 'elysian-dark') && window.gsap) {
      initElysianAnimations();
    }
    
    // Load Ethereal Gold animations
    if (currentTheme === 'ethereal-gold' && window.gsap) {
      initEtherealAnimations();
    }
    
    // Load Editorial Luxe animations
    if (currentTheme === 'editorial-luxe' && window.gsap) {
      initEditorialAnimations();
    }
    
    // Load Emerald Editorial animations
    if (currentTheme === 'emerald-editorial' && window.gsap) {
      initEmeraldAnimations();
    }
    
    // Load Elysian Modern animations (snake scroll + all GSAP animations)
    if (currentTheme === 'elysian-modern' && window.gsap) {
      initSnakeScroll();
      // Delay to ensure DOM is ready
      setTimeout(() => {
        initElysianModernAnimations();
      }, 100);
    }
    
    // Auto-circularize logos for Elysian Gold theme
    if (currentTheme === 'elysian-gold') {
      // Apply to existing logos
      document.querySelectorAll('img[alt*="Success Club"]:not([data-circled])').forEach(el => {
        el.style.borderRadius = '50%';
        el.style.objectFit = 'cover';
        el.style.aspectRatio = '1/1';
        el.dataset.circled = '1';
      });
      
      // Watch for new logos being added
      const observer = new MutationObserver(() => {
        document.querySelectorAll('img[alt*="Success Club"]:not([data-circled])').forEach(el => {
          el.style.borderRadius = '50%';
          el.style.objectFit = 'cover';
          el.style.aspectRatio = '1/1';
          el.dataset.circled = '1';
        });
      });
      
      observer.observe(document.documentElement, {childList: true, subtree: true});
      
      // Cleanup observer when theme changes
      return () => observer.disconnect();
    }
  }, [currentTheme]);
  
  // Elysian GSAP Animations (works for both light and dark)
  const initElysianAnimations = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    
    // Clear previous animations
    ScrollTrigger.getAll().forEach(st => st.kill());
    gsap.globalTimeline.clear();
    
    // Hero intro animation
    const tlIntro = gsap.timeline({ defaults: { ease: 'power2.out' }});
    tlIntro
      .to('.hero-logo', { opacity: 1, scale: 1, duration: 1.2 })
      .to('.hero-title', { opacity: 1, y: 0, duration: 0.9 }, '-=0.4')
      .to('.hero-subtitle', { opacity: 1, y: 0, duration: 0.7 }, '-=0.5')
      .to('.cta-button', { opacity: 1, y: 0, duration: 0.5 }, '-=0.6');
    
    // Network cards reveal
    gsap.utils.toArray('.network-card').forEach((el) => {
      gsap.to(el, {
        opacity: 1, y: 0, duration: 0.8,
        scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' }
      });
    });
    
    // Stats count-up
    gsap.utils.toArray('.stat-number').forEach((el) => {
      const targetText = el.textContent;
      const target = parseInt(targetText.replace(/[^0-9]/g, ''));
      if (isNaN(target)) return;
      
      const obj = { val: 0 };
      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(obj, {
            val: target,
            duration: 1.4,
            ease: 'power1.out',
            onUpdate: () => {
              const v = Math.floor(obj.val);
              el.textContent = v.toLocaleString() + (targetText.includes('$') ? '' : '+');
            }
          });
        }
      });
    });
    
    // Gold pulse on icons
    gsap.utils.toArray('.network-icon').forEach((icon) => {
      ScrollTrigger.create({
        trigger: icon,
        start: 'top 80%',
        once: true,
        onEnter: () => gsap.fromTo(icon, 
          { boxShadow: '0 0 0 0 rgba(212,175,55,0.0)' }, 
          { boxShadow: '0 0 0 12px rgba(212,175,55,0.18)', duration: .6, yoyo: true, repeat: 1, ease: 'power1.out' }
        )
      });
    });
    
    // Immersive section animations
    const quoteCard = document.querySelector('.quote-card-main');
    if (quoteCard) {
      // Parallax images fade in and move
      gsap.utils.toArray('.parallax-img-1, .parallax-img-2, .parallax-img-3').forEach((img, index) => {
        gsap.to(img, {
          opacity: 0.3,
          duration: 1,
          delay: index * 0.2,
          scrollTrigger: {
            trigger: '.immersive-section',
            start: 'top 70%',
            toggleActions: 'play none none reverse'
          }
        });
      });
      
      // Quote card dramatic entrance
      gsap.to(quoteCard, {
        opacity: 1,
        scale: 1,
        duration: 1.2,
        ease: 'back.out(1.2)',
        scrollTrigger: {
          trigger: '.immersive-section',
          start: 'top 60%',
          toggleActions: 'play none none reverse'
        }
      });
      
      // Parallax scrolling effect
      gsap.utils.toArray('.parallax-layer').forEach((layer) => {
        const speed = parseFloat(layer.dataset.speed || 10);
        gsap.to(layer, {
          y: () => speed * 3,
          ease: 'none',
          scrollTrigger: {
            trigger: '.immersive-section',
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          }
        });
      });
    }
  };

  
  // Ethereal Gold GSAP Animations
  const initEtherealAnimations = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    
    // Clear previous animations
    ScrollTrigger.getAll().forEach(st => st.kill());
    
    // Preloader animation
    gsap.fromTo('.preloader .crest', 
      { scale: 0.8, opacity: 0 }, 
      { scale: 1, opacity: 1, duration: 1.1, ease: 'power2.out' }
    );
    
    // Hide preloader after load
    setTimeout(() => {
      gsap.to('.preloader', {
        opacity: 0, 
        duration: 0.6, 
        ease: 'power2.out',
        onComplete: () => {
          const preloader = document.querySelector('.preloader');
          if (preloader) preloader.style.display = 'none';
        }
      });
    }, 1300);
    
    // Initialize particle canvas
    initParticleCanvas();
    
    // Split words and animate
    const splitWords = (el) => {
      if (!el) return [];
      const text = el.textContent.trim();
      el.innerHTML = text.split(' ').map(w => `<span class="word">${w}</span>`).join(' ');
      return el.querySelectorAll('.word');
    };
    
    const titleWords = splitWords(document.querySelector('.hero-title'));
    const leadWords = splitWords(document.querySelector('.lead'));
    
    if (titleWords.length) {
      gsap.from(titleWords, {
        opacity: 0, y: 28, stagger: 0.05, duration: 0.9, ease: 'power2.out'
      });
    }
    
    if (leadWords.length) {
      gsap.from(leadWords, {
        opacity: 0, y: 16, stagger: 0.02, delay: 0.2, duration: 0.8, ease: 'power2.out'
      });
    }
    
    // Feature cards reveal
    gsap.utils.toArray('.feature').forEach(el => {
      gsap.from(el, {
        opacity: 0, y: 24, duration: 0.8,
        scrollTrigger: { trigger: el, start: 'top 80%' }
      });
    });
    
    // Parallax layers
    gsap.utils.toArray('.layer').forEach(el => {
      const speed = parseFloat(el.dataset.speed || 10);
      gsap.to(el, {
        y: () => speed,
        ease: 'none',
        scrollTrigger: {
          trigger: '.immersive',
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    });
    
    // Quote text animation
    const quoteWords = splitWords(document.querySelector('.quote-text'));
    if (quoteWords.length) {
      gsap.from(quoteWords, {
        opacity: 0, y: 18, stagger: 0.05, duration: 0.6,
        scrollTrigger: { trigger: '.quote', start: 'top 85%' }
      });
    }
    
    // Stats counter animation
    gsap.utils.toArray('.num').forEach(el => {
      const target = +el.dataset.count;
      const obj = { v: 0 };
      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(obj, {
            v: target,
            duration: 1.4,
            ease: 'power1.out',
            onUpdate: () => {
              el.textContent = Math.floor(obj.v) + '+';
            }
          });
        }
      });
    });
    
    // Book hover effects
    document.querySelectorAll('.book').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        gsap.to(card, {
          rotateY: x * 10,
          rotateX: -y * 10,
          duration: 0.2
        });
      });
      
      card.addEventListener('mouseleave', () => {
        gsap.to(card, {
          rotateY: 0,
          rotateX: 0,
          duration: 0.35
        });
      });
    });
  };
  
  // Particle canvas initialization for Ethereal theme
  const initParticleCanvas = () => {
    const canvas = document.getElementById('particles');
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    
    const resize = () => {
      W = canvas.width = window.innerWidth;
      const hero = document.querySelector('.hero');
      H = canvas.height = hero ? hero.offsetHeight : window.innerHeight;
    };
    
    const init = () => {
      resize();
      particles = Array.from(
        { length: Math.min(120, Math.floor(W / 12)) },
        () => ({
          x: Math.random() * W,
          y: Math.random() * H,
          r: Math.random() * 1.6 + 0.4,
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2
        })
      );
    };
    
    const loop = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(212, 175, 55, 0.8)';
      
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      
      requestAnimationFrame(loop);
    };
    
    init();
    loop();
    
    window.addEventListener('resize', init);
  };

  
  // Editorial Luxe GSAP Animations
  const initEditorialAnimations = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    
    // Clear previous animations
    ScrollTrigger.getAll().forEach(st => st.kill());
    
    // Preloader fade
    setTimeout(() => {
      const preloader = document.querySelector('.preloader');
      if (preloader) {
        gsap.to(preloader, {
          opacity: 0,
          duration: 0.7,
          delay: 0.35,
          onComplete: () => {
            preloader.classList.add('hidden');
          }
        });
      }
    }, 100);
    
    // Split-lines reveal for headings & copy - ONLY for Editorial Luxe
    const splitLines = (node) => {
      if (!node || !node.innerHTML || !node.closest('.theme-editorial-luxe')) return;
      const words = node.innerHTML.trim().split(' ');
      node.innerHTML = words.map(w => `<span class="w"><span class="i">${w}</span></span>`).join(' ');
      node.style.display = 'inline';
      node.style.lineHeight = '1.2';
      
      const wrappers = node.querySelectorAll('.w');
      const inners = node.querySelectorAll('.i');
      
      wrappers.forEach(el => {
        el.style.display = 'inline-block';
        el.style.overflow = 'hidden';
      });
      
      inners.forEach(el => {
        el.style.display = 'inline-block';
        el.style.transform = 'translateY(120%)';
      });
    };
    
    // Apply split to titles and paragraphs - only within editorial luxe theme
    document.querySelectorAll('.theme-editorial-luxe .hero-title, .theme-editorial-luxe .hero-subtitle, .theme-editorial-luxe .lead').forEach(splitLines);
    
    // Stagger words into view
    document.querySelectorAll('.theme-editorial-luxe .hero-title, .theme-editorial-luxe .hero-subtitle, .theme-editorial-luxe .lead').forEach((blk, i) => {
      const inners = blk.querySelectorAll('.i');
      if (inners.length) {
        gsap.to(inners, {
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.03,
          delay: i * 0.1,
          scrollTrigger: {
            trigger: blk,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          }
        });
      }
    });
    
    // Card reveals
    gsap.utils.toArray('.card, .network-card, .feature').forEach(el => {
      gsap.fromTo(el,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%'
          }
        }
      );
    });
    
    // Parallax on editorial images
    gsap.utils.toArray('.parallax').forEach(box => {
      const img = box.querySelector('img');
      if (img) {
        gsap.to(img, {
          yPercent: 12,
          ease: 'none',
          scrollTrigger: {
            trigger: box,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      }
    });
  };

  
  // Emerald Editorial GSAP Animations
  const initEmeraldAnimations = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    
    // Clear previous animations
    ScrollTrigger.getAll().forEach(st => st.kill());
    
    // Hero type rise - only if elements exist
    const heroTitle = document.querySelector(".hero h1, .hero-title");
    if (heroTitle) {
      gsap.from(heroTitle, {
        y: 40,
        opacity: 0,
        duration: 1,
        ease: "power3.out"
      });
    }
    
    const heroSubtitle = document.querySelector(".deck, .lead, .hero-subtitle");
    if (heroSubtitle) {
      gsap.from(heroSubtitle, {
        y: 20,
        opacity: 0,
        duration: 0.8,
        delay: 0.15,
        ease: "power3.out"
      });
    }
    
    // Mint panel content - only if exists
    const mintSketch = document.querySelector(".mint-panel .sketch");
    if (mintSketch) {
      gsap.from(mintSketch, {
        opacity: 0,
        y: 30,
        duration: 0.9,
        scrollTrigger: {
          trigger: ".mint-panel",
          start: "top 80%"
        }
      });
    }
    
    const mintH2 = document.querySelector(".mint-panel h2");
    if (mintH2) {
      gsap.from(mintH2, {
        y: 30,
        opacity: 0,
        duration: 0.9,
        scrollTrigger: {
          trigger: mintH2,
          start: "top 85%"
        }
      });
    }
    
    // Arch parallax - only if exists
    const archImages = document.querySelectorAll(".arch img");
    if (archImages.length > 0) {
      archImages.forEach((img) => {
        gsap.to(img, {
          yPercent: 12,
          ease: "none",
          scrollTrigger: {
            trigger: img,
            start: "top bottom",
            end: "bottom top",
            scrub: true
          }
        });
      });
    }
    
    // Editorial reveal - only if exists
    const editorialImg = document.querySelector(".editorial .img");
    if (editorialImg) {
      gsap.from(editorialImg, {
        x: -30,
        opacity: 0,
        duration: 0.9,
        scrollTrigger: {
          trigger: ".editorial",
          start: "top 80%"
        }
      });
    }
    
    const editorialH2 = document.querySelector(".editorial h2");
    if (editorialH2) {
      gsap.from(editorialH2, {
        x: 30,
        opacity: 0,
        duration: 0.9,
        scrollTrigger: {
          trigger: editorialH2,
          start: "top 85%"
        }
      });
    }
    
    // Card reveals - standard cards that exist on all themes
    const cards = document.querySelectorAll('.card, .network-card, .feature');
    if (cards.length > 0) {
      cards.forEach(el => {
        gsap.fromTo(el,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 85%'
            }
          }
        );
      });
    }
  };


  // Snake Scroll Animation for Elysian Modern (Simple scroll-based)
  const initSnakeScroll = () => {
    if (!window.gsap || !window.ScrollTrigger) return;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    
    gsap.registerPlugin(ScrollTrigger);

    const dot = document.querySelector("#scSnakeDot");
    if (!dot) {
      console.log("Snake dot not found");
      return;
    }

    console.log("Initializing snake scroll animation");

    // Create a simple diagonal movement tied to scroll (from top-left to bottom-right)
    gsap.to(dot, {
      attr: {
        cx: 90,  // Move from 10 to 90 (percentage of viewBox)
        cy: 90   // Move from 10 to 90 (percentage of viewBox)
      },
      ease: "none",
      scrollTrigger: {
        trigger: "body",
        start: "top top",
        end: "bottom bottom",
        scrub: 1.5,
        onUpdate: (self) => {
          console.log("Scroll progress:", self.progress);
        }
      }
    });

    // Accessibility: freeze motion for reduced-motion users
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches){
      ScrollTrigger.getAll().forEach(s => s.disable());
    }
  };


  // Elysian Modern GSAP Animations (All 5 types)
  const initElysianModernAnimations = () => {
    if (!window.gsap || !window.ScrollTrigger) return;
    
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);
    
    // Clear previous animations
    ScrollTrigger.getAll().forEach(st => st.kill());
    
    console.log("Initializing Elysian Modern GSAP animations");
    
    // 1. HERO TEXT REVEAL ANIMATION (Letter-by-letter)
    const heroTitle = document.querySelector('.theme-elysian-modern .hero-section h1');
    const heroSubtitle = document.querySelector('.theme-elysian-modern .hero-subtitle');
    const heroButton = document.querySelector('.theme-elysian-modern .cta-primary');
    
    if (heroTitle) {
      gsap.to(heroTitle, {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: 'power3.out',
        delay: 0.3
      });
    }
    
    if (heroSubtitle) {
      gsap.to(heroSubtitle, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        delay: 0.6
      });
    }
    
    if (heroButton) {
      gsap.to(heroButton, {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'back.out(1.7)',
        delay: 0.9
      });
    }
    
    // 2. FADE-IN ON SCROLL for stats
    const statItems = document.querySelectorAll('.theme-elysian-modern .stat-item');
    statItems.forEach((item, index) => {
      gsap.fromTo(item,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          delay: index * 0.15,
          scrollTrigger: {
            trigger: item,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );
    });
    
    // 3. SLIDE-IN ANIMATIONS for cards (alternate left/right)
    const networkCards = document.querySelectorAll('.theme-elysian-modern .network-card');
    networkCards.forEach((card, index) => {
      const direction = index % 2 === 0 ? -60 : 60;
      gsap.fromTo(card,
        { opacity: 0, x: direction },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    });
    
    // 4. SLIDE-IN for feature cards
    const featureCards = document.querySelectorAll('.theme-elysian-modern .feature-card');
    featureCards.forEach((card, index) => {
      gsap.fromTo(card,
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: 'power2.out',
          delay: index * 0.1,
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );
    });
    
    // 5. SLIDE-IN for luxury cards
    const luxuryCards = document.querySelectorAll('.theme-elysian-modern .luxury-card');
    luxuryCards.forEach((card, index) => {
      gsap.fromTo(card,
        { opacity: 0, y: 70 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          delay: index * 0.2,
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );
    });
    
    // 6. PARALLAX EFFECT for hero background
    const heroSection = document.querySelector('.theme-elysian-modern .hero-section');
    if (heroSection) {
      gsap.to(heroSection, {
        backgroundPosition: '50% 100px',
        ease: 'none',
        scrollTrigger: {
          trigger: heroSection,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      });
    }
    
    // 7. BUTTON HOVER ANIMATION with GSAP
    const buttons = document.querySelectorAll('.theme-elysian-modern .cta-primary, .theme-elysian-modern .luxury-card-button');
    buttons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        gsap.to(button, {
          scale: 1.05,
          duration: 0.3,
          ease: 'power2.out'
        });
      });
      button.addEventListener('mouseleave', () => {
        gsap.to(button, {
          scale: 1,
          duration: 0.3,
          ease: 'power2.out'
        });
      });
    });
    
    // 8. SECTION HEADERS animation
    const sectionHeaders = document.querySelectorAll('.theme-elysian-modern .section-header h2');
    sectionHeaders.forEach(header => {
      gsap.fromTo(header,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: header,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );
    });
    
    console.log("✅ All Elysian Modern animations initialized");
  };




  return (
    <div className={`theme-${currentTheme} min-h-screen relative overflow-hidden`}>
      {/* Elysian Modern - Snake Scroll Animation (Background) */}
      {currentTheme === 'elysian-modern' && (
        <section className="sc-snake-wrap" aria-label="Animated Success Club accent">
          <svg className="sc-snake-svg" viewBox="0 0 100 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <circle id="scSnakeDot" cx="10" cy="10" r="5" fill="#D4AF37" />
          </svg>
        </section>
      )}

      {/* Modern Noir Intro Animation */}
      {currentTheme === 'modern-noir' && showIntro && (
        <div className="intro-animation">
          <img 
            src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png"
            alt="Success Club"
            className="intro-crest"
          />
        </div>
      )}
      {/* Navigation Header - Minimal and Theme Adaptive */}
      <header className="relative z-50" style={{
        borderBottom: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '1px solid #EFEFEF' : (currentTheme === 'elysian-modern' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.1)'),
        backgroundColor: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? 'rgba(255,255,255,0.95)' : (currentTheme === 'elysian-modern' || currentTheme === 'elysian-gold' || currentTheme === 'editorial-luxe' ? '#000' : 'rgba(0,0,0,0.3)'),
        backdropFilter: currentTheme === 'elysian-gold' || currentTheme === 'editorial-luxe' || currentTheme === 'elysian-modern' ? 'none' : 'blur(10px)'
      }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Brand */}
            <div className="flex items-center gap-6">
              <div className={currentTheme === 'elysian-modern' ? 'w-36 h-36 flex items-center justify-center' : 'w-14 h-14 flex items-center justify-center'}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                  alt="Success Club Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className={currentTheme === 'elysian-modern' ? 'text-6xl font-bold tracking-wide -ml-4' : 'text-xl font-bold tracking-wide'} style={{
                color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#111' : '#fff',
                fontFamily: currentTheme === 'modern-noir' ? 'Bodoni Moda, serif' : (currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? 'Inter, sans-serif' : (currentTheme === 'elysian-modern' ? 'Cormorant Garamond, serif' : 'Playfair Display, serif'))
              }}>SUCCESS CLUB</span>
            </div>
            
            {/* Navigation Menu - Desktop */}
            <nav className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => navigate('/features')} 
                style={{
                  color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#666' : (currentTheme === 'elysian-modern' ? 'rgba(255,255,255,0.8)' : '#ccc'),
                  fontSize: currentTheme === 'elysian-modern' ? '1.25rem' : '0.875rem'
                }}
                className="hover:opacity-80 transition-opacity"
              >
                Features
              </button>
              <button 
                onClick={() => navigate('/magazine')} 
                style={{
                  color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#666' : (currentTheme === 'elysian-modern' ? 'rgba(255,255,255,0.8)' : '#ccc'),
                  fontSize: currentTheme === 'elysian-modern' ? '1.25rem' : '0.875rem'
                }}
                className="hover:opacity-80 transition-opacity"
              >
                Magazine
              </button>
              <button 
                onClick={() => navigate('/pricing')} 
                style={{
                  color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#666' : (currentTheme === 'elysian-modern' ? 'rgba(255,255,255,0.8)' : '#ccc'),
                  fontSize: currentTheme === 'elysian-modern' ? '1.25rem' : '0.875rem'
                }}
                className="hover:opacity-80 transition-opacity"
              >
                Pricing
              </button>
              <button 
                onClick={() => navigate('/auth')}
                style={{
                  backgroundColor: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#D4AF37' : (currentTheme === 'elysian-modern' ? '#D4AF37' : '#D4AF37'),
                  color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#1A1A1A' : (currentTheme === 'elysian-modern' ? '#000' : '#000')
                }}
                className="px-4 py-2 rounded text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Login
              </button>
            </nav>

            {/* Hamburger Menu - Mobile */}
            <div className="md:hidden flex items-center gap-4">
              <button 
                onClick={() => navigate('/auth')}
                style={{
                  backgroundColor: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#D4AF37' : (currentTheme === 'elysian-modern' ? '#D4AF37' : '#D4AF37'),
                  color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#1A1A1A' : (currentTheme === 'elysian-modern' ? '#000' : '#000')
                }}
                className="px-3 py-1.5 rounded text-xs font-medium hover:opacity-90 transition-opacity"
              >
                Login
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
                style={{
                  color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#111' : '#fff'
                }}
              >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t" style={{
              borderColor: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#EFEFEF' : 'rgba(255,255,255,0.1)'
            }}>
              <div className="flex flex-col gap-4 pt-4">
                <button 
                  onClick={() => { navigate('/features'); setMobileMenuOpen(false); }} 
                  style={{
                    color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#666' : (currentTheme === 'elysian-modern' ? 'rgba(255,255,255,0.8)' : '#ccc'),
                    fontSize: '1rem'
                  }}
                  className="text-left px-4 py-2 hover:opacity-80 transition-opacity"
                >
                  Features
                </button>
                <button 
                  onClick={() => { navigate('/magazine'); setMobileMenuOpen(false); }} 
                  style={{
                    color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#666' : (currentTheme === 'elysian-modern' ? 'rgba(255,255,255,0.8)' : '#ccc'),
                    fontSize: '1rem'
                  }}
                  className="text-left px-4 py-2 hover:opacity-80 transition-opacity"
                >
                  Magazine
                </button>
                <button 
                  onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} 
                  style={{
                    color: currentTheme === 'daylight-gold' || currentTheme === 'balanced-modern' ? '#666' : (currentTheme === 'elysian-modern' ? 'rgba(255,255,255,0.8)' : '#ccc'),
                    fontSize: '1rem'
                  }}
                  className="text-left px-4 py-2 hover:opacity-80 transition-opacity"
                >
                  Pricing
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Animated Ticker Banner */}
      <div className="ticker-banner">
        <div className="ticker-content">
          <span className="ticker-item">SUCCESS CLUB</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">ELITE NETWORK</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">AMBITIOUS LEADERS</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">BUILD CONNECTIONS</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">EXCLUSIVE EVENTS</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">BUSINESS GROWTH</span>
          <span className="ticker-separator">•</span>
          {/* Duplicate for seamless loop */}
          <span className="ticker-item">SUCCESS CLUB</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">ELITE NETWORK</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">AMBITIOUS LEADERS</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">BUILD CONNECTIONS</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">EXCLUSIVE EVENTS</span>
          <span className="ticker-separator">•</span>
          <span className="ticker-item">BUSINESS GROWTH</span>
          <span className="ticker-separator">•</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        {(currentTheme === 'midnight-elegance' || currentTheme === 'elysian-gold' || currentTheme === 'elysian-dark' || currentTheme === 'elysian-modern') && (
          <>
            <div className="hero-video-bg" style={{
              backgroundImage: currentTheme === 'elysian-modern' 
                ? 'url(https://customer-assets.emergentagent.com/job_club-dashboard-17/artifacts/6uuu5l3f_couple-celebrating-with-champagne-dark-background.jpg)'
                : 'url(https://customer-assets.emergentagent.com/job_club-dashboard-17/artifacts/fph4gmpd_men-women-playing-card-game-poker-evening-entertainment-excitement-concept.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}></div>
            {(currentTheme === 'elysian-gold' || currentTheme === 'elysian-dark' || currentTheme === 'elysian-modern') && (
              <div className="hero-veil"></div>
            )}
          </>
        )}
        
        {currentTheme === 'ethereal-gold' && (
          <>
            <canvas className="hero__canvas" id="particles"></canvas>
            <div className="hero__veil"></div>
            <div className="preloader" style={{opacity: 1}}>
              <div className="crest">
                <img src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" alt="Success Club crest" />
              </div>
            </div>
          </>
        )}
        
        <div className="hero-content">
          {/* Hide large logo for Midnight Elegance, Balanced Modern, and Elysian Modern, show for other themes */}
          {(currentTheme !== 'midnight-elegance' && currentTheme !== 'balanced-modern' && currentTheme !== 'elysian-modern') && (
            <div className={currentTheme === 'ethereal-gold' ? 'crest-circle' : 'hero-logo'}>
              <img 
                src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                alt="Success Club Logo" 
                style={{width: '100%', height: '100%', objectFit: 'contain'}}
              />
            </div>
          )}
          
          <h1 className="hero-title">
            {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern')
              ? 'Where Ambitious Leaders Connect' 
              : 'SUCCESS CLUB'
            }
          </h1>
          
          <p className={currentTheme === 'ethereal-gold' ? 'lead' : 'hero-subtitle'}>
            {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern')
              ? 'Join an elite network of entrepreneurs, investors, and executives. Build meaningful connections that drive extraordinary growth.'
              : 'Join an exclusive network of entrepreneurs, investors, and business leaders. Build connections that drive extraordinary growth and success.'
            }
          </p>
          
          {currentTheme === 'modern-noir' ? (
            <div className="cta-buttons">
              <button
                onClick={() => navigate('/signup/member')}
                className="cta-button-primary"
              >
                Join Success Club
              </button>
              <button
                onClick={() => navigate('/signup/host')}
                className="cta-button-secondary"
              >
                Become a Host
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/signup/member')}
              className={(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'cta-primary' : currentTheme === 'elysian-gold' ? 'cta-button' : 'cta-button'}
            >
              {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'Get Started' : 'Join Success Club'}
            </button>
          )}
        </div>
      </section>

      {/* Section Divider for Modern Noir */}
      {currentTheme === 'modern-noir' && <div className="section-divider"></div>}

      {/* Section Divider for Modern Noir */}
      {currentTheme === 'modern-noir' && <div className="section-divider"></div>}

      {/* Exclusive Business Network Section */}
      <section className={`network-section ${currentTheme === 'modern-noir' ? 'network-section-light' : 'relative z-10'}`}>
        <div className="max-w-7xl mx-auto">
          {currentTheme === 'modern-noir' && (
            <h2 className="section-title">Exclusive Business Network</h2>
          )}
          {currentTheme !== 'modern-noir' && (
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Exclusive Business Network</h2>
              <p className="text-xl max-w-3xl mx-auto" style={{color: currentTheme === 'daylight-gold' ? '#666' : (currentTheme === 'elysian-modern' ? '#111' : '#d1d5db')}}>
                Connect with like-minded entrepreneurs and unlock opportunities that drive real business growth.
              </p>
            </div>
          )}
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="network-card">
              <div className="network-icon">
                <Users className="w-8 h-8" style={{color: (currentTheme === 'daylight-gold' || currentTheme === 'editorial-luxe' || currentTheme === 'emerald-editorial' || currentTheme === 'elysian-gold') ? '#D4AF37' : '#fff'}} />
              </div>
              <h3 className="network-title">Elite Network</h3>
              <p className="network-description">
                Access an exclusive community of successful entrepreneurs, investors, and industry leaders.
              </p>
            </div>
            
            <div className="network-card">
              <div className="network-icon">
                <Target className="w-8 h-8" style={{color: (currentTheme === 'daylight-gold' || currentTheme === 'editorial-luxe' || currentTheme === 'emerald-editorial' || currentTheme === 'elysian-gold') ? '#D4AF37' : '#fff'}} />
              </div>
              <h3 className="network-title">Growth Opportunities</h3>
              <p className="network-description">
                Discover partnerships, investments, and business opportunities through meaningful connections.
              </p>
            </div>
            
            <div className="network-card">
              <div className="network-icon">
                <Trophy className="w-8 h-8" style={{color: (currentTheme === 'daylight-gold' || currentTheme === 'editorial-luxe' || currentTheme === 'emerald-editorial' || currentTheme === 'elysian-gold') ? '#D4AF37' : '#fff'}} />
              </div>
              <h3 className="network-title">Proven Results</h3>
              <p className="network-description">
                Join a community where members consistently achieve exceptional business outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider for Modern Noir */}
      {currentTheme === 'modern-noir' && <div className="section-divider"></div>}


      {/* Balanced Modern & Elysian Modern - Detailed Features Section */}
      {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') && (
        <section className="features-section">
          <div className="features-container">
            <div className="section-header">
              <h2>Everything You Need to Succeed</h2>
              <p>Comprehensive tools and resources designed to accelerate your business growth</p>
            </div>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">
                  <Calendar className="w-7 h-7" />
                </div>
                <h3>Exclusive Events</h3>
                <p>Access curated networking events, workshops, and masterclasses led by industry leaders</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <Target className="w-7 h-7" />
                </div>
                <h3>Growth Challenges</h3>
                <p>Participate in structured challenges that push your business forward with measurable results</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <MessageCircle className="w-7 h-7" />
                </div>
                <h3>Direct Messaging</h3>
                <p>Connect privately with members, hosts, and mentors through our integrated chat system</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <Trophy className="w-7 h-7" />
                </div>
                <h3>Achievement System</h3>
                <p>Track your progress, earn points, and compete on leaderboards that recognize real accomplishments</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <Users className="w-7 h-7" />
                </div>
                <h3>Accountability Partners</h3>
                <p>Get matched with like-minded entrepreneurs for weekly goal-setting and accountability</p>
              </div>
              
              <div className="feature-card">
                <div className="feature-icon">
                  <BookOpen className="w-7 h-7" />
                </div>
                <h3>Premium Content</h3>
                <p>Access exclusive articles, magazines, and resources curated for ambitious business leaders</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Balanced Modern & Elysian Modern - Dark Testimonials Section */}
      {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') && (
        <section className="dark-section">
          <div className="features-container">
            <div className="section-header">
              <h2>Trusted by Industry Leaders</h2>
              <p>See what our members are saying about their Success Club experience</p>
            </div>
            
            <div className="testimonials-grid">
              <div className="testimonial-card">
                <p className="testimonial-text">
                  "Success Club transformed how I network. I've closed three major deals in six months through connections made here."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">JM</div>
                  <div className="author-info">
                    <h4>James Morrison</h4>
                    <p>CEO, TechVentures</p>
                  </div>
                </div>
              </div>
              
              <div className="testimonial-card">
                <p className="testimonial-text">
                  "The accountability partner system kept me focused. I achieved goals I'd been putting off for years."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">SP</div>
                  <div className="author-info">
                    <h4>Sarah Parker</h4>
                    <p>Founder, GrowthLab</p>
                  </div>
                </div>
              </div>
              
              <div className="testimonial-card">
                <p className="testimonial-text">
                  "More than a network—it's a community of ambitious people who genuinely want to see each other succeed."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">RC</div>
                  <div className="author-info">
                    <h4>Robert Chen</h4>
                    <p>Investor & Advisor</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Luxury Card Section - Hotel Style (Elysian Modern Only) */}
      {currentTheme === 'elysian-modern' && (
        <section className="luxury-cards-section">
          <div className="section-header">
            <h2>Membership Options</h2>
          </div>
          
          <div className="luxury-cards-grid">
            <div className="luxury-card">
              <div className="luxury-card-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=600&h=800&fit=crop" 
                  alt="Member Tier"
                  className="luxury-card-image"
                />
              </div>
              <div className="luxury-card-content">
                <div>
                  <h3 className="luxury-card-title">Member</h3>
                  <p className="luxury-card-description">
                    For established professionals | Access to 50+ events/year | Priority networking
                  </p>
                </div>
                <button className="luxury-card-button" onClick={() => navigate('/pricing')}>
                  Explore Member
                </button>
              </div>
            </div>
            
            <div className="luxury-card">
              <div className="luxury-card-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&h=800&fit=crop" 
                  alt="Host Tier"
                  className="luxury-card-image"
                />
              </div>
              <div className="luxury-card-content">
                <div>
                  <h3 className="luxury-card-title">Host</h3>
                  <p className="luxury-card-description">
                    For scaling entrepreneurs | Unlimited events | Personal concierge
                  </p>
                </div>
                <button className="luxury-card-button" onClick={() => navigate('/pricing')}>
                  Explore Host
                </button>
              </div>
            </div>
            
            <div className="luxury-card">
              <div className="luxury-card-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop" 
                  alt="Event Host Tier"
                  className="luxury-card-image"
                />
              </div>
              <div className="luxury-card-content">
                <div>
                  <h3 className="luxury-card-title">Event Host</h3>
                  <p className="luxury-card-description">
                    For industry leaders | VIP access | Executive retreats | White-glove service
                  </p>
                </div>
                <button className="luxury-card-button" onClick={() => navigate('/pricing')}>
                  Explore Event Host
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Carousel Navigation */}
          <div className="carousel-nav">
            <button 
              id="carousel-prev" 
              onClick={() => {
                const cards = document.querySelectorAll('.luxury-card');
                const current = document.querySelector('.luxury-card.carousel-active');
                if (!current) return;
                
                const currentIndex = Array.from(cards).indexOf(current);
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1;
                
                cards.forEach(card => card.classList.remove('carousel-active', 'carousel-left', 'carousel-right', 'carousel-hidden'));
                
                cards[prevIndex].classList.add('carousel-active');
                const leftIdx = prevIndex > 0 ? prevIndex - 1 : cards.length - 1;
                const rightIdx = prevIndex < cards.length - 1 ? prevIndex + 1 : 0;
                cards[leftIdx].classList.add('carousel-left');
                cards[rightIdx].classList.add('carousel-right');
                
                cards.forEach((card, idx) => {
                  if (idx !== prevIndex && idx !== leftIdx && idx !== rightIdx) {
                    card.classList.add('carousel-hidden');
                  }
                });
                
                // Update dots
                document.querySelectorAll('.carousel-dots .dot').forEach((dot, i) => {
                  dot.classList.toggle('active', i === prevIndex);
                });
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <button 
              id="carousel-next"
              onClick={() => {
                const cards = document.querySelectorAll('.luxury-card');
                const current = document.querySelector('.luxury-card.carousel-active');
                if (!current) return;
                
                const currentIndex = Array.from(cards).indexOf(current);
                const nextIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0;
                
                cards.forEach(card => card.classList.remove('carousel-active', 'carousel-left', 'carousel-right', 'carousel-hidden'));
                
                cards[nextIndex].classList.add('carousel-active');
                const leftIdx = nextIndex > 0 ? nextIndex - 1 : cards.length - 1;
                const rightIdx = nextIndex < cards.length - 1 ? nextIndex + 1 : 0;
                cards[leftIdx].classList.add('carousel-left');
                cards[rightIdx].classList.add('carousel-right');
                
                cards.forEach((card, idx) => {
                  if (idx !== nextIndex && idx !== leftIdx && idx !== rightIdx) {
                    card.classList.add('carousel-hidden');
                  }
                });
                
                // Update dots
                document.querySelectorAll('.carousel-dots .dot').forEach((dot, i) => {
                  dot.classList.toggle('active', i === nextIndex);
                });
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>

          {/* Carousel Dots */}
          <div className="carousel-dots">
            <span className="dot active" onClick={() => {
              const cards = document.querySelectorAll('.luxury-card');
              cards.forEach((card, idx) => {
                card.classList.remove('carousel-active', 'carousel-left', 'carousel-right', 'carousel-hidden');
                if (idx === 0) card.classList.add('carousel-active');
                else if (idx === 2) card.classList.add('carousel-left');
                else if (idx === 1) card.classList.add('carousel-right');
              });
              document.querySelectorAll('.carousel-dots .dot').forEach((dot, i) => {
                dot.classList.toggle('active', i === 0);
              });
            }}></span>
            <span className="dot" onClick={() => {
              const cards = document.querySelectorAll('.luxury-card');
              cards.forEach((card, idx) => {
                card.classList.remove('carousel-active', 'carousel-left', 'carousel-right', 'carousel-hidden');
                if (idx === 1) card.classList.add('carousel-active');
                else if (idx === 0) card.classList.add('carousel-left');
                else if (idx === 2) card.classList.add('carousel-right');
              });
              document.querySelectorAll('.carousel-dots .dot').forEach((dot, i) => {
                dot.classList.toggle('active', i === 1);
              });
            }}></span>
            <span className="dot" onClick={() => {
              const cards = document.querySelectorAll('.luxury-card');
              cards.forEach((card, idx) => {
                card.classList.remove('carousel-active', 'carousel-left', 'carousel-right', 'carousel-hidden');
                if (idx === 2) card.classList.add('carousel-active');
                else if (idx === 1) card.classList.add('carousel-left');
                else if (idx === 0) card.classList.add('carousel-right');
              });
              document.querySelectorAll('.carousel-dots .dot').forEach((dot, i) => {
                dot.classList.toggle('active', i === 2);
              });
            }}></span>
          </div>
        </section>
      )}



      {/* Elysian Immersive Parallax Section */}
      {(currentTheme === 'elysian-gold' || currentTheme === 'elysian-dark') && (
        <section className="immersive-section">
          <div className="immersive-wrap">
            <div className="parallax-layer parallax-img-1" data-speed="-20" style={{left:'-5vw', top:'15vh'}}>
              <img src="https://customer-assets.emergentagent.com/job_club-dashboard-17/artifacts/kr9hs8pz_1.jpg" alt="Texture" />
            </div>
            <div className="parallax-layer parallax-img-2" data-speed="15" style={{right:'-8vw', top:'25vh'}}>
              <img src="https://customer-assets.emergentagent.com/job_club-dashboard-17/artifacts/mld7153a_chessman-is-changed-shadow-crown.jpg" alt="Texture" />
            </div>
            <div className="parallax-layer parallax-img-3" data-speed="8" style={{left:'15vw', bottom:'10vh'}}>
              <img src="https://customer-assets.emergentagent.com/job_club-dashboard-17/artifacts/6uuu5l3f_couple-celebrating-with-champagne-dark-background.jpg" alt="Texture" />
            </div>
            
            <div className="quote-card quote-card-main">
              <div className="quote-author" style={{marginBottom: '16px', fontSize: '14px', fontWeight: '600', letterSpacing: '0.1em'}}>SUCCESS CLUB</div>
              <p className="quote-text" style={{fontStyle: 'italic'}}>"Make mistakes of ambition not mistakes of sloth - learn the strength to do bold things, not the strength to suffer."</p>
              <div className="quote-attribution" style={{marginTop: '20px', fontSize: '14px', fontStyle: 'italic', opacity: 0.8}}>— Machiavelli</div>
            </div>
          </div>
        </section>
      )}

      {/* Host for Free Section */}
      <section className={`host-section ${currentTheme !== 'modern-noir' ? 'relative z-10' : ''}`}>
        <div className="host-content">
          {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? (
            <div className="host-container">
              <div className="section-header">
                <h2>Host for Free</h2>
                <p>Build your own exclusive community within Success Club and establish yourself as a thought leader</p>
              </div>
              
              <div className="host-benefits">
                <div className="benefit-item">
                  <div className="benefit-icon-large">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div className="benefit-content">
                    <h3>Revenue Generation</h3>
                    <p>Earn revenue from your community members, exclusive events, and premium content offerings</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon-large">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="benefit-content">
                    <h3>Community Management</h3>
                    <p>Access advanced tools to manage challenges, events, and member engagement effectively</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon-large">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div className="benefit-content">
                    <h3>Thought Leadership</h3>
                    <p>Establish authority in your industry through exclusive content, events, and community building</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <div className="benefit-icon-large">
                    <BarChart2 className="w-6 h-6" />
                  </div>
                  <div className="benefit-content">
                    <h3>Analytics & Insights</h3>
                    <p>Track community growth, engagement metrics, and revenue through comprehensive dashboards</p>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-12">
                <button
                  onClick={() => navigate('/signup/host')}
                  className="cta-primary"
                >
                  Become a Host
                </button>
              </div>
            </div>
          ) : currentTheme === 'elysian-gold' || currentTheme === 'elysian-dark' ? (
            <div className="host-grid">
              <div>
                <h2 className="host-title">Host for Free</h2>
                <p className="host-description">
                  Build your own exclusive community within Success Club. Generate revenue, 
                  create meaningful connections, and establish yourself as a thought leader.
                </p>
                
                <div className="host-features">
                  <div className="host-feature">
                    <div className="feature-bullet"></div>
                    <div className="feature-content">
                      <h4 className="feature-title">Revenue Generation</h4>
                      <p className="feature-description">Earn revenue from your community members and exclusive events.</p>
                    </div>
                  </div>
                  
                  <div className="host-feature">
                    <div className="feature-bullet"></div>
                    <div className="feature-content">
                      <h4 className="feature-title">Community Management</h4>
                      <p className="feature-description">Advanced tools to manage challenges, events, and member engagement.</p>
                    </div>
                  </div>
                  
                  <div className="host-feature">
                    <div className="feature-bullet"></div>
                    <div className="feature-content">
                      <h4 className="feature-title">Thought Leadership</h4>
                      <p className="feature-description">Establish authority in your industry through exclusive content and events.</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/signup/host')}
                  className="host-cta-button"
                >
                  Become a Host
                </button>
              </div>
              
              <div className="host-visual">
                <div className="host-icon">
                  <Crown className="w-16 h-16" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="host-visual-title">Start Your Community</h3>
                <p className="host-visual-description">
                  Join hundreds of successful hosts who have built thriving communities on Success Club.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
              <div>
                <h2 className="host-title">Host for Free</h2>
                <p className="host-description">
                  Build your own exclusive community within Success Club. Generate revenue, 
                  create meaningful connections, and establish yourself as a thought leader.
                </p>
                
                <div className="space-y-6">
                  <div className="host-feature">
                    <div className="feature-bullet"></div>
                    <div>
                      <h4 className="feature-title">Revenue Generation</h4>
                      <p className="feature-description">Earn revenue from your community members and exclusive events.</p>
                    </div>
                  </div>
                  
                  <div className="host-feature">
                    <div className="feature-bullet"></div>
                    <div>
                      <h4 className="feature-title">Community Management</h4>
                      <p className="feature-description">Advanced tools to manage challenges, events, and member engagement.</p>
                    </div>
                  </div>
                  
                  <div className="host-feature">
                    <div className="feature-bullet"></div>
                    <div>
                      <h4 className="feature-title">Thought Leadership</h4>
                      <p className="feature-description">Establish authority in your industry through exclusive content and events.</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/signup/host')}
                  className={currentTheme === 'daylight-gold' ? 'host-button mt-8' : (currentTheme === 'elysian-gold' || currentTheme === 'elysian-dark') ? 'cta-button cta-button-gold mt-8' : 'cta-button mt-8'}
                >
                  Become a Host
                </button>
              </div>
              
              <div className="text-center">
                <div className="host-icon">
                  <Crown className="w-16 h-16" style={{color: currentTheme === 'daylight-gold' ? '#D4AF37' : '#fff'}} />
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${currentTheme === 'daylight-gold' ? 'text-gray-900' : ''}`}>Start Your Community</h3>
                <p className={currentTheme === 'daylight-gold' ? 'text-gray-600' : 'network-description'}>
                  Join hundreds of successful hosts who have built thriving communities on Success Club.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Ready to Join Section */}
      <section className={(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'final-cta-section' : 'relative z-10 py-20 px-6'} style={{
        backgroundColor: currentTheme === 'elysian-gold' ? '#FDFBF7' : ((currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? undefined : 'transparent')
      }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{
            fontFamily: (currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'Noto Serif Display, serif' : (currentTheme === 'elysian-gold' ? 'Cormorant Garamond, serif' : 'inherit'),
            color: currentTheme === 'elysian-gold' ? '#111' : ((currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? '#fff' : 'inherit'),
            fontSize: (currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? '4rem' : undefined,
            fontStretch: (currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'extra-condensed' : 'normal',
            letterSpacing: (currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? '-0.03em' : 'normal'
          }}>
            {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'Ready to Elevate Your Network?' : 'Ready to Join Success Club?'}
          </h2>
          <p className="text-xl mb-12" style={{
            color: currentTheme === 'elysian-gold' ? '#4A4A4A' : ((currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'rgba(255, 255, 255, 0.8)' : '#d1d5db')
          }}>
            {(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern')
              ? 'Join thousands of ambitious entrepreneurs who are building meaningful connections and driving real business growth'
              : 'Take the next step in your entrepreneurial journey. Connect with like-minded leaders and unlock opportunities that drive extraordinary success.'
            }
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <button
              onClick={() => navigate('/signup/member')}
              className={(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'cta-primary' : 'px-12 py-4 text-lg font-semibold hover:opacity-90 transition-opacity rounded-lg'}
              style={(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? {} : {
                background: currentTheme === 'elysian-gold' ? 'linear-gradient(180deg, #F4E4C0, #D4AF37)' : '#fff',
                color: currentTheme === 'elysian-gold' ? '#111' : '#000',
                border: currentTheme === 'elysian-gold' ? '1px solid #C29A4B' : 'none'
              }}
            >
              Join as Member
            </button>
            <button
              onClick={() => navigate('/signup/host')}
              className={(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? 'cta-primary' : 'px-12 py-4 text-lg font-semibold hover:opacity-80 transition-opacity rounded-lg'}
              style={(currentTheme === 'balanced-modern' || currentTheme === 'elysian-modern') ? {} : {
                background: currentTheme === 'elysian-gold' ? 'transparent' : 'transparent',
                color: currentTheme === 'elysian-gold' ? '#111' : '#fff',
                border: currentTheme === 'elysian-gold' ? '2px solid #D4AF37' : '2px solid #fff'
              }}
            >
              Host Community
            </button>
          </div>
          
          {/* Testimonial with Quote - Hide for balanced-modern and elysian-modern as we have a dedicated section */}
          {(currentTheme !== 'balanced-modern' && currentTheme !== 'elysian-modern') && (
            <div className="p-8 rounded-lg max-w-2xl mx-auto" style={{
              backgroundColor: currentTheme === 'elysian-gold' ? '#0b0b0b' : '#1f2937'
            }}>
              <div className="text-center mb-4">
                <div className="text-sm font-semibold tracking-wider" style={{
                  color: currentTheme === 'elysian-gold' ? '#D4AF37' : '#d1d5db'
                }}>
                  SUCCESS CLUB
                </div>
              </div>
              <p className="text-lg italic mb-6 text-center" style={{
                color: currentTheme === 'elysian-gold' ? '#e9e9e9' : '#fff'
              }}>
                "Make mistakes of ambition not mistakes of sloth - learn the strength to do bold things, not the strength to suffer."
              </p>
              <div className="text-center">
                <div className="text-sm italic" style={{
                  color: currentTheme === 'elysian-gold' ? '#d9d9d9' : '#9ca3af'
                }}>
                  — Machiavelli
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Success Club Portal Component
const SuccessClubPortal = ({ initialPortalMode = "host" }) => {
  const [portalMode, setPortalMode] = useState(initialPortalMode);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [events, setEvents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  // Notification badge counts
  const [notificationCounts, setNotificationCounts] = useState({
    challenges: 0,
    events: 0,
    leaderboard: 0,
    boosts: 0,
    chat: 0,
    referrals: 0,
    magazines: 0
  });
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [campaignType, setCampaignType] = useState("email"); // "email" or "sms"
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignFormData, setCampaignFormData] = useState({
    title: "",
    subject: "",
    html_content: "",
    message: "",
    recipient_type: "all_members",
    custom_recipients: "",
    scheduled_for: "",
    track_opens: true,
    track_clicks: true
  });
  const [members, setMembers] = useState(null);
  const [hostRevenue, setHostRevenue] = useState(null);
  const [marketingBoosts, setMarketingBoosts] = useState([]);
  const [pointsAnalytics, setPointsAnalytics] = useState(null);
  const [adminLeaderboard, setAdminLeaderboard] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [pendingHosts, setPendingHosts] = useState([]);
  const [selectedHosts, setSelectedHosts] = useState([]);
  
  // Analytics drill-down state
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [analyticsTimeFilter, setAnalyticsTimeFilter] = useState('30d'); // '7d', '30d', '90d', '1y', 'all'
  const [analyticsRoleFilter, setAnalyticsRoleFilter] = useState('all'); // 'all', 'member', 'host'
  const [activationPeriod, setActivationPeriod] = useState('week'); // 'day', 'week', 'month', 'year'
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [detailedMetricData, setDetailedMetricData] = useState(null);
  const [showHostDetails, setShowHostDetails] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showAdminForm, setShowAdminForm] = useState(false);
  
  // Analytics states
  const [analyticsData, setAnalyticsData] = useState({
    revenue: {},
    userEngagement: {},
    hostPerformance: {},
    challengeParticipation: {}
  });
  
  // Enhanced User Management states
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userFilters, setUserFilters] = useState({
    role: "all",
    status: "all",
    searchTerm: "",
    joinedAfter: "",
    joinedBefore: ""
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserDetails, setShowUserDetails] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserActions, setShowUserActions] = useState(null);
  
  // Bulk Operations states
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionType, setBulkActionType] = useState("");
  const [bulkMessage, setBulkMessage] = useState("");
  const [exportFormat, setExportFormat] = useState("csv");
  const [massEmailTemplate, setMassEmailTemplate] = useState({
    subject: "",
    message: "",
    targetGroups: []
  });
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Twilio Communication Center states
  const [twilioAnalytics, setTwilioAnalytics] = useState({});
  const [showCrmSync, setShowCrmSync] = useState(false);
  const [showEnhancedEmail, setShowEnhancedEmail] = useState(false);
  const [showVoiceCampaign, setShowVoiceCampaign] = useState(false);
  const [enhancedEmailForm, setEnhancedEmailForm] = useState({
    subject: "",
    content: "",
    targetGroups: []
  });
  const [voiceCampaignForm, setVoiceCampaignForm] = useState({
    message: "",
    targetGroups: []
  });
  
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [adminForm, setAdminForm] = useState({
    full_name: "",
    email: "",
    password: "",
    can_manage_users: true,
    can_manage_events: true,
    can_manage_challenges: true,
    can_approve_hosts: true
  });
  const [passwordResetForm, setPasswordResetForm] = useState({
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(true);
  
  // Member portal specific state
  const [memberDashboardData, setMemberDashboardData] = useState({});
  const [memberChallenges, setMemberChallenges] = useState([]);
  const [memberEvents, setMemberEvents] = useState([]);
  const [memberReferrals, setMemberReferrals] = useState({});
  const [memberBoosts, setMemberBoosts] = useState({});
  const [eventFilter, setEventFilter] = useState('upcoming'); // upcoming, past, my_rsvps, host_events, general_events
  const [challengeFilter, setChallengeFilter] = useState('all'); // all, active, completed, my_challenges
  const [adminChallengeFilter, setAdminChallengeFilter] = useState('current'); // current, past
  const [adminEventFilter, setAdminEventFilter] = useState('current'); // current, past
  const [showChallengeProgress, setShowChallengeProgress] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [progressForm, setProgressForm] = useState({
    progress_notes: '',
    document_url: '',
    completion_proof: null
  });

  // Social Media Boost States
  const [socialProfiles, setSocialProfiles] = useState([]);
  const [boostTargets, setBoostTargets] = useState([]);
  const [boostStats, setBoostStats] = useState({
    boosts_given: 0,
    boosts_received: 0,
    points_earned: 0,
    this_month_given: 0,
    this_month_received: 0
  });
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showBoostNotification, setShowBoostNotification] = useState(false);
  const [boostNotificationData, setBoostNotificationData] = useState({ platform: '', points: 0 });
  
  // Generic notification system
  const [showPointsNotification, setShowPointsNotification] = useState(false);
  const [pointsNotificationData, setPointsNotificationData] = useState({ title: '', message: '', points: 0 });
  const [showSimpleNotification, setShowSimpleNotification] = useState(false);
  const [simpleNotificationData, setSimpleNotificationData] = useState({ title: '', message: '', type: 'success' });
  
  // Cancelled subscriptions state
  const [cancelledSubscriptions, setCancelledSubscriptions] = useState([]);
  const [subscriptionTimeframe, setSubscriptionTimeframe] = useState('all');
  
  const [pendingBoostNotification, setPendingBoostNotification] = useState(null);
  
  // Accountability Partner States
  const [accountabilityStatus, setAccountabilityStatus] = useState(null);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [showPartnerSelection, setShowPartnerSelection] = useState(false);
  const [showCheckInForm, setShowCheckInForm] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [checkInForm, setCheckInForm] = useState({ notes: '', progress_rating: 3 });
  const [reviewForm, setReviewForm] = useState({ rating: 5, review: '' });
  const [accountabilityReviews, setAccountabilityReviews] = useState(null);
  const [weeklyGoals, setWeeklyGoals] = useState({ my_goals: null, partner_goals: null });
  const [participationStatus, setParticipationStatus] = useState(null);
  const [showGoalsForm, setShowGoalsForm] = useState(false);
  const [goalsFormData, setGoalsFormData] = useState([]);
  const [newGoalText, setNewGoalText] = useState('');
  const [activeAccountabilityTab, setActiveAccountabilityTab] = useState('partner');
  const [showCheckInModal, setShowCheckInModal] = useState(false); // partner, goals, reviews, chat
  const [showMatchingPreferencesModal, setShowMatchingPreferencesModal] = useState(false);
  const [showPartnerSuccessNotification, setShowPartnerSuccessNotification] = useState(false);
  const [partnerSuccessMessage, setPartnerSuccessMessage] = useState('');
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [showScheduleCallForm, setShowScheduleCallForm] = useState(false);
  const [callFormData, setCallFormData] = useState({ meeting_link: '', scheduled_time: '', notes: '' });
  const [chatMessages, setChatMessages] = useState([]);
  const [accountabilityChatMessage, setAccountabilityChatMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Analytics states
  const [pointsBreakdown, setPointsBreakdown] = useState(null);
  const [pointsTimeline, setPointsTimeline] = useState(null);
  const [activityHeatmap, setActivityHeatmap] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [adminAnalytics, setAdminAnalytics] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState('month'); // week, month, year, all
  const [leaderboardFilter, setLeaderboardFilter] = useState('all-time'); // all-time, week, month
  const [leaderboardCategory, setLeaderboardCategory] = useState('points'); // points, checkins, boosts
  
  // Social media links state for the profile setup form
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    linkedin: '',
    facebook: '',
    google_reviews: ''
  });

  // Host-specific states
  const [hostChallenges, setHostChallenges] = useState([]);
  const [hostEvents, setHostEvents] = useState([]);
  const [hostLeaderboard, setHostLeaderboard] = useState([]);
  const [hostChallengeFilter, setHostChallengeFilter] = useState("all");
  const [hostEventFilter, setHostEventFilter] = useState("all");
  const [showChallengeAnalytics, setShowChallengeAnalytics] = useState(false);
  
  // Admin Challenge Analytics states
  const [showAdminChallengeAnalytics, setShowAdminChallengeAnalytics] = useState(false);
  const [adminChallengeAnalytics, setAdminChallengeAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFilters, setAnalyticsFilters] = useState({
    sortBy: 'created_at',
    sortOrder: 'desc',
    status: 'all',
    category: 'all',
    createdBy: 'all'
  });

  // Magazine system states
  const [articles, setArticles] = useState([]);
  const [selectedMagazine, setSelectedMagazine] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showMagazineForm, setShowMagazineForm] = useState(false);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [showAdRequestForm, setShowAdRequestForm] = useState(false);
  const [editingMagazine, setEditingMagazine] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [userAdRequests, setUserAdRequests] = useState([]);
  const [allAdRequests, setAllAdRequests] = useState([]);
  
  // Admin Magazine Filtering States
  const [magazineTab, setMagazineTab] = useState('all'); // all, admin, host, event
  const [magazineFilters, setMagazineFilters] = useState({
    hostName: '',
    location: '',
    startDate: '',
    endDate: ''
  });
  const [availableLocations, setAvailableLocations] = useState([]);
  const [magazineForm, setMagazineForm] = useState({
    title: '',
    issue_number: '',
    description: '',
    cover_image_url: '',
    pdf_file_url: '',
    pdf_file_name: ''
  });
  const [articleForm, setArticleForm] = useState({
    magazine_id: '',
    title: '',
    subtitle: '',
    content: '',
    excerpt: '',
    author_name: '',
    author_bio: '',
    author_image_url: '',
    category: '',
    featured_image_url: '',
    tags: [],
    read_time_minutes: ''
  });
  const [adRequestForm, setAdRequestForm] = useState({
    company_name: '',
    ad_content: '',
    target_page: '',
    budget_range: '',
    contact_phone: '',
    additional_notes: ''
  });

  // Chat system states
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // messageId for which emoji picker is shown

  
  // Settings states
  const [userProfile, setUserProfile] = useState({});

  const [profilePictureDragging, setProfilePictureDragging] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: true,
    event_reminder_emails: true,
    challenge_completion_emails: true,
    magazine_drop_emails: true,
    chat_notification_emails: true,
    announcement_notifications: true,
    boost_notifications: true,
    sms_notifications: true,
    event_reminder_sms: true,
    announcement_sms: true,
    weekly_digest: true
  });
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'public',
    contact_info_visible: true,
    activity_visible: true,
    leaderboard_visible: true
  });
  const [accountSettings, setAccountSettings] = useState({
    language: 'en',
    timezone: 'UTC',
    two_factor_enabled: false
  });

  
  // Admin analytics states
  const [boostAnalytics, setBoostAnalytics] = useState(null);
  const [platformConfig, setPlatformConfig] = useState({
    challenge_completion: 100,
    event_attendance: 50,
    daily_login: 10,
    boost_given: 5,
    boost_received: 10,
    referral_signup: 200,
    daily_boost_limit: 5
  });
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);


  const [chatLoading, setChatLoading] = useState(false);
  const [chatPollingInterval, setChatPollingInterval] = useState(null);

  // Filter events based on selected filter
  const getFilteredEvents = () => {
    if (!memberEvents || memberEvents.length === 0) {
      return [];
    }
    
    console.log(`🔍 Filtering ${memberEvents.length} events with filter: ${eventFilter}`);
    
    const now = new Date();
    
    const filtered = memberEvents.filter(event => {
      // Fix date parsing - add 'Z' if no timezone info
      const dateStr = event.event_date;
      const eventDate = new Date(dateStr.includes('T') && !dateStr.includes('Z') ? dateStr + 'Z' : dateStr);
      
      switch(eventFilter) {
        case 'upcoming':
          const isUpcoming = eventDate >= now;
          return isUpcoming;
        case 'past':
          return eventDate < now;
        case 'my_rsvps':
          return event.rsvp_status === 'attending' || event.rsvp_status === 'not_attending';
        case 'host_events':
          return event.is_host_specific === true;
        case 'general_events':
          return event.is_host_specific === false;
        default:
          return true;
      }
    });
    
    console.log(`📊 Filtered to ${filtered.length} events`);
    return filtered;
  };

  // Filter challenges based on selected filter
  const getFilteredChallenges = () => {
    if (!memberChallenges || memberChallenges.length === 0) {
      return [];
    }
    
    console.log(`🔍 Filtering ${memberChallenges.length} challenges with filter: ${challengeFilter}`);
    
    const now = new Date();
    
    const filtered = memberChallenges.filter(challenge => {
      // Fix date parsing for deadline
      const deadlineStr = challenge.deadline;
      const deadlineDate = deadlineStr ? new Date(deadlineStr.includes('T') && !deadlineStr.includes('Z') ? deadlineStr + 'Z' : deadlineStr) : null;
      
      switch(challengeFilter) {
        case 'active':
          // Show all available challenges (active status or no status) that haven't expired
          return (challenge.status === 'active' || !challenge.status || challenge.status === 'draft') && 
                 (!deadlineDate || deadlineDate >= now);
        case 'completed': 
          return challenge.participation_status === 'completed';
        case 'my_challenges':
          return challenge.participation_status === 'active' || challenge.participation_status === 'completed';
        case 'all':
          // Show ALL challenges regardless of status or deadline
          return true;
        default:
          // Default: show all non-expired challenges
          return (challenge.status === 'active' || !challenge.status || challenge.status === 'draft') && 
                 (!deadlineDate || deadlineDate >= now);
      }
    });
    
    console.log(`📊 Filtered to ${filtered.length} challenges`);
    return filtered;
  };

  // Filter admin challenges based on selected filter
  const getFilteredAdminChallenges = () => {
    if (!challenges || challenges.length === 0) {
      return [];
    }
    
    const now = new Date();
    
    return challenges.filter(challenge => {
      const deadlineStr = challenge.deadline;
      const deadlineDate = deadlineStr ? new Date(deadlineStr) : null;
      
      switch(adminChallengeFilter) {
        case 'current':
          // Current and upcoming challenges (no deadline or future deadline)
          return !deadlineDate || deadlineDate >= now;
        case 'past':
          // Past challenges (deadline has passed)
          return deadlineDate && deadlineDate < now;
        default:
          return true;
      }
    });
  };

  // Filter admin events based on selected filter
  const getFilteredAdminEvents = () => {
    if (!events || events.length === 0) {
      return [];
    }
    
    const now = new Date();
    
    return events.filter(event => {
      const eventDateStr = event.event_date;
      const eventDate = eventDateStr ? new Date(eventDateStr.includes('T') && !eventDateStr.includes('Z') ? eventDateStr + 'Z' : eventDateStr) : null;
      
      switch(adminEventFilter) {
        case 'current':
          // Current and upcoming events (no date or future date)
          return !eventDate || eventDate >= now;
        case 'past':
          // Past events (date has passed)
          return eventDate && eventDate < now;
        default:
          return true;
      }
    });
  };

  // RSVP Function
  const handleRSVP = async (eventId, status) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to RSVP');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/member/events/${eventId}/rsvp`,
        { status: status },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Optimistically update the RSVP status first
      setMemberEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === eventId 
            ? { ...event, rsvp_status: status }
            : event
        )
      );

      // Refetch events to get updated attendance count
      const authHeaders = { Authorization: `Bearer ${token}` };
      const memberEventsRes = await axios.get(BACKEND_URL + "/api/member/events", { headers: authHeaders });
      setMemberEvents(memberEventsRes.data.events || []);

      alert(`RSVP ${status === 'attending' ? 'confirmed' : 'updated'}: ${response.data.message}`);
    } catch (error) {
      console.error('RSVP Error:', error);
      alert('Failed to RSVP. Please try again.');
    }
  };

  // Challenge Join/Leave Functions
  const handleJoinChallenge = async (challengeId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to join challenges');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/member/challenges/${challengeId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Optimistically update the challenge status
      setMemberChallenges(prevChallenges => 
        prevChallenges.map(challenge => 
          challenge.id === challengeId 
            ? { ...challenge, participation_status: 'active' }
            : challenge
        )
      );

      // Refetch challenges to get updated data
      const authHeaders = { Authorization: `Bearer ${token}` };
      const memberChallengesRes = await axios.get(BACKEND_URL + "/api/member/challenges", { 
        headers: authHeaders,
        params: { status: 'active' }
      });
      setMemberChallenges(memberChallengesRes.data.challenges || []);

      alert(`Successfully joined challenge: ${response.data.message}`);
    } catch (error) {
      console.error('Join Challenge Error:', error);
      alert(error.response?.data?.detail || 'Failed to join challenge. Please try again.');
    }
  };

  const handleLeaveChallenge = async (challengeId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to manage challenges');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/member/challenges/${challengeId}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Optimistically update the challenge status
      setMemberChallenges(prevChallenges => 
        prevChallenges.map(challenge => 
          challenge.id === challengeId 
            ? { ...challenge, participation_status: 'not_joined' }
            : challenge
        )
      );

      // Refetch challenges to get updated data
      const authHeaders = { Authorization: `Bearer ${token}` };
      const memberChallengesRes = await axios.get(BACKEND_URL + "/api/member/challenges", { 
        headers: authHeaders,
        params: { status: 'active' }
      });
      setMemberChallenges(memberChallengesRes.data.challenges || []);

      alert(`Successfully left challenge: ${response.data.message}`);
    } catch (error) {
      console.error('Leave Challenge Error:', error);
      alert(error.response?.data?.detail || 'Failed to leave challenge. Please try again.');
    }
  };

  // Social Media Boost Functions
  const fetchSocialData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const authHeaders = { Authorization: `Bearer ${token}` };
      
      // Fetch available boost targets
      const targetsRes = await axios.get(`${BACKEND_URL}/api/profiles/boost-targets`, { headers: authHeaders });
      setBoostTargets(targetsRes.data.targets || []);

      // Fetch boost stats
      try {
        const statsRes = await axios.get(`${BACKEND_URL}/api/member/boosts`, { headers: authHeaders });
        if (statsRes.data.stats) {
          setBoostStats({
            boosts_given: statsRes.data.stats.boosts_given || 0,
            boosts_received: statsRes.data.stats.boosts_received || 0,
            points_earned: statsRes.data.stats.points_earned_from_boosts || 0,
            this_month_given: statsRes.data.stats.boosts_given || 0, // TODO: Add monthly tracking
            this_month_received: statsRes.data.stats.boosts_received || 0
          });
        }
      } catch (statsError) {
        console.log('Boost stats not available:', statsError);
      }

      // Fetch current user's social profile
      const profileRes = await axios.get(`${BACKEND_URL}/api/profiles/my-profile`, { headers: authHeaders });
      const userProfile = profileRes.data;
      
      // Update the social links state with current profile data
      setSocialLinks({
        instagram: userProfile.links?.instagram || '',
        linkedin: userProfile.links?.linkedin || '',
        facebook: userProfile.links?.facebook || '',
        google_reviews: userProfile.links?.google_reviews || ''
      });

      console.log("✅ Social boost data loaded", userProfile);

    } catch (error) {
      console.error('Error fetching social data:', error);
    }
  };

  // Host Data Fetching Functions
  const fetchHostData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const authHeaders = { Authorization: `Bearer ${token}` };
      
      // Fetch host-specific data
      const [challengesRes, eventsRes, leaderboardRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/host/challenges`, { headers: authHeaders }),
        axios.get(`${BACKEND_URL}/api/host/events`, { headers: authHeaders }),
        axios.get(`${BACKEND_URL}/api/host/leaderboard`, { headers: authHeaders })
      ]);

      setHostChallenges(challengesRes.data || []);
      setHostEvents(eventsRes.data || []);
      setHostLeaderboard(leaderboardRes.data || []);

      console.log("✅ Host data loaded successfully");

    } catch (error) {
      console.error('Error fetching host data:', error);
      // Set empty arrays as fallback
      setHostChallenges([]);
      setHostEvents([]);
      setHostLeaderboard([]);
    }
  };

  // Host Filter Functions
  const getFilteredHostChallenges = () => {
    if (!hostChallenges) return [];
    
    switch (hostChallengeFilter) {
      case 'active':
        return hostChallenges.filter(challenge => challenge.status === 'active');
      case 'completed':
        return hostChallenges.filter(challenge => challenge.status === 'completed');
      default:
        return hostChallenges;
    }
  };

  const getFilteredHostEvents = () => {
    if (!hostEvents) return [];
    
    const now = new Date();
    switch (hostEventFilter) {
      case 'upcoming':
        return hostEvents.filter(event => new Date(event.event_date) >= now);
      case 'past':
        return hostEvents.filter(event => new Date(event.event_date) < now);
      default:
        return hostEvents;
    }
  };

  // Host Action Functions
  const handleEditChallenge = (challenge) => {
    setSelectedChallenge(challenge);
    setShowChallengeForm(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setShowEventForm(true);
  };

  // Chat Functions
  // Helper functions for portal-specific localStorage
  const getPortalStorageKey = (key) => `${portalMode}_${key}`;
  
  const getPortalToken = () => {
    // Try portal-specific token first, then fallback to generic token
    return localStorage.getItem(getPortalStorageKey('access_token')) || 
           localStorage.getItem('access_token');
  };
  
  const setPortalToken = (token) => {
    // Store both portal-specific and generic tokens
    localStorage.setItem(getPortalStorageKey('access_token'), token);
    localStorage.setItem('access_token', token);
  };

  const fetchConversations = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/chat/conversations`, { headers: authHeaders });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      setChatLoading(true);
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/chat/conversations/${conversationId}/messages`, { headers: authHeaders });
      setMessages(response.data.messages || []);
      
      // Auto-scroll to bottom after messages load
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setChatLoading(false);
    }
  };

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('[data-chat-messages]');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  const searchUsers = async (query) => {
    try {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      const token = getPortalToken();
      if (!token) return;

      const authHeaders = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/users/search?query=${encodeURIComponent(query)}`, { headers: authHeaders });
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    }
  };

  const startConversation = async (userId) => {
    try {
      const token = getPortalToken();
      if (!token) return;

      const authHeaders = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${BACKEND_URL}/api/conversations`, {
        participants: [userId],
        conversation_type: "direct"
      }, { headers: authHeaders });

      const newConversation = response.data;
      
      // Update conversations list
      setConversations(prev => [newConversation, ...prev.filter(c => c.id !== newConversation.id)]);
      
      // Select the conversation
      setSelectedConversation(newConversation);
      await fetchMessages(newConversation.id);
      
      // Close modal
      setShowNewChatModal(false);
      setUserSearchQuery("");
      setSearchResults([]);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const token = getPortalToken();
      if (!token) return;

      const authHeaders = getAuthHeaders();
      const response = await axios.post(
        `${API}/chat/conversations/${selectedConversation.id}/messages`,
        null,
        {
          headers: authHeaders,
          params: {
            content: newMessage.trim()
          }
        }
      );

      // Refresh messages to show the new one
      await fetchMessages(selectedConversation.id);
      
      const sentMessage = { content: newMessage.trim(), created_at: response.data.created_at };
      
      // Add message to current messages
      setMessages(prev => [...prev, sentMessage]);
      
      // Update conversation in list
      setConversations(prev => prev.map(conv => 
        conv.id === selectedConversation.id 
          ? { ...conv, last_message: newMessage.trim(), last_message_at: sentMessage.timestamp }
          : conv
      ));
      
      // Clear input
      setNewMessage("");
      
      // Auto-scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
      
      // Refresh conversations to ensure both sides see the update
      setTimeout(() => fetchConversations(), 500);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };


  // Enhanced chat features
  const searchUsersForChat = async (query) => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/chat/users/search`, {
        headers: authHeaders,
        params: { query }
      });
      return response.data.users || [];
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  };

  const createConversation = async (participantId) => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.post(
        `${API}/chat/conversations`,
        null,
        {
          headers: authHeaders,
          params: { participant_id: participantId }
        }
      );
      await fetchConversations();
      return response.data.conversation_id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to start conversation');
      return null;
    }
  };

  const addReaction = async (messageId, emoji) => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.post(
        `${API}/chat/messages/${messageId}/react`,
        null,
        {
          headers: authHeaders,
          params: { emoji }
        }
      );
      // Refresh messages to show updated reactions
      if (selectedConversation) {
        await fetchMessages(selectedConversation.id);
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const updateTypingStatus = async (isTyping) => {
    if (!selectedConversation) return;
    try {
      const authHeaders = getAuthHeaders();
      await axios.post(
        `${API}/chat/typing`,
        null,
        {
          headers: authHeaders,
          params: {
            conversation_id: selectedConversation.id,
            is_typing: isTyping
          }
        }
      );
    } catch (error) {
      console.error('Error updating typing status:', error);
    }
  };

  const getTypingStatus = async () => {
    if (!selectedConversation) return [];
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/chat/typing/${selectedConversation.id}`, {
        headers: authHeaders
      });
      return response.data.typing_users || [];
    } catch (error) {
      console.error('Error getting typing status:', error);
      return [];
    }
  };


  // Helper function to get current user ID reliably
  const getCurrentUserId = () => {
    let currentUserId = null;
    let source = '';
    
    // PRIORITY 1: Try to decode JWT token first (source of truth for authenticated user)
    try {
      const token = getPortalToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.user_id || payload.sub || payload.id) {
          currentUserId = payload.user_id || payload.sub || payload.id;
          source = 'jwt-token';
        }
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    
    // PRIORITY 2: Fallback to user state
    if (!currentUserId && user?.id) {
      currentUserId = user.id;
      source = 'user-state';
    }
    
    // PRIORITY 3: Fallback to portal-specific localStorage user data
    if (!currentUserId) {
      try {
        const portalUser = localStorage.getItem(getPortalStorageKey('user'));
        if (portalUser) {
          const userData = JSON.parse(portalUser);
          if (userData.id) {
            currentUserId = userData.id;
            source = 'portal-localStorage';
          }
        } else {
          // Fallback to generic user data
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            if (userData.id) {
              currentUserId = userData.id;
              source = 'generic-localStorage';
            }
          }
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
    }
    
    // Debug logging for user ID detection
    console.log(`🔍 User ID Debug - Portal: ${portalMode}, UserID: ${currentUserId}, Source: ${source}`, {
      userState: user?.id,
      userRole: user?.role,
      portalMode
    });
    
    return currentUserId;
  };

  // Fetch conversations when chat tab becomes active
  React.useEffect(() => {
    if (activeTab === "chat" && localStorage.getItem('access_token')) {
      fetchConversations();
    }
  }, [activeTab]);

  // Chat polling for real-time updates
  const startChatPolling = () => {
    // Clear existing interval if any
    if (chatPollingInterval) {
      clearInterval(chatPollingInterval);
    }

    // Poll every 3 seconds when chat is active
    const interval = setInterval(() => {
      if (activeTab === "chat") {
        // Refresh conversations list
        fetchConversations();
        
        // Refresh current conversation messages if one is selected
        if (selectedConversation) {
          fetchMessages(selectedConversation.id);
        }
      }
    }, 3000);

    setChatPollingInterval(interval);
  };

  const stopChatPolling = () => {
    if (chatPollingInterval) {
      clearInterval(chatPollingInterval);
      setChatPollingInterval(null);
    }
  };

  // Start/stop polling based on chat tab activity
  React.useEffect(() => {
    if (activeTab === "chat") {
      startChatPolling();
    } else {
      stopChatPolling();
    }

    // Cleanup on unmount
    return () => stopChatPolling();
  }, [activeTab, selectedConversation]);


  // Fetch settings when settings tab becomes active
  React.useEffect(() => {
    if (activeTab === "settings" && localStorage.getItem('access_token')) {
      fetchUserSettings();
      // Load platform config for admins
      if (portalMode === "admin") {
        fetchPlatformConfig();
      }
    }
  }, [activeTab, portalMode]);


  // Host-specific form handlers
  const handleChallengeSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      const challengeData = {
        title: challengeForm.title,
        description: challengeForm.description,
        category: challengeForm.category,
        points_reward: parseInt(challengeForm.points_reward), // Ensure it's a number
        deadline: new Date(challengeForm.deadline).toISOString(),
        is_recurring: challengeForm.is_recurring || false,
        recurrence_type: challengeForm.recurrence_type || "none", // Enum value
        recurrence_interval: parseInt(challengeForm.recurrence_interval) || 1,
        recurrence_end_date: challengeForm.recurrence_end_date || null,
        approval_type: challengeForm.approval_type || "automatic", // Enum value
        target_segment: challengeForm.target_segment || "all", // Enum value
        segment_min_rank: challengeForm.segment_min_rank ? parseInt(challengeForm.segment_min_rank) : null,
        segment_max_rank: challengeForm.segment_max_rank ? parseInt(challengeForm.segment_max_rank) : null
      };

      // Determine the correct API endpoint based on portal mode and construct clean URL
      const challengesEndpoint = portalMode === "admin" ? "admin/challenges" : "host/challenges";
      
      // Use clean URL construction to avoid any potential suffix issues
      const baseURL = BACKEND_URL.replace(/\/$/, ''); // Remove trailing slash
      const apiPath = `/api/${challengesEndpoint}`;
      const fullURL = baseURL + apiPath;
      
      if (editingChallenge) {
        // Update existing challenge
        const updateURL = `${baseURL}/api/${challengesEndpoint}/${editingChallenge.id}`;
        await axios.put(updateURL, challengeData, { 
          headers: authHeaders,
          timeout: 10000 // 10 second timeout
        });
        alert('Challenge updated successfully!');
      } else {
        // Create new challenge
        await axios.post(fullURL, challengeData, { 
          headers: authHeaders,
          timeout: 10000 // 10 second timeout
        });
        alert('Challenge created successfully!');
      }

      // Refresh host data and close form
      await fetchHostData();
      setShowChallengeForm(false);
      setEditingChallenge(null);
      setChallengeForm({ 
        title: "", 
        description: "", 
        category: "", 
        points_reward: 50, 
        deadline: "" 
      });

    } catch (error) {
      console.error('Challenge creation error:', error);
      console.log('Error details:', {
        status: error.response?.status,
        url: error.config?.url,
        data: error.response?.data
      });
      
      // Better error message handling
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.status === 404) {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please ensure you are logged in as a host.';
      } else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else {
        errorMessage = error.message;
      }
      
      alert(`Failed to ${editingChallenge ? 'update' : 'create'} challenge: ${errorMessage}`);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      const eventData = {
        ...eventForm,
        event_date: new Date(eventForm.event_date).toISOString()
      };

      // Determine the correct API endpoint based on portal mode
      const eventsEndpoint = portalMode === "admin" ? "admin/events" : "host/events";
      
      if (editingEvent) {
        // Update existing event
        await axios.put(`${BACKEND_URL}/api/${eventsEndpoint}/${editingEvent.id}`, eventData, { headers: authHeaders });
        alert('Event updated successfully!');
      } else {
        // Create new event
        await axios.post(`${BACKEND_URL}/api/${eventsEndpoint}`, eventData, { headers: authHeaders });
        alert('Event created successfully!');
      }

      // Refresh host data and close form
      await fetchHostData();
      setShowEventForm(false);
      setEditingEvent(null);
      setEventForm({ 
        title: "", 
        description: "", 
        event_date: "", 
        event_type: "online", 
        online_link: "", 
        address: "", 
        max_attendees: 20, 
        points_reward: 50 
      });

    } catch (error) {
      console.error('Error saving event:', error);
      
      // Better error message handling
      let errorMessage = error.message;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          // Handle validation errors or other complex error objects
          errorMessage = JSON.stringify(error.response.data);
        }
      }
      
      alert(`Failed to ${editingEvent ? 'update' : 'create'} event: ${errorMessage}`);
    }
  };

  // Challenge Progress Functions
  const fetchChallengeProgress = async (challengeId) => {
    try {
      const token = getPortalToken();
      if (!token) return null;

      const authHeaders = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}/progress`, { headers: authHeaders });
      return response.data;
    } catch (error) {
      console.error('Error fetching challenge progress:', error);
      return null;
    }
  };

  const submitChallengeCompletion = async (challengeId, submissionData) => {
    try {
      const token = getPortalToken();
      if (!token) return;

      const authHeaders = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${BACKEND_URL}/api/challenges/${challengeId}/submit`, submissionData, { headers: authHeaders });
      
      alert(t('challenges.submissionSuccess'));
      
      // Refresh challenges data
      if (portalMode === "member") {
        fetchData();
      } else if (portalMode === "host") {
        fetchHostData();
      } else if (portalMode === "admin") {
        fetchData();
      }
      
      return response.data;
    } catch (error) {
      console.error('Error submitting challenge completion:', error);
      alert(`Failed to submit challenge completion: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  const updateChallengeProgress = async (challengeId, progressData) => {
    try {
      const token = getPortalToken();
      if (!token) return;

      const authHeaders = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${BACKEND_URL}/api/challenges/${challengeId}/progress`, progressData, { headers: authHeaders });
      
      // Refresh challenges data
      if (portalMode === "member") {
        fetchData();
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating challenge progress:', error);
      alert(`Failed to update progress: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Confetti animation trigger
  const triggerConfetti = () => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Create confetti from two edges
      if (window.confetti) {
        window.confetti(Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        }));
        window.confetti(Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        }));
      }
    }, 250);
  };

  // Show boost success notification
  const showBoostSuccessNotification = (platform, points, persist = false) => {
    setBoostNotificationData({ platform, points });
    setShowBoostNotification(true);

    if (persist) {
      // Store in localStorage for when user returns
      localStorage.setItem('pendingBoostNotification', JSON.stringify({ 
        platform, 
        points, 
        timestamp: Date.now() 
      }));
    }

    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowBoostNotification(false);
      // Clear from localStorage after showing
      localStorage.removeItem('pendingBoostNotification');
    }, 4000);
  };

  // Accountability Partner Functions
  const fetchAccountabilityStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountabilityStatus(response.data);
    } catch (error) {
      console.error('Error fetching accountability status:', error);
    }
  };

  const fetchAvailablePartners = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/available-partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePartners(response.data.available_partners);
    } catch (error) {
      console.error('Error fetching available partners:', error);
    }
  };

  const handleSelectPartner = async (partnerId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/select-partner`, 
        { partner_id: partnerId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowPartnerSelection(false);
      await fetchAccountabilityStatus();
      setPartnerSuccessMessage('Partnership request sent! Your accountability partner has been notified by email.');
      setShowPartnerSuccessNotification(true);
      setTimeout(() => setShowPartnerSuccessNotification(false), 5000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to select partner');
    }
  };

  const handleRandomPartner = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${BACKEND_URL}/api/member/accountability/random-partner`, {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchAccountabilityStatus();
      setPartnerSuccessMessage(`You've been paired with ${response.data.partner.name}! They have been notified by email.`);
      setShowPartnerSuccessNotification(true);
      setTimeout(() => setShowPartnerSuccessNotification(false), 5000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to find partner');
    }
  };

  const handleCheckIn = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${BACKEND_URL}/api/member/accountability/check-in`,
        checkInForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowCheckInForm(false);
      setCheckInForm({ notes: '', progress_rating: 3 });
      await fetchAccountabilityStatus();
      alert(`Check-in submitted! You earned ${response.data.points_earned} points.`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit check-in');
    }
  };

  const handleSubmitReview = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/end-partnership`,
        reviewForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowReviewForm(false);
      setReviewForm({ rating: 5, review: '' });
      await fetchAccountabilityStatus();
      await fetchAccountabilityReviews(); // Refresh reviews
      alert('Thank you for your review! Your partnership has ended.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const fetchAccountabilityReviews = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountabilityReviews(response.data);
    } catch (error) {
      console.error('Error fetching accountability reviews:', error);
    }
  };

  const fetchWeeklyGoals = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/weekly-goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyGoals(response.data);
    } catch (error) {
      console.error('Error fetching weekly goals:', error);
    }
  };

  const fetchParticipationStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/participation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipationStatus(response.data);
    } catch (error) {
      console.error('Error fetching participation status:', error);
    }
  };

  const handleParticipate = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/participate`, {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchParticipationStatus();
      alert('Thank you for participating this week!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark participation');
    }
  };

  const handleSubmitGoals = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/weekly-goals`,
        { goals: goalsFormData },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowGoalsForm(false);
      setGoalsFormData([]);
      setNewGoalText('');
      await fetchWeeklyGoals();
      alert('Weekly goals saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save goals');
    }
  };

  const handleAddGoalItem = () => {
    if (newGoalText.trim()) {
      setGoalsFormData([...goalsFormData, newGoalText.trim()]);
      setNewGoalText('');
    }
  };

  const handleRemoveGoalItem = (index) => {
    setGoalsFormData(goalsFormData.filter((_, i) => i !== index));
  };

  const handleToggleGoal = async (goalItemId, isMyGoal) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/toggle-goal`,
        { goal_item_id: goalItemId, is_my_goal: isMyGoal },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchWeeklyGoals();
    } catch (error) {
      console.error('Error toggling goal:', error);
      alert(error.response?.data?.detail || 'Failed to toggle goal');
    }
  };

  const fetchScheduledCalls = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/scheduled-calls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledCalls(response.data.calls || []);
    } catch (error) {
      console.error('Error fetching scheduled calls:', error);
    }
  };

  const handleScheduleCall = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/schedule-call`,
        callFormData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setShowScheduleCallForm(false);
      setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
      await fetchScheduledCalls();
      alert('Video call scheduled successfully! Your partner has been notified.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to schedule call');
    }
  };

  const fetchChatMessages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/chat-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!accountabilityChatMessage.trim()) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/member/accountability/send-message`,
        { message: accountabilityChatMessage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setAccountabilityChatMessage('');
      await fetchChatMessages();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/member/accountability/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Generic notification helpers
  const showPointsEarnedNotification = (title, message, points) => {
    setPointsNotificationData({ title, message, points });
    setShowPointsNotification(true);
    setTimeout(() => setShowPointsNotification(false), 4000);
  };

  const showSuccessNotification = (title, message, type = 'success') => {
    setSimpleNotificationData({ title, message, type });
    setShowSimpleNotification(true);
    setTimeout(() => setShowSimpleNotification(false), 3000);
  };

  const fetchCancelledSubscriptions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/cancelled-subscriptions?timeframe=${subscriptionTimeframe}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancelledSubscriptions(response.data.cancellations || []);
    } catch (error) {
      console.error('Error fetching cancelled subscriptions:', error);
    }
  };

  // Analytics fetching functions
  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch all analytics data
      const [breakdown, timeline, heatmap, stats] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/points-breakdown`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/analytics/points-timeline?timeframe=${timelineFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/analytics/activity-heatmap`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/analytics/user-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPointsBreakdown(breakdown.data);
      setPointsTimeline(timeline.data);
      setActivityHeatmap(heatmap.data);
      setUserStats(stats.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchAdminAnalytics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      console.log('Calling admin analytics endpoint with activation period:', activationPeriod);
      const response = await axios.get(`${BACKEND_URL}/api/admin/analytics/overview?activationPeriod=${activationPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Admin analytics response:', response.data);
      setAdminAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching admin analytics:', error);
      console.error('Error details:', error.response?.data);
      // Set empty data to stop loading state
      setAdminAnalytics({
        users: { total_members: 0, total_hosts: 0, active_users: 0 },
        points: { total_distributed: 0, average_per_user: 0 },
        engagement: { total_checkins: 0, total_boosts: 0, total_challenges: 0, recent_checkins: 0, recent_boosts: 0 },
        top_performers: { members: [], hosts: [] },
        points_timeline: []
      });
    }
  };

  // Load accountability status when accountability tab is active
  useEffect(() => {
    if (activeTab === 'accountability' && portalMode === 'member') {
      fetchAccountabilityStatus();
      fetchAccountabilityReviews();
      fetchWeeklyGoals();
      fetchParticipationStatus();
      fetchScheduledCalls();
      fetchChatMessages();
      fetchUnreadCount();
    }
  }, [activeTab, portalMode]);

  // Fetch magazines when tab changes or filters are applied
  useEffect(() => {
    if (activeTab === 'magazines' && portalMode === 'admin') {
      fetchMagazines();
    }
  }, [activeTab, portalMode, magazineTab]);


  // Real-time chat polling - check for new messages every 5 seconds when chat is open
  useEffect(() => {
    let chatInterval;
    if (activeTab === 'accountability' && activeAccountabilityTab === 'chat' && portalMode === 'member') {
      chatInterval = setInterval(() => {
        fetchChatMessages();
        fetchUnreadCount();
      }, 5000);
    }
    return () => {
      if (chatInterval) clearInterval(chatInterval);
    };
  }, [activeTab, activeAccountabilityTab, portalMode]);

  // Load analytics when dashboard is active
  useEffect(() => {
    if (activeTab === 'dashboard' && (portalMode === 'member' || portalMode === 'host')) {
      fetchAnalytics();
    }
    if (activeTab === 'dashboard' && portalMode === 'admin') {
      console.log('Fetching admin analytics...');
      fetchAdminAnalytics();
    }
  }, [activeTab, portalMode, timelineFilter]);

  // Check for pending boost notification when user returns to boosts tab
  useEffect(() => {
    if (activeTab === 'boosts' && portalMode === 'member') {
      const pending = localStorage.getItem('pendingBoostNotification');
      if (pending) {
        try {
          const data = JSON.parse(pending);
          // Only show if notification is less than 5 minutes old
          if (Date.now() - data.timestamp < 5 * 60 * 1000) {
            setTimeout(() => {
              triggerConfetti();
              showBoostSuccessNotification(data.platform, data.points, false);
            }, 500); // Small delay for smooth transition
          } else {
            // Clear old notification
            localStorage.removeItem('pendingBoostNotification');
          }
        } catch (e) {
          console.error('Error parsing pending notification:', e);
          localStorage.removeItem('pendingBoostNotification');
        }
      }
    }
  }, [activeTab, portalMode]);

  const handleBoost = async (targetProfileId, platform, profileUrl) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to give boosts');
        return;
      }

      // Open the social media profile in a new tab first
      window.open(profileUrl, '_blank', 'noopener');

      // Record the boost
      const response = await axios.post(
        `${BACKEND_URL}/api/boosts/give`,
        { target_profile_id: targetProfileId, platform: platform },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Update stats in real-time from API response
      setBoostStats(prev => ({
        ...prev,
        boosts_given: response.data.given_total || (prev.boosts_given + 1),
        points_earned: prev.points_earned + 25,
        this_month_given: prev.this_month_given + 1
      }));

      // Trigger confetti animation
      triggerConfetti();

      // Show exciting notification with persistence (will show when user returns)
      showBoostSuccessNotification(platform, 25, true);

      // Refresh data in background
      fetchSocialData();

    } catch (error) {
      console.error('Boost Error:', error);
      alert(error.response?.data?.detail || 'Failed to give boost. Please try again.');
    }
  };

  const updateSocialProfiles = async (links) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      await axios.post(
        `${BACKEND_URL}/api/profiles/update-links`,
        { links: links },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      alert('Social profiles updated successfully!');
      await fetchSocialData(); // Refresh targets

    } catch (error) {
      console.error('Error updating social profiles:', error);
      alert('Failed to update social profiles. Please try again.');
    }
  };
  
  // Existing state variables...
  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [challengeForm, setChallengeForm] = useState({
    title: "",
    description: "",
    points_reward: 50,
    deadline: "",
    is_recurring: false,
    recurrence_type: "none",
    recurrence_interval: 1,
    recurrence_end_date: "",
    approval_type: "automatic",
    target_segment: "all",
    segment_min_rank: "",
    segment_max_rank: ""
  });
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    event_date: "",
    event_type: "in_person",
    address: "",
    online_link: "",
    points_reward: 50,
    max_attendees: "",
    is_recurring: false,
    recurrence_type: "none",
    recurrence_interval: 1,
    recurrence_end_date: "",
    approval_type: "automatic",
    target_segment: "all",
    segment_min_rank: "",
    segment_max_rank: ""
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const hostSidebarItems = [
    { id: "dashboard", label: "Host Dashboard", icon: BarChart3 },
    { id: "revenue", label: "Revenue Analytics", icon: DollarSign },
    { id: "challenges", label: "Manage Challenges", icon: Target },
    { id: "leaderboard", label: "Leaderboard", icon: Trophy },
    { id: "events", label: "Events", icon: Calendar },
    { id: "chat", label: "Chat", icon: MessageCircle },
    { id: "marketing", label: "Marketing Tools", icon: Megaphone },
    { id: "members", label: "My Members", icon: Users },
    { id: "magazines", label: "Magazines", icon: BookOpen },
    { id: "settings", label: "Settings", icon: Settings }
  ];

  const memberSidebarItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "challenges", label: "Challenges", icon: Target },
    { id: "events", label: "Events", icon: Calendar },
    { id: "leaderboard", label: "Leaderboard", icon: Trophy },
    { id: "habits", label: "Habits", icon: Zap },
    { id: "accountability", label: "Accountability", icon: UserPlus },
    { id: "referrals", label: "Refer Friends", icon: UserPlus },
    { id: "boosts", label: "Social Boosts", icon: Megaphone },
    { id: "chat", label: "Chat", icon: MessageCircle },
    { id: "magazines", label: "Magazines", icon: BookOpen },
    { id: "settings", label: "Settings", icon: Settings }
  ];

  const adminSidebarItems = [
    { id: "dashboard", label: "Admin Dashboard", icon: BarChart3 },
    { id: "campaigns", label: "Email & SMS Campaigns", icon: Send },
    { id: "twilio-center", label: "Communication Center", icon: MessageSquare },
    { id: "pending-hosts", label: "Host Approvals", icon: Crown },
    { id: "manage-users", label: "Manage Users", icon: Users },
    { id: "manage-admins", label: "Manage Admins", icon: Shield },
    { id: "leaderboard", label: "Leaderboard", icon: Trophy },
    { id: "events", label: "Events", icon: Calendar },
    { id: "challenges", label: "Challenges", icon: Target },
    { id: "magazines", label: "Magazines", icon: BookOpen },
    { id: "chat", label: "Chat", icon: MessageCircle },
    { id: "settings", label: "Settings", icon: Settings }
  ];

  // Enhanced mock data for marketing boosts
  const mockMarketingBoosts = [
    {
      id: "boost-1",
      member: {
        name: "Sarah Johnson",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
        business: "SJ Digital Marketing"
      },
      platforms: [
        { type: "instagram", handle: "@sjdigitalmarketing", followers: 12500 },
        { type: "linkedin", handle: "Sarah Johnson", followers: 8900 },
        { type: "twitter", handle: "@sarahjohnsonbiz", followers: 6700 }
      ],
      boosts_given: 45,
      boosts_received: 67,
      points_earned: 890
    },
    {
      id: "boost-2", 
      member: {
        name: "Michael Chen",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        business: "Chen Tech Solutions"
      },
      platforms: [
        { type: "linkedin", handle: "Michael Chen", followers: 15200 },
        { type: "twitter", handle: "@michentechsol", followers: 4300 },
        { type: "facebook", handle: "Chen Tech Solutions", followers: 9800 }
      ],
      boosts_given: 32,
      boosts_received: 58,
      points_earned: 720
    }
  ];

  useEffect(() => {
    fetchData();
  }, [portalMode]);


  // Load campaigns when campaigns tab is active
  useEffect(() => {
    if (activeTab === 'campaigns' && portalMode === 'admin') {
      loadCampaigns();
    }
  }, [activeTab, portalMode]);

  const loadCampaigns = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/admin/campaigns`, { headers: authHeaders });
      setCampaigns(response.data);
      console.log('✅ Campaigns loaded:', response.data.length);
    } catch (error) {
      console.error('❌ Failed to load campaigns:', error);
    }
  };

  // Load notification preferences when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      loadNotificationPreferences();
    }
  }, [activeTab]);

  const loadNotificationPreferences = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/users/notification-preferences`, { headers: authHeaders });
      setNotificationPrefs(response.data);
      console.log('✅ Notification preferences loaded');
    } catch (error) {
      console.error('❌ Failed to load notification preferences:', error);
    }
  };

  // GSAP Animations for dashboard cards (excluding Total Points which has its own class)
  useEffect(() => {
    if (activeTab === 'dashboard' && dashboardStats) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Animate only non-Total Points cards to avoid animation issues with that specific card
        const cards = document.querySelectorAll('.dashboard-stat-card:not(.stats-card-gradient)');
        if (cards.length > 0) {
          // Reset cards to full opacity first to prevent stuck translucent state
          gsap.set(cards, { opacity: 1, y: 0, clearProps: 'all' });
          // Then animate
          animations.staggerFadeIn(cards, { delay: 0.1, stagger: 0.12 });
        }
      }, 100);
    }
  }, [activeTab, dashboardStats]);

  // Animate modal appearances
  useEffect(() => {
    if (showCampaignForm) {
      const modal = document.querySelector('.campaign-modal');
      if (modal) {
        animations.modalAppear(modal);
      }
    }
  }, [showCampaignForm]);

  // Animate campaign cards
  useEffect(() => {
    if (activeTab === 'campaigns' && campaigns.length > 0) {
      const cards = document.querySelectorAll('.campaign-card');
      if (cards.length > 0) {
        animations.staggerFadeIn(cards, { stagger: 0.08 });
      }
    }
  }, [campaigns, activeTab]);

  // GSAP ScrollTrigger animation for challenge progress bars
  useEffect(() => {
    const progressBars = document.querySelectorAll('.challenge-progress-bar');
    
    progressBars.forEach((bar) => {
      const progressValue = bar.getAttribute('data-progress') || bar.style.width || '0%';
      
      // Reset to 0 initially
      gsap.set(bar, { width: '0%' });
      
      // Animate on scroll
      ScrollTrigger.create({
        trigger: bar,
        start: 'top 80%',
        end: 'bottom 20%',
        onEnter: () => {
          gsap.to(bar, {
            width: progressValue,
            duration: 1.5,
            ease: 'power2.out'
          });
        },
        onLeaveBack: () => {
          gsap.to(bar, {
            width: '0%',
            duration: 0.5,
            ease: 'power2.in'
          });
        }
      });
    });
    
    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [challenges, activeTab]);

  // Initialize mobile carousel for membership cards (Homepage only)
  useEffect(() => {
    // Only run on homepage with mobile viewport
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && window.location.pathname === '/') {
      const cards = document.querySelectorAll('.luxury-card');
      if (cards && cards.length > 0) {
        // Initialize first card as active
        cards.forEach((card, idx) => {
          if (idx === 0) {
            card.classList.add('carousel-active');
          } else if (idx === 1) {
            card.classList.add('carousel-right');
          } else if (idx === 2) {
            card.classList.add('carousel-left');
          }
        });
      }
    }
  }, []);

  // GSAP ScrollTrigger animations for member analytics graphs
  useEffect(() => {
    if (activeTab === 'analytics' && portalMode === 'member') {
      // Animate analytics cards
      const analyticsCards = document.querySelectorAll('.member-analytics-card');
      analyticsCards.forEach((card, index) => {
        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          },
          opacity: 0,
          y: 30,
          duration: 0.6,
          delay: index * 0.1,
          ease: 'power2.out'
        });
      });

      // Animate pie chart sections
      const pieCharts = document.querySelectorAll('.recharts-pie-sector');
      pieCharts.forEach((sector, index) => {
        gsap.from(sector, {
          scrollTrigger: {
            trigger: sector.closest('.recharts-wrapper'),
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          },
          scale: 0,
          transformOrigin: 'center',
          duration: 0.8,
          delay: index * 0.05,
          ease: 'elastic.out(1, 0.5)'
        });
      });

      // Animate line/area charts
      const areaCharts = document.querySelectorAll('.recharts-area');
      areaCharts.forEach((area) => {
        gsap.from(area, {
          scrollTrigger: {
            trigger: area.closest('.recharts-wrapper'),
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          },
          opacity: 0,
          scaleY: 0,
          transformOrigin: 'bottom',
          duration: 1.2,
          ease: 'power3.out'
        });
      });

      // Animate heatmap cells
      const heatmapCells = document.querySelectorAll('.activity-heatmap-cell');
      heatmapCells.forEach((cell, index) => {
        gsap.from(cell, {
          scrollTrigger: {
            trigger: cell.closest('.grid'),
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          },
          scale: 0,
          opacity: 0,
          duration: 0.3,
          delay: index * 0.005,
          ease: 'back.out(1.7)'
        });
      });
    }

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [activeTab, portalMode, pointsTimeline, activityHeatmap]);


  // Handle metric card click for drill-down
  const handleMetricClick = async (metricType) => {
    setSelectedMetric(metricType);
    setShowAnalyticsModal(true);
    setAnalyticsRoleFilter('all'); // Reset to 'all' when opening new metric
    
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(
        `${API}/admin/analytics/detail/${metricType}?timeFilter=${analyticsTimeFilter}&roleFilter=all`,
        { headers: authHeaders }
      );
      setDetailedMetricData(response.data);
    } catch (error) {
      console.error('Failed to fetch detailed metric data:', error);
    }
  };

  // Handle time filter change
  const handleTimeFilterChange = async (newFilter) => {
    setAnalyticsTimeFilter(newFilter);
    if (selectedMetric) {
      try {
        const authHeaders = getAuthHeaders();
        const response = await axios.get(
          `${API}/admin/analytics/detail/${selectedMetric}?timeFilter=${newFilter}&roleFilter=${analyticsRoleFilter}`,
          { headers: authHeaders }
        );
        setDetailedMetricData(response.data);
      } catch (error) {
        console.error('Failed to fetch filtered metric data:', error);
      }
    }
  };

  // Handle role filter change
  const handleRoleFilterChange = async (newRoleFilter) => {
    setAnalyticsRoleFilter(newRoleFilter);
    if (selectedMetric) {
      try {
        const authHeaders = getAuthHeaders();
        const response = await axios.get(
          `${API}/admin/analytics/detail/${selectedMetric}?timeFilter=${analyticsTimeFilter}&roleFilter=${newRoleFilter}`,
          { headers: authHeaders }
        );
        setDetailedMetricData(response.data);
      } catch (error) {
        console.error('Failed to fetch filtered metric data:', error);
      }
    }
  };

  // Handle activation period change
  const handleActivationPeriodChange = async (newPeriod) => {
    setActivationPeriod(newPeriod);
    // Refetch admin analytics with new period
    if (portalMode === "admin") {
      try {
        const authHeaders = getAuthHeaders();
        const response = await axios.get(
          `${API}/admin/analytics/overview?activationPeriod=${newPeriod}`,
          { headers: authHeaders }
        );
        setAdminAnalytics(response.data);
      } catch (error) {
        console.error('Failed to refetch admin analytics:', error);
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const authHeaders = getAuthHeaders();
      console.log('Auth headers:', authHeaders);
      console.log('API base URL:', API);
      
      if (portalMode === "admin") {
        // Admin-specific data fetching with individual error handling
        console.log('Starting admin data fetch...');
        
        // First, try to get the most critical data individually
        try {
          const userRes = await axios.get(`${API}/user/profile?role=${portalMode}`, { headers: authHeaders });
          console.log('✅ User profile loaded:', userRes.data);
          setUser(userRes.data);
        } catch (error) {
          console.error('❌ Failed to load user profile:', error.response?.status, error.response?.data);
        }

        try {
          const statsRes = await axios.get(`${API}/admin/dashboard-stats`, { headers: authHeaders });
          console.log('✅ Dashboard stats loaded:', statsRes.data);
          setDashboardStats(statsRes.data);
        } catch (error) {
          console.error('❌ Failed to load dashboard stats:', error.response?.status, error.response?.data);
        }

        try {
          const pendingHostsRes = await axios.get(`${API}/admin/pending-hosts`, { headers: authHeaders });
          console.log('✅ Pending hosts loaded:', pendingHostsRes.data);
          setPendingHosts(pendingHostsRes.data.pending_hosts);
        } catch (error) {
          console.error('❌ Failed to load pending hosts:', error.response?.status, error.response?.data);
        }

        try {
          const allUsersRes = await axios.get(`${API}/admin/users/all?limit=100`, { headers: authHeaders });
          console.log('✅ All users loaded:', allUsersRes.data);
          setAllUsers(allUsersRes.data.users);
          setFilteredUsers(allUsersRes.data.users);
        } catch (error) {
          console.error('❌ Failed to load all users:', error.response?.status, error.response?.data);
        }

        // Load critical admin data (challenges and events) independently
        try {
          const challengesRes = await axios.get(`${API}/admin/challenges`, { headers: authHeaders });
          console.log('✅ Admin challenges loaded:', challengesRes.data);
          setChallenges(challengesRes.data || []);
        } catch (error) {
          console.error('❌ Failed to load challenges:', error.response?.status, error.response?.data);
          setChallenges([]); // Set empty array on failure
        }

        try {
          const eventsRes = await axios.get(`${API}/admin/events`, { headers: authHeaders });
          console.log('✅ Admin events loaded:', eventsRes.data);
          setEvents(eventsRes.data || []);
        } catch (error) {
          console.error('❌ Failed to load events:', error.response?.status, error.response?.data);
          setEvents([]); // Set empty array on failure
        }

        // Load other non-critical analytics data (can fail without breaking core functionality)
        try {
          const [
            pointsRes,
            leaderboardRes,
            adminsRes,
            revenueAnalyticsRes,
            engagementAnalyticsRes,
            hostPerformanceRes,
            challengeParticipationRes,
            twilioAnalyticsRes
          ] = await Promise.all([
            axios.get(`${API}/admin/points/analytics`, { headers: authHeaders }),
            axios.get(`${API}/admin/points/leaderboard`, { headers: authHeaders }),
            axios.get(`${API}/admin/admins`, { headers: authHeaders }),
            axios.get(`${API}/admin/analytics/revenue`, { headers: authHeaders }),
            axios.get(`${API}/admin/analytics/user-engagement`, { headers: authHeaders }),
            axios.get(`${API}/admin/analytics/host-performance`, { headers: authHeaders }),
            axios.get(`${API}/admin/analytics/challenge-participation`, { headers: authHeaders }),
            axios.get(`${API}/admin/twilio/analytics`, { headers: authHeaders })
          ]);
        
        // Challenges and Events already set above independently
        setPointsAnalytics(pointsRes.data);
        setAdminLeaderboard(leaderboardRes.data.leaderboard);
        setAdmins(adminsRes.data.admins);
        
        // Set analytics data
        setAnalyticsData({
          revenue: revenueAnalyticsRes.data,
          userEngagement: engagementAnalyticsRes.data,
          hostPerformance: hostPerformanceRes.data,
          challengeParticipation: challengeParticipationRes.data
        });
        
        // Set Twilio analytics data
        setTwilioAnalytics(twilioAnalyticsRes.data.analytics);
        
        console.log('✅ All additional admin data loaded successfully');
        } catch (error) {
          console.error('⚠️ Some additional admin data failed to load:', error);
        }
        
      } else {
        // Regular member/host data fetching
        try {
          const [
            userRes,
            statsRes,
            leaderboardRes,
            challengesRes,
            eventsRes,
            conversationsRes,
            magazinesRes,
            campaignsRes,
            membersRes
          ] = await Promise.all([
            axios.get(`${API}/user/profile?role=${portalMode}`, { headers: authHeaders }),
            axios.get(`${API}/dashboard/stats?role=${portalMode}`, { headers: authHeaders }),
            axios.get(`${API}/leaderboard?role=${portalMode}`, { headers: authHeaders }),
            axios.get(`${API}/challenges?role=${portalMode}`, { headers: authHeaders }),
            axios.get(`${API}/events`, { headers: authHeaders }),
            axios.get(`${API}/conversations`, { headers: authHeaders }),
            axios.get(`${API}/magazines`, { headers: authHeaders }),
            axios.get(`${API}/marketing/campaigns`, { headers: authHeaders }),
            axios.get(`${API}/members?role=${portalMode}`, { headers: authHeaders })
          ]);
          
          setUser(userRes.data);
          setDashboardStats(statsRes.data);
          setLeaderboard(leaderboardRes.data);
          setChallenges(challengesRes.data);
          setEvents(eventsRes.data);
          setConversations(conversationsRes.data);
          setMagazines(magazinesRes.data);
          setCampaigns(campaignsRes.data);
          setMembers(membersRes.data);
          setMarketingBoosts(mockMarketingBoosts);
        } catch (error) {
          console.error('❌ Failed to load member/host data:', error);
          // Continue even if some data fails to load
        }

        // Fetch host-specific data if in host mode
        if (portalMode === "host") {
          const revenueRes = await axios.get(`${API}/host/revenue`, { headers: authHeaders });
          setHostRevenue(revenueRes.data);
          
          // Fetch host challenges, events, and leaderboard
          await fetchHostData();
        }

        // Fetch chat conversations for all portal modes
        await fetchConversations();

        // Fetch magazine data for all portal modes
        await fetchMagazines();
        await fetchArticles();
        await fetchUserAdRequests();

        // Fetch member-specific data if in member mode
        if (portalMode === "member") {
          try {
            console.log("🚀 Starting member data fetch...");
            
            const memberDashRes = await axios.get(BACKEND_URL + "/api/member/dashboard", { headers: authHeaders });
            setMemberDashboardData(memberDashRes.data);
            console.log("✅ Member dashboard loaded");
            
            const memberChallengesRes = await axios.get(BACKEND_URL + "/api/member/challenges", { 
              headers: authHeaders,
              params: { status: 'active' }
            });
            setMemberChallenges(memberChallengesRes.data.challenges || []);
            console.log("✅ Member challenges loaded:", memberChallengesRes.data.challenges?.length || 0);
            
            const memberEventsRes = await axios.get(BACKEND_URL + "/api/member/events", { headers: authHeaders });
            setMemberEvents(memberEventsRes.data.events || []);
            console.log("✅ Member events loaded:", memberEventsRes.data.events?.length || 0);
            
            const memberReferralsRes = await axios.get(BACKEND_URL + "/api/member/referrals", { headers: authHeaders });
            setMemberReferrals(memberReferralsRes.data);
            console.log("✅ Member referrals loaded");
            
            const memberBoostsRes = await axios.get(BACKEND_URL + "/api/member/boosts", { headers: authHeaders });
            setMemberBoosts(memberBoostsRes.data);
            console.log("✅ Member boosts loaded");

            // Fetch social media data
            await fetchSocialData();
            console.log("✅ Social media data loaded");
            
          } catch (memberError) {
            console.error("❌ Error fetching member data:", memberError);
            console.error("❌ Error details:", memberError.response?.data);
            // Don't break the whole app if member data fails
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      
      // Check if it's an authentication error (401 unauthorized or 403 forbidden)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn("Authentication failed - token expired or invalid. Redirecting to login...");
        
        // Clear stored authentication data
        localStorage.removeItem('access_token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
        
        // Redirect to login
        window.location.href = '/auth';
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch admin challenge analytics (detailed version)
  const fetchAdminChallengeAnalytics = async (filters = {}) => {
    try {
      setAnalyticsLoading(true);
      const authHeaders = getAuthHeaders();
      
      // Build query parameters for filtering and sorting
      const params = new URLSearchParams({
        sort_by: filters.sortBy || 'created_at',
        sort_order: filters.sortOrder || 'desc',
        filter_status: filters.status || 'all',
        filter_category: filters.category || 'all',
        created_by: filters.createdBy || 'all'
      });
      
      const response = await axios.get(`${API}/admin/analytics/challenges-detailed?${params}`, { 
        headers: authHeaders 
      });
      
      setAdminChallengeAnalytics(response.data);
      console.log('✅ Admin detailed challenge analytics loaded:', response.data);
      
    } catch (error) {
      console.error('❌ Failed to load admin challenge analytics:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Magazine Management Functions
  const fetchMagazines = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const userRole = localStorage.getItem('userRole');
      let endpoint = '/magazines'; // Default for members and hosts
      
      if (userRole === 'admin' || userRole === 'super_admin') {
        endpoint = '/admin/magazines'; // Admin sees all magazines including drafts
        
        // Add filter parameters
        const params = new URLSearchParams();
        params.append('magazine_type', magazineTab);
        if (magazineFilters.hostName) params.append('host_name', magazineFilters.hostName);
        if (magazineFilters.location) params.append('location', magazineFilters.location);
        if (magazineFilters.startDate) params.append('start_date', magazineFilters.startDate);
        if (magazineFilters.endDate) params.append('end_date', magazineFilters.endDate);
        
        endpoint += `?${params.toString()}`;
      }
      
      const response = await axios.get(`${API}${endpoint}`, { headers: authHeaders });
      setMagazines(response.data.magazines || []);
      if (response.data.locations) {
        setAvailableLocations(response.data.locations);
      }
      console.log('✅ Magazines loaded:', response.data.magazines?.length || 0);
    } catch (error) {
      console.error('❌ Failed to load magazines:', error);
      setMagazines([]);
    }
  };

  const fetchArticles = async (magazineId = null) => {
    try {
      const authHeaders = getAuthHeaders();
      const userRole = localStorage.getItem('userRole');
      let endpoint = '/articles';
      const params = {};
      
      if (userRole === 'admin' || userRole === 'super_admin') {
        endpoint = '/admin/articles';
        if (magazineId) {
          params.magazine_id = magazineId;
        }
      } else {
        // Members and hosts see published articles only
        if (magazineId) {
          // Get articles for specific magazine
          const response = await axios.get(`${API}/magazines/${magazineId}`, { headers: authHeaders });
          setArticles(response.data.articles || []);
          return;
        }
      }
      
      const response = await axios.get(`${API}${endpoint}`, { 
        headers: authHeaders,
        params
      });
      setArticles(response.data.articles || []);
      console.log('✅ Articles loaded:', response.data.articles?.length || 0);
      
    } catch (error) {
      console.error('❌ Failed to load articles:', error);
      setArticles([]);
    }
  };

  const fetchUserAdRequests = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const userRole = localStorage.getItem('userRole');
      
      if (userRole === 'admin' || userRole === 'super_admin') {
        // Admin sees all ad requests
        const response = await axios.get(`${API}/admin/ad-requests`, { headers: authHeaders });
        setAllAdRequests(response.data.ad_requests || []);
      } else if (userRole === 'member' || userRole === 'host') {
        // Members and hosts see their own ad requests
        const response = await axios.get(`${API}/ad-requests`, { headers: authHeaders });
        setUserAdRequests(response.data.ad_requests || []);
      }
    } catch (error) {
      console.error('❌ Failed to load ad requests:', error);
      setAllAdRequests([]);
      setUserAdRequests([]);
    }
  };

  const handleMagazineSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const authHeaders = getAuthHeaders();
      
      if (editingMagazine) {
        // Update existing magazine
        await axios.put(`${API}/admin/magazines/${editingMagazine.id}`, magazineForm, { headers: authHeaders });
        alert('Magazine updated successfully!');
      } else {
        // Create new magazine
        await axios.post(`${API}/admin/magazines`, magazineForm, { headers: authHeaders });
        alert('Magazine created successfully!');
      }
      
      await fetchMagazines();
      setShowMagazineForm(false);
      setEditingMagazine(null);
      setMagazineForm({ title: '', issue_number: '', description: '', cover_image_url: '', pdf_file_url: '', pdf_file_name: '' });
      
    } catch (error) {
      console.error('Error saving magazine:', error);
      alert(`Failed to ${editingMagazine ? 'update' : 'create'} magazine: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const authHeaders = getAuthHeaders();
      
      // Convert tags string to array if needed
      const processedForm = {
        ...articleForm,
        tags: typeof articleForm.tags === 'string' ? articleForm.tags.split(',').map(tag => tag.trim()) : articleForm.tags,
        read_time_minutes: articleForm.read_time_minutes ? parseInt(articleForm.read_time_minutes) : null
      };
      
      if (editingArticle) {
        // Update existing article
        await axios.put(`${API}/admin/articles/${editingArticle.id}`, processedForm, { headers: authHeaders });
        alert('Article updated successfully!');
      } else {
        // Create new article
        await axios.post(`${API}/admin/articles`, processedForm, { headers: authHeaders });
        alert('Article created successfully!');
      }
      
      await fetchArticles();
      setShowArticleForm(false);
      setEditingArticle(null);
      setArticleForm({
        magazine_id: '',
        title: '',
        subtitle: '',
        content: '',
        excerpt: '',
        author_name: '',
        author_bio: '',
        author_image_url: '',
        category: '',
        featured_image_url: '',
        tags: [],
        read_time_minutes: ''
      });
      
    } catch (error) {
      console.error('Error saving article:', error);
      alert(`Failed to ${editingArticle ? 'update' : 'create'} article: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleAdRequestSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const authHeaders = getAuthHeaders();
      
      const processedForm = {
        ...adRequestForm,
        target_page: adRequestForm.target_page ? parseInt(adRequestForm.target_page) : null
      };
      
      await axios.post(`${API}/magazines/${selectedMagazine.id}/ad-requests`, processedForm, { headers: authHeaders });
      alert('Ad space request submitted successfully! We will review and contact you soon.');
      
      await fetchUserAdRequests();
      setShowAdRequestForm(false);
      setAdRequestForm({
        company_name: '',
        ad_content: '',
        target_page: '',
        budget_range: '',
        contact_phone: '',
        additional_notes: ''
      });
      
    } catch (error) {
      console.error('Error submitting ad request:', error);
      alert(`Failed to submit ad request: ${error.response?.data?.detail || error.message}`);
    }
  };

  const publishMagazine = async (magazineId) => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.post(`${API}/admin/magazines/${magazineId}/publish`, {}, { headers: authHeaders });
      alert('Magazine published successfully!');
      await fetchMagazines();
    } catch (error) {
      console.error('Error publishing magazine:', error);
      alert(`Failed to publish magazine: ${error.response?.data?.detail || error.message}`);
    }
  };

  const unpublishMagazine = async (magazineId) => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.post(`${API}/admin/magazines/${magazineId}/unpublish`, {}, { headers: authHeaders });
      alert('Magazine unpublished successfully!');
      await fetchMagazines();
    } catch (error) {
      console.error('Error unpublishing magazine:', error);
      alert(`Failed to unpublish magazine: ${error.response?.data?.detail || error.message}`);
    }
  };

  const deleteMagazine = async (magazineId) => {
    if (!window.confirm('Are you sure you want to delete this magazine? This action cannot be undone and will also delete all associated articles.')) {
      return;
    }
    try {
      const authHeaders = getAuthHeaders();
      await axios.delete(`${API}/admin/magazines/${magazineId}`, { headers: authHeaders });
      alert('Magazine deleted successfully!');
      await fetchMagazines();
    } catch (error) {
      console.error('Error deleting magazine:', error);
      alert(`Failed to delete magazine: ${error.response?.data?.detail || error.message}`);
    }
  };


  const publishArticle = async (articleId) => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.post(`${API}/admin/articles/${articleId}/publish`, {}, { headers: authHeaders });
      alert('Article published successfully!');
      await fetchArticles();
    } catch (error) {
      console.error('Error publishing article:', error);
      alert(`Failed to publish article: ${error.response?.data?.detail || error.message}`);
    }
  };


  // Settings handler functions
  const fetchUserSettings = async () => {
    try {
      const authHeaders = getAuthHeaders();
      
      // Fetch profile
      const profileRes = await axios.get(`${API}/settings/profile`, { headers: authHeaders });
      setUserProfile(profileRes.data.profile);
      
      // Fetch notifications
      const notifRes = await axios.get(`${API}/settings/notifications`, { headers: authHeaders });
      setNotificationPrefs(notifRes.data.preferences);
      
      // Fetch privacy
      const privacyRes = await axios.get(`${API}/settings/privacy`, { headers: authHeaders });
      setPrivacySettings(privacyRes.data.privacy);
      
      // Fetch account
      const accountRes = await axios.get(`${API}/settings/account`, { headers: authHeaders });
      setAccountSettings(accountRes.data.account);
      
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.put(`${API}/settings/profile`, userProfile, { headers: authHeaders });
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Failed to save profile: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleChangePassword = async () => {
    try {
      if (passwordForm.new_password !== passwordForm.confirm_password) {
        alert('New passwords do not match!');
        return;
      }
      
      if (passwordForm.new_password.length < 8) {
        alert('New password must be at least 8 characters long!');
        return;
      }
      
      const authHeaders = getAuthHeaders();
      await axios.post(`${API}/settings/change-password`, passwordForm, { headers: authHeaders });
      alert('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      alert(`Failed to change password: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.put(`${API}/users/notification-preferences`, notificationPrefs, { headers: authHeaders });
      alert('Notification preferences saved successfully!');
    } catch (error) {
      console.error('Error saving notifications:', error);
      alert(`Failed to save preferences: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSavePrivacy = async () => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.put(`${API}/settings/privacy`, privacySettings, { headers: authHeaders });
      alert('Privacy settings saved successfully!');
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      alert(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSaveAccount = async () => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.put(`${API}/settings/account`, accountSettings, { headers: authHeaders });
      alert('Account settings saved successfully!');
    } catch (error) {
      console.error('Error saving account settings:', error);
      alert(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    }

  };

  // Profile picture upload handler
  const handleProfilePictureUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserProfile({...userProfile, profile_picture_url: reader.result});
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload a valid image file');
    }
  };

  const handleProfilePictureDrop = (e) => {
    e.preventDefault();
    setProfilePictureDragging(false);
    const file = e.dataTransfer.files[0];
    handleProfilePictureUpload(file);
  };

  const handleProfilePictureDragOver = (e) => {
    e.preventDefault();
    setProfilePictureDragging(true);
  };

  const handleProfilePictureDragLeave = () => {
    setProfilePictureDragging(false);


  };

  // Invoice generation and download
  const generateInvoice = (invoiceData) => {
    const { month, date, amount, invoiceNumber } = invoiceData;
    
    // Create invoice HTML with black background and white text
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Success Club Invoice</title>
        <style>
          @page {
            size: A4;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            background: #000000;
            color: #FFFFFF;
            font-family: 'Times New Roman', 'Georgia', serif;
            padding: 40px;
            line-height: 1.6;
          }
          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: #000000;
            padding: 40px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 3px solid #F59E0B;
            padding-bottom: 30px;
            margin-bottom: 40px;
          }
          .logo-section {
            display: flex;
            align-items: center;
          }
          .logo {
            width: 80px;
            height: 80px;
            margin-right: 20px;
            object-fit: contain;
          }
          .logo-placeholder {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 36px;
            font-weight: bold;
            color: #000000;
            margin-right: 20px;
            font-family: 'Times New Roman', serif;
          }
          .company-name {
            font-size: 36px;
            font-weight: bold;
            color: #F59E0B;
            letter-spacing: 2px;
            font-family: 'Times New Roman', 'Georgia', serif;
          }
          .invoice-title {
            font-size: 52px;
            font-weight: bold;
            color: #FFFFFF;
            text-align: right;
            font-family: 'Times New Roman', 'Georgia', serif;
            letter-spacing: 3px;
          }
          .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 50px;
          }
          .info-block {
            flex: 1;
          }
          .info-block h3 {
            color: #F59E0B;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 10px;
            font-family: 'Times New Roman', 'Georgia', serif;
          }
          .info-block p {
            color: #FFFFFF;
            font-size: 14px;
            margin: 5px 0;
          }
          .invoice-details {
            background: #1A1A1A;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 40px;
            border: 1px solid #333333;
          }
          .invoice-details table {
            width: 100%;
          }
          .invoice-details td {
            padding: 8px 0;
            color: #FFFFFF;
          }
          .invoice-details td:first-child {
            color: #F59E0B;
            font-weight: bold;
            width: 150px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 40px;
          }
          .items-table thead {
            background: #F59E0B;
            color: #000000;
          }
          .items-table th {
            padding: 15px;
            text-align: left;
            font-weight: bold;
            text-transform: uppercase;
            font-size: 12px;
            letter-spacing: 1px;
          }
          .items-table tbody tr {
            border-bottom: 1px solid #333333;
          }
          .items-table td {
            padding: 20px 15px;
            color: #FFFFFF;
          }
          .items-table tbody tr:hover {
            background: #1A1A1A;
          }
          .totals {
            margin-left: auto;
            width: 300px;
            background: #1A1A1A;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #333333;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            color: #FFFFFF;
          }
          .total-row.grand-total {
            border-top: 2px solid #F59E0B;
            margin-top: 10px;
            padding-top: 15px;
            font-size: 20px;
            font-weight: bold;
            color: #F59E0B;
          }
          .footer {
            margin-top: 60px;
            padding-top: 30px;
            border-top: 2px solid #333333;
            text-align: center;
            color: #999999;
            font-size: 12px;
          }
          .footer p {
            margin: 5px 0;
          }
          .footer .highlight {
            color: #F59E0B;
            font-weight: bold;
          }
          .paid-stamp {
            position: absolute;
            top: 150px;
            right: 80px;
            transform: rotate(-15deg);
            border: 4px solid #10B981;
            color: #10B981;
            padding: 10px 20px;
            font-size: 36px;
            font-weight: bold;
            border-radius: 8px;
            opacity: 0.3;
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="paid-stamp">PAID</div>
          
          <div class="header">
            <div class="logo-section">
              <img 
                src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                alt="Success Club Logo" 
                class="logo"
                onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
              />
              <div class="logo-placeholder" style="display: none;">SC</div>
              <div class="company-name">SUCCESS CLUB</div>
            </div>
            <div class="invoice-title">INVOICE</div>
          </div>

          <div class="info-section">
            <div class="info-block">
              <h3>From</h3>
              <p><strong>Success Club LLC</strong></p>
              <p>123 Innovation Drive</p>
              <p>San Francisco, CA 94105</p>
              <p>United States</p>
              <p>Email: billing@successclub.com</p>
              <p>Phone: +1 (555) 123-4567</p>
            </div>
            <div class="info-block">
              <h3>Bill To</h3>
              <p><strong>${userProfile?.full_name || 'Member'}</strong></p>
              <p>${userProfile?.email || 'member@email.com'}</p>
              <p>${userProfile?.city || 'City'}, ${userProfile?.state || 'State'}</p>
              <p>${userProfile?.country || 'Country'}</p>
            </div>
          </div>

          <div class="invoice-details">
            <table>
              <tr>
                <td>Invoice Number:</td>
                <td>#${invoiceNumber}</td>
              </tr>
              <tr>
                <td>Invoice Date:</td>
                <td>${date}</td>
              </tr>
              <tr>
                <td>Payment Status:</td>
                <td><span style="color: #10B981; font-weight: bold;">PAID</span></td>
              </tr>
              <tr>
                <td>Payment Method:</td>
                <td>Card ending in •••• 4242</td>
              </tr>
            </table>
          </div>

          <table class="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Period</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Success Club Membership</strong><br>
                  <span style="color: #999; font-size: 12px;">Premium access to all features, challenges, and events</span>
                </td>
                <td>${month}</td>
                <td style="text-align: right; font-weight: bold;">$${amount}</td>
              </tr>
            </tbody>
          </table>

          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>$${amount}</span>
            </div>
            <div class="total-row">
              <span>Tax (0%):</span>
              <span>$0.00</span>
            </div>
            <div class="total-row grand-total">
              <span>Total Paid:</span>
              <span>$${amount}</span>
            </div>
          </div>

          <div class="footer">
            <p class="highlight">Thank you for being a valued member of Success Club!</p>
            <p>This invoice was automatically generated and is valid without signature.</p>
            <p>For questions about this invoice, contact: billing@successclub.com</p>
            <p style="margin-top: 20px; color: #666;">© 2024 Success Club LLC. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Create a blob and download
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SuccessClub_Invoice_${invoiceNumber}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // Also open in new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();

  };

  // Admin Analytics Functions
  const fetchBoostAnalytics = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/admin/analytics/boosts`, { headers: authHeaders });
      setBoostAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching boost analytics:', error);
      alert('Failed to load boost analytics');
    }
  };

  const handleResetBoostLimits = async (userIds = null) => {
    try {
      const authHeaders = getAuthHeaders();
      const confirmMessage = userIds 
        ? `Reset boost limits for ${userIds.length} selected user(s)?`
        : 'Reset boost limits for ALL users?';
      
      if (!window.confirm(confirmMessage)) return;
      
      const response = await axios.post(
        `${API}/admin/boosts/reset-limits`,
        userIds,
        { headers: authHeaders }
      );
      
      alert(response.data.message);
      fetchBoostAnalytics();
    } catch (error) {
      console.error('Error resetting boost limits:', error);
      alert('Failed to reset boost limits');
    }
  };

  const fetchPlatformConfig = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/admin/platform-config`, { headers: authHeaders });
      setPlatformConfig(response.data.config);
    } catch (error) {
      console.error('Error fetching platform config:', error);
    }
  };

  const savePlatformConfig = async () => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.put(`${API}/admin/platform-config`, platformConfig, { headers: authHeaders });
      alert('Point values saved successfully!');
    } catch (error) {
      console.error('Error saving platform config:', error);
      alert('Failed to save point values');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${API}/admin/users`, { 
        headers: authHeaders,
        params: { search: userSearchQuery, limit: 100 }
      });
      setAllUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const authHeaders = getAuthHeaders();
      await axios.put(`${API}/admin/users/${userId}/role`, { new_role: newRole }, { headers: authHeaders });
      alert('User role updated successfully!');
      fetchAllUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    }
  };

  const handleUpdateUserStatus = async (userId, action) => {
    try {
      const authHeaders = getAuthHeaders();
      const confirmMessage = action === 'suspend' 
        ? 'Are you sure you want to suspend this user?' 
        : 'Are you sure you want to activate this user?';
      
      if (!window.confirm(confirmMessage)) return;
      
      await axios.put(`${API}/admin/users/${userId}/status`, { action }, { headers: authHeaders });
      alert(`User ${action}d successfully!`);
      fetchAllUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    }


  };


  const handleAdRequestApproval = async (requestId, approved, adminNotes = '') => {
    try {
      const authHeaders = getAuthHeaders();
      const endpoint = approved ? 'approve' : 'reject';
      
      await axios.post(`${API}/admin/ad-requests/${requestId}/${endpoint}`, { 
        admin_notes: adminNotes 
      }, { headers: authHeaders });
      
      alert(`Ad request ${approved ? 'approved' : 'rejected'} successfully!`);
      await fetchUserAdRequests();
    } catch (error) {
      console.error(`Error ${approved ? 'approving' : 'rejecting'} ad request:`, error);
      alert(`Failed to ${approved ? 'approve' : 'reject'} ad request: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleAdminChallengeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const endpoint = portalMode === "admin" ? "/admin/challenges" : "/challenges";
      
      // Clean up the form data - convert empty strings to null and ensure proper types
      const cleanChallengeData = {
        ...challengeForm,
        deadline: challengeForm.deadline ? new Date(challengeForm.deadline).toISOString() : null,
        points_reward: parseInt(challengeForm.points_reward),
        recurrence_interval: challengeForm.recurrence_interval ? parseInt(challengeForm.recurrence_interval) : 1,
        recurrence_end_date: challengeForm.recurrence_end_date ? new Date(challengeForm.recurrence_end_date).toISOString() : null,
        segment_min_rank: challengeForm.segment_min_rank ? parseInt(challengeForm.segment_min_rank) : null,
        segment_max_rank: challengeForm.segment_max_rank ? parseInt(challengeForm.segment_max_rank) : null
      };
      
      if (editingChallenge) {
        await axios.put(`${API}${endpoint}/${editingChallenge.id}`, cleanChallengeData, {
          headers: getAuthHeaders()
        });
      } else {
        await axios.post(`${API}${endpoint}`, cleanChallengeData, {
          headers: getAuthHeaders()
        });
      }
      fetchData();
      setShowChallengeForm(false);
      setEditingChallenge(null);
      setChallengeForm({ 
        title: "", 
        description: "", 
        points_reward: 50, 
        deadline: "",
        is_recurring: false,
        recurrence_type: "none",
        recurrence_interval: 1,
        recurrence_end_date: "",
        approval_type: "automatic",
        target_segment: "all",
        segment_min_rank: "",
        segment_max_rank: ""
      });
    } catch (error) {
      console.error("Error saving challenge:", error);
    }
  };

  const handleAdminEventSubmit = async (e) => {
    e.preventDefault();
    
    console.log("🚀 EVENT SUBMISSION STARTED");
    console.log("Portal Mode:", portalMode);
    console.log("API Base URL:", API);
    
    try {
      const endpoint = portalMode === "admin" ? "/admin/events" : "/events";
      const fullURL = `${API}${endpoint}`;
      
      console.log("📝 Event form data BEFORE processing:", eventForm);
      console.log("🎯 Target endpoint:", fullURL);
      
      // Validate required fields
      if (!eventForm.title) {
        console.error("❌ Validation failed: Missing title");
        alert("Event title is required");
        return;
      }
      if (!eventForm.description) {
        console.error("❌ Validation failed: Missing description");
        alert("Event description is required");
        return;
      }
      if (!eventForm.event_date) {
        console.error("❌ Validation failed: Missing event_date");
        alert("Event date is required");
        return;
      }
      
      // Validate event type specific fields
      if (eventForm.event_type === "in_person" && !eventForm.address) {
        console.error("❌ Validation failed: Missing address for in-person event");
        alert("Address is required for in-person events");
        return;
      }
      if (eventForm.event_type === "online" && !eventForm.online_link) {
        console.error("❌ Validation failed: Missing online_link for online event");
        alert("Online link is required for online events");
        return;
      }
      
      console.log("✅ All validation passed");
      
      // Clean up the form data - convert empty strings to null and ensure proper types
      const cleanEventData = {
        title: eventForm.title,
        description: eventForm.description,
        event_date: eventForm.event_date, // Keep as datetime string, backend will parse
        event_type: eventForm.event_type,
        points_reward: parseInt(eventForm.points_reward) || 50,
        max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null,
        is_recurring: eventForm.is_recurring || false,
        recurrence_type: eventForm.recurrence_type || "none",
        recurrence_interval: parseInt(eventForm.recurrence_interval) || 1,
        recurrence_end_date: eventForm.recurrence_end_date || null,
        approval_type: eventForm.approval_type || "automatic",
        target_segment: eventForm.target_segment || "all",
        segment_min_rank: eventForm.segment_min_rank ? parseInt(eventForm.segment_min_rank) : null,
        segment_max_rank: eventForm.segment_max_rank ? parseInt(eventForm.segment_max_rank) : null,
        // Include location fields based on event type
        address: eventForm.event_type === "in_person" ? eventForm.address : null,
        online_link: eventForm.event_type === "online" ? eventForm.online_link : null
      };
      
      console.log("🧹 Cleaned event data:", cleanEventData);
      
      // Get auth headers
      const authHeaders = getAuthHeaders();
      console.log("🔐 Auth headers:", authHeaders);
      
      if (editingEvent) {
        console.log("📝 UPDATING existing event:", editingEvent.id);
        const response = await axios.put(fullURL + `/${editingEvent.id}`, cleanEventData, {
          headers: authHeaders
        });
        console.log("✅ Event UPDATE response:", response.data);
      } else {
        console.log("➕ CREATING new event");
        console.log("🌐 Making API call to:", fullURL);
        console.log("📦 With data:", JSON.stringify(cleanEventData, null, 2));
        console.log("🔑 With headers:", JSON.stringify(authHeaders, null, 2));
        
        const response = await axios.post(fullURL, cleanEventData, {
          headers: authHeaders
        });
        console.log("🎉 Event CREATION response:", response);
        console.log("📄 Response data:", response.data);
        console.log("📊 Response status:", response.status);
      }
      
      console.log("🔄 Refreshing data...");
      
      // Refresh data and close form
      await fetchData();
      setShowEventForm(false);
      setEditingEvent(null);
      setEventForm({ 
        title: "", 
        description: "", 
        event_date: "", 
        event_type: "in_person",
        address: "",
        online_link: "",
        points_reward: 50, 
        max_attendees: "",
        is_recurring: false,
        recurrence_type: "none",
        recurrence_interval: 1,
        recurrence_end_date: "",
        approval_type: "automatic",
        target_segment: "all",
        segment_min_rank: "",
        segment_max_rank: ""
      });
      
      console.log("✅ EVENT SUBMISSION COMPLETED SUCCESSFULLY!");
      alert("Event created successfully!");
      
    } catch (error) {
      console.error("💥 ERROR saving event:", error);
      console.error("📜 Error details:", error.response?.data);
      console.error("📊 Error status:", error.response?.status);
      console.error("🔗 Error config:", error.config);
      
      // Show user-friendly error message
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else if (error.response?.status) {
        alert(`Error ${error.response.status}: ${error.response.statusText || 'Unknown error'}`);
      } else {
        alert("Failed to save event. Please check all fields and try again.");
      }
    }
  };

  const handleApproveHost = async (hostId) => {
    if (!confirm("Are you sure you want to approve this host application?")) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/approve-host/${hostId}`, {}, {
        headers: getAuthHeaders()
      });
      
      // Refresh data to update the lists
      fetchData();
      alert("Host approved successfully!");
    } catch (error) {
      console.error("Error approving host:", error);
      alert("Error approving host: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleRejectHost = async () => {
    if (!rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/reject-host/${showRejectionModal}`, {
        rejection_reason: rejectionReason.trim()
      }, {
        headers: getAuthHeaders()
      });
      
      setShowRejectionModal(null);
      setRejectionReason("");
      fetchData();
      alert("Host application rejected");
    } catch (error) {
      console.error("Error rejecting host:", error);
      alert("Error rejecting host: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleBulkApproval = async () => {
    if (selectedHosts.length === 0) {
      alert("Please select hosts to approve");
      return;
    }
    
    if (!confirm(`Are you sure you want to approve ${selectedHosts.length} host applications?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/bulk-approve-hosts`, {
        host_ids: selectedHosts
      }, {
        headers: getAuthHeaders()
      });
      
      setSelectedHosts([]);
      fetchData();
      
      const { approved_hosts, failed_hosts } = response.data;
      alert(`Bulk approval completed!\nApproved: ${approved_hosts.length}\nFailed: ${failed_hosts.length}`);
    } catch (error) {
      console.error("Error bulk approving hosts:", error);
      alert("Error bulk approving hosts: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleHostSelection = (hostId, isSelected) => {
    if (isSelected) {
      setSelectedHosts([...selectedHosts, hostId]);
    } else {
      setSelectedHosts(selectedHosts.filter(id => id !== hostId));
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAdmin) {
        // Update admin permissions
        const updateData = {
          full_name: adminForm.full_name,
          email: adminForm.email,
          can_manage_users: adminForm.can_manage_users,
          can_manage_events: adminForm.can_manage_events,
          can_manage_challenges: adminForm.can_manage_challenges,
          can_approve_hosts: adminForm.can_approve_hosts
        };
        
        await axios.put(`${API}/admin/admins/${editingAdmin.id}`, updateData, {
          headers: getAuthHeaders()
        });
      } else {
        // Create new admin
        await axios.post(`${API}/admin/create-admin`, adminForm, {
          headers: getAuthHeaders()
        });
      }
      
      fetchData();
      setShowAdminForm(false);
      setEditingAdmin(null);
      setAdminForm({
        full_name: "",
        email: "",
        password: "",
        can_manage_users: true,
        can_manage_events: true,
        can_manage_challenges: true,
        can_approve_hosts: true
      });
    } catch (error) {
      console.error("Error saving admin:", error);
      alert("Error saving admin: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!confirm("Are you sure you want to delete this admin? This action cannot be undone.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, {
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting admin:", error);
      alert("Error deleting admin: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handlePasswordReset = async (adminId) => {
    if (passwordResetForm.new_password !== passwordResetForm.confirm_password) {
      alert("Passwords do not match");
      return;
    }
    
    if (passwordResetForm.new_password.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/reset-admin-password/${adminId}`, {
        new_password: passwordResetForm.new_password
      }, {
        headers: getAuthHeaders()
      });
      
      setShowPasswordReset(null);
      setPasswordResetForm({ new_password: "", confirm_password: "" });
      alert("Password reset successfully");
    } catch (error) {
      console.error("Error resetting password:", error);
      alert("Error resetting password: " + (error.response?.data?.detail || "Unknown error"));
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    try {
      const endpoint = portalMode === "admin" ? "/admin/challenges" : "/challenges";
      await axios.delete(`${API}${endpoint}/${challengeId}`, {
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting challenge:", error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const endpoint = portalMode === "admin" ? "/admin/events" : "/events";
      await axios.delete(`${API}${endpoint}/${eventId}`, {
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'instagram': return Instagram;
      case 'twitter': return Twitter;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      default: return Share2;
    }
  };

  const getPlatformColor = (platform) => {
    switch(platform) {
      case 'instagram': return 'text-pink-600';
      case 'twitter': return 'text-blue-400';
      case 'facebook': return 'text-blue-600';
      case 'linkedin': return 'text-blue-700';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Success Club Portal...</p>
        </div>
      </div>
    );
  }

  const currentSidebarItems = 
    portalMode === "host" ? hostSidebarItems : 
    portalMode === "admin" ? adminSidebarItems : 
    memberSidebarItems;

  return (
    <div className="dashboard-container min-h-screen">
      {/* Unified Sidebar with Dynamic Width */}
      <div 
        className={`dashboard-sidebar fixed inset-y-0 left-0 bg-black text-white z-50 luxury-black-sidebar flex flex-col border-r border-gray-800 transition-all duration-300 ${
          isSidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo Section */}
        <div className={`flex-shrink-0 flex ${isSidebarCollapsed ? 'justify-center p-3' : 'flex-col items-center gap-4 p-6'}`}>
          <div className={`flex items-center justify-center bg-white rounded-full shadow-lg overflow-hidden ${isSidebarCollapsed ? 'w-10 h-10' : 'w-20 h-20'}`}>
            <img 
              src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
              alt="Success Club Logo" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                scale: '1.5'
              }}
            />
          </div>
          {/* Success Club Typography - Only show when expanded */}
          {!isSidebarCollapsed && (
            <div className="text-center">
              <h1 className="text-xl font-bold tracking-wider success-club-title">
                SUCCESS CLUB
              </h1>
              <p className="text-xs text-gray-400 mt-1 font-light tracking-wide uppercase">
                {portalMode === "host" ? "HOST PORTAL" : 
                 portalMode === "admin" ? "ADMIN PORTAL" : 
                 "MEMBER PORTAL"}
              </p>
            </div>
          )}
        </div>

        {/* Menu Toggle Button */}
        <div className={`flex-shrink-0 mb-4 ${isSidebarCollapsed ? 'px-2 flex justify-center' : 'px-4'}`}>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={`flex items-center gap-2 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-900 transition-all ${
              isSidebarCollapsed ? 'w-10 h-10 justify-center' : 'w-full justify-center'
            }`}
            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {isSidebarCollapsed ? (
              <Menu className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>

        {/* Portal Actions - Only show when expanded */}
        {!isSidebarCollapsed && (
          <div className="px-4 mb-4 flex-shrink-0">
            <div className="bg-gray-900 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400 uppercase tracking-wide">Portal Actions</span>
                {portalMode === "host" ? (
                  <Crown className="w-4 h-4 text-amber-500" />
                ) : portalMode === "admin" ? (
                  <Shield className="w-4 h-4 text-red-500" />
                ) : (
                  <Users className="w-4 h-4 text-blue-500" />
                )}
              </div>
              <button 
                className="w-full flex items-center gap-3 p-2 rounded-lg transition-all duration-200 bg-gray-800 hover:bg-gray-700"
                onClick={() => navigate('/')}
              >
                <Home className="w-5 h-5 text-gray-300" />
                <span className="text-sm font-medium">Back to Home</span>
              </button>
            </div>
          </div>
        )}

        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto">
          <nav className={`space-y-2 pb-4 ${isSidebarCollapsed ? 'px-2' : 'px-4'}`}>
            {currentSidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center rounded-lg transition-all duration-200 ${
                    isSidebarCollapsed 
                      ? 'w-10 h-10 justify-center' 
                      : 'w-full gap-3 px-4 py-3 text-left'
                  } ${
                    activeTab === item.id
                      ? isSidebarCollapsed 
                        ? "bg-amber-600 text-white"
                        : "active"
                      : "text-gray-300 hover:bg-gray-900 hover:text-white"
                  }`}
                  title={isSidebarCollapsed ? item.label : undefined}
                >
                  <Icon className="w-5 h-5" />
                  {!isSidebarCollapsed && <span className="font-medium">{item.label}</span>}
                </button>
              );
            })}
          </nav>
        </div>

        {/* User Profile in Sidebar - Only show for Member and Host portals */}
        {portalMode !== "admin" && (
          <div className={`border-t border-gray-800 flex-shrink-0 ${isSidebarCollapsed ? 'p-3 flex justify-center' : 'p-4'}`}>
            {isSidebarCollapsed ? (
              <Avatar className="w-10 h-10 sidebar-user-avatar cursor-pointer" title={user?.full_name}>
                <AvatarImage src={userProfile?.profile_picture_url || user?.profile_picture_url} alt={user?.full_name} />
                <AvatarFallback className="bg-amber-600 text-white text-xs">{user?.full_name?.split(' ').map(n => n[0]).join('')}</AvatarFallback>
              </Avatar>
            ) : (
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10 sidebar-user-avatar">
                  <AvatarImage src={userProfile?.profile_picture_url || user?.profile_picture_url} alt={user?.full_name} />
                  <AvatarFallback className="bg-amber-600 text-white">{user?.full_name?.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-amber-300">Rank #{user?.rank}</p>
                    {portalMode === "host" && <Crown className="w-3 h-3 text-amber-400" />}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={`dashboard-content p-8 transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="space-y-8">
          {/* Dashboard Content */}
          {activeTab === "dashboard" && (
            <div className="space-y-8">
              <div className="dashboard-header flex items-center justify-between">
                <div>
                  <h1 style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    marginBottom: '0.75rem',
                    color: 'var(--ink)',
                    letterSpacing: '-0.02em',
                    lineHeight: '1.2'
                  }}>
                    Welcome back, {user?.full_name?.split(' ')[0]}!
                    {portalMode === "host" && <Crown className="inline w-10 h-10 ml-3" style={{color: 'var(--gold)', verticalAlign: 'middle'}} />}
                    {portalMode === "admin" && <Shield className="inline w-10 h-10 text-red-500 ml-3" style={{verticalAlign: 'middle'}} />}
                  </h1>
                  <p style={{
                    color: 'var(--muted)',
                    fontSize: '1.1rem',
                    marginTop: '0.5rem'
                  }}>
                    {portalMode === "host" 
                      ? "Manage your Success Club community and track your revenue." 
                      : portalMode === "admin" 
                      ? "Manage Success Club platform and approve host applications."
                      : "Here's what's happening in your Success Club journey."
                    }
                  </p>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {portalMode === "admin" ? (
                  // Admin-specific stats
                  <>
                    <Card className="dashboard-stat-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          Total Users
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600">{dashboardStats?.total_users || 0}</div>
                        <p className="text-gray-600 text-sm mt-1">All registered users</p>
                      </CardContent>
                    </Card>

                    <Card className="dashboard-stat-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Crown className="w-5 h-5 text-amber-600" />
                          Pending Hosts
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-amber-600">{dashboardStats?.pending_hosts || 0}</div>
                        <p className="text-gray-600 text-sm mt-1">Awaiting approval</p>
                      </CardContent>
                    </Card>

                    <Card className="dashboard-stat-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-green-600" />
                          Active Members
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-600">{dashboardStats?.total_members || 0}</div>
                        <p className="text-gray-600 text-sm mt-1">Paying members</p>
                      </CardContent>
                    </Card>

                    <Card className="dashboard-stat-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Crown className="w-5 h-5 text-purple-600" />
                          Active Hosts
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-600">{dashboardStats?.total_hosts || 0}</div>
                        <p className="text-gray-600 text-sm mt-1">Approved hosts</p>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  // Member/Host stats
                  <>
                    <Card className="stats-card-gradient text-white">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Total Points</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold">{dashboardStats?.total_points?.toLocaleString() || 0}</div>
                        <p className="text-amber-100 text-sm mt-1">Rank #{dashboardStats?.current_rank}</p>
                      </CardContent>
                    </Card>

                    {portalMode === "host" && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-green-600" />
                            Monthly Revenue
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-green-600">${dashboardStats?.monthly_revenue?.toLocaleString()}</div>
                          <p className="text-gray-600 text-sm mt-1">{dashboardStats?.revenue_share}% share</p>
                        </CardContent>
                      </Card>
                    )}

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          My Members
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600">{dashboardStats?.member_count}</div>
                        <p className="text-gray-600 text-sm mt-1">Active referrals</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <MessageCircle className="w-5 h-5 text-purple-600" />
                          Messages
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-600">{dashboardStats?.unread_messages}</div>
                        <p className="text-gray-600 text-sm mt-1">Unread messages</p>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>

              {/* Admin Analytics Dashboard */}
              {portalMode === "admin" && adminAnalytics && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-6 text-gray-800">Platform Analytics & Reports</h3>
                  
                  {/* First Row - Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {/* Total Users */}
                    <Card 
                      className="border-blue-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('users')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          Total Users
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600">
                          {(adminAnalytics.users?.total_members + adminAnalytics.users?.total_hosts) || 0}
                        </div>
                        <div className="flex justify-between mt-2 text-sm text-gray-600">
                          <span>{adminAnalytics.users?.total_members || 0} Members</span>
                          <span>{adminAnalytics.users?.total_hosts || 0} Hosts</span>
                        </div>
                        <p className="text-xs text-blue-500 mt-2">Click for details →</p>
                      </CardContent>
                    </Card>

                    {/* Active Subscriptions */}
                    <Card 
                      className="border-green-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('subscriptions')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Active Subscriptions
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-600">
                          {adminAnalytics.users?.active_subscriptions || 0}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          {adminAnalytics.users?.cancelled_subscriptions || 0} Cancelled
                        </p>
                        <p className="text-xs text-green-500 mt-2">Click for details →</p>
                      </CardContent>
                    </Card>

                    {/* Total Points */}
                    <Card 
                      className="border-amber-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('points')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-amber-600" />
                          Points Distributed
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-amber-600">
                          {adminAnalytics.points?.total_distributed?.toLocaleString() || 0}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          Avg: {Math.round(adminAnalytics.points?.average_per_user || 0)} per user
                        </p>
                        <p className="text-xs text-amber-500 mt-2">Click for details →</p>
                      </CardContent>
                    </Card>

                    {/* Activation Rate */}
                    <Card 
                      className="border-purple-200 shadow-lg"
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Activity className="w-4 h-4 text-purple-600" />
                          Activation Rate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-600">
                          {adminAnalytics.users?.activation_rate || 0}%
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          {adminAnalytics.users?.active_users || 0} active in {activationPeriod}
                        </p>
                        
                        {/* Time Period Selector */}
                        <div className="mt-3 flex flex-wrap gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleActivationPeriodChange('day'); }}
                            className={`px-2 py-1 text-xs rounded transition-colors flex-shrink-0 ${
                              activationPeriod === 'day'
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          >
                            Day
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleActivationPeriodChange('week'); }}
                            className={`px-2 py-1 text-xs rounded transition-colors flex-shrink-0 ${
                              activationPeriod === 'week'
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          >
                            Week
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleActivationPeriodChange('month'); }}
                            className={`px-2 py-1 text-xs rounded transition-colors flex-shrink-0 ${
                              activationPeriod === 'month'
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          >
                            Month
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleActivationPeriodChange('year'); }}
                            className={`px-2 py-1 text-xs rounded transition-colors flex-shrink-0 ${
                              activationPeriod === 'year'
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          >
                            Year
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Second Row - Detailed Metrics (4 cards) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    
                    {/* Challenge Participation (Detailed) */}
                    <Card 
                      className="border-purple-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('challenges')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Target className="w-5 h-5 text-purple-600" />
                          Challenge Participation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Challenges:</span>
                            <span className="font-bold text-purple-600">{adminAnalytics.challenges?.total || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Active:</span>
                            <span className="font-semibold">{adminAnalytics.challenges?.active || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Participants:</span>
                            <span className="font-bold">{adminAnalytics.challenges?.total_participants || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Completion Rate:</span>
                            <span className="font-bold text-purple-600">{adminAnalytics.challenges?.completion_rate || 0}%</span>
                          </div>
                          <p className="text-xs text-purple-500 mt-3 text-center">Click for detailed analytics →</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Event Participation (Detailed) */}
                    <Card 
                      className="border-teal-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('events')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-teal-600" />
                          Event Participation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Events:</span>
                            <span className="font-bold text-teal-600">{adminAnalytics.events?.total || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Upcoming:</span>
                            <span className="font-semibold">{adminAnalytics.events?.upcoming || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total RSVPs:</span>
                            <span className="font-bold">{adminAnalytics.events?.total_rsvps || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Attendance Rate:</span>
                            <span className="font-bold text-teal-600">{adminAnalytics.events?.attendance_rate || 0}%</span>
                          </div>
                          <p className="text-xs text-teal-500 mt-3 text-center">Click for detailed analytics →</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Social Boosting */}
                    <Card 
                      className="border-pink-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('social_boosting')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Zap className="w-5 h-5 text-pink-600" />
                          Social Boosting
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Boosts:</span>
                            <span className="font-bold text-pink-600">{adminAnalytics.engagement?.total_boosts || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Recent Boosts (7d):</span>
                            <span className="font-semibold">{adminAnalytics.engagement?.recent_boosts || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Avg Boosts/Member:</span>
                            <span className="font-semibold">{adminAnalytics.engagement?.avg_boosts_per_member || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Points from Boosts:</span>
                            <span className="font-bold text-pink-600">{(adminAnalytics.engagement?.total_boosts || 0) * 5}</span>
                          </div>
                          <p className="text-xs text-pink-500 mt-3 text-center">Click for detailed analytics →</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Accountability Partners */}
                    <Card 
                      className="border-orange-200 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => handleMetricClick('accountability_partners')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-orange-600" />
                          Accountability Partners
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Active Partnerships:</span>
                            <span className="font-bold text-orange-600">{adminAnalytics.engagement?.active_partnerships || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Total Check-ins:</span>
                            <span className="font-bold">{adminAnalytics.engagement?.total_checkins || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Recent Check-ins (7d):</span>
                            <span className="font-semibold">{adminAnalytics.engagement?.recent_checkins || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Avg Check-ins/Member:</span>
                            <span className="font-semibold">{adminAnalytics.engagement?.avg_checkins_per_member || 0}</span>
                          </div>
                          <p className="text-xs text-orange-500 mt-3 text-center">Click for detailed analytics →</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Third Row - Host Performance & Top Performers */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Host Performance */}
                    <Card className="border-orange-200 shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Crown className="w-5 h-5 text-orange-600" />
                          Top Host Performance
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {adminAnalytics.host_performance?.slice(0, 5).map((host, index) => (
                            <div key={host.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-full">
                                  <span className="text-sm font-bold text-orange-600">#{index + 1}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-800">{host.name}</p>
                                  <p className="text-xs text-gray-600">
                                    {host.challenges_created} challenges • {host.events_created} events
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-orange-600">{host.engagement_score}</p>
                                <p className="text-xs text-gray-600">Score</p>
                              </div>
                            </div>
                          ))}
                          {(!adminAnalytics.host_performance || adminAnalytics.host_performance.length === 0) && (
                            <p className="text-gray-500 text-center py-4">No host data available</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Top Performers */}
                    <Card className="border-indigo-200 shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-indigo-600" />
                          Top Points Leaders
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Top Members</p>
                          {adminAnalytics.top_performers?.members?.slice(0, 3).map((member, index) => (
                            <div key={member.id} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                              <span className="text-sm font-medium">#{index + 1} {member.name}</span>
                              <span className="font-bold text-blue-600">{member.points?.toLocaleString()}</span>
                            </div>
                          ))}
                          
                          <p className="text-xs font-semibold text-gray-600 uppercase mb-2 mt-4">Top Hosts</p>
                          {adminAnalytics.top_performers?.hosts?.slice(0, 3).map((host, index) => (
                            <div key={host.id} className="flex justify-between items-center p-2 bg-amber-50 rounded">
                              <span className="text-sm font-medium">#{index + 1} {host.name}</span>
                              <span className="font-bold text-amber-600">{host.points?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* New Member & Host Sign-ups Timeline Chart */}
                  {adminAnalytics.signups_timeline && adminAnalytics.signups_timeline.length > 0 && (
                    <div className="mb-6">
                      <Card className="shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            New Member & Host Sign-ups (Last 30 Days)
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={adminAnalytics.signups_timeline}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="date" 
                                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              />
                              <YAxis />
                              <Tooltip 
                                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                                formatter={(value, name) => {
                                  if (name === 'members') return [`${value} new members`, 'Members'];
                                  if (name === 'hosts') return [`${value} new hosts`, 'Hosts'];
                                  return [value, name];
                                }}
                              />
                              <Legend 
                                formatter={(value) => {
                                  if (value === 'members') return 'New Members';
                                  if (value === 'hosts') return 'New Hosts';
                                  return value;
                                }}
                              />
                              <Area 
                                type="monotone" 
                                dataKey="members" 
                                stackId="1"
                                stroke="#3b82f6" 
                                fill="#60a5fa" 
                                fillOpacity={0.7}
                                name="members"
                              />
                              <Area 
                                type="monotone" 
                                dataKey="hosts" 
                                stackId="1"
                                stroke="#f59e0b" 
                                fill="#fbbf24" 
                                fillOpacity={0.7}
                                name="hosts"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Cancelled Subscriptions Section */}
                  {adminAnalytics.cancelled_subscriptions_detail && adminAnalytics.cancelled_subscriptions_detail.length > 0 && (
                    <div className="mb-6">
                      <Card className="shadow-lg border-red-200">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <XCircle className="w-5 h-5 text-red-600" />
                            Cancelled Subscriptions ({adminAnalytics.cancelled_subscriptions_detail.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Name</th>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Email</th>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Role</th>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">Points Earned</th>
                                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">End Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {adminAnalytics.cancelled_subscriptions_detail.map((user) => (
                                  <tr key={user.id} className="border-t hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm">{user.name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                    <td className="px-4 py-3">
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        user.role === 'member' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {user.role}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold">{user.points_earned?.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {user.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString() : 'N/A'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )}

              {/* Host Revenue Analytics Dashboard */}
              {portalMode === "host" && (
                <div className="mt-8 space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900">Revenue Analytics</h3>
                  
                  {/* Revenue Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="border-green-200 shadow-lg">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          Monthly Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-600">
                          ${dashboardStats?.monthly_revenue?.toLocaleString() || 0}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          {dashboardStats?.revenue_share || 30}% revenue share
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-blue-200 shadow-lg">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          Active Members
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600">
                          {dashboardStats?.member_count || 0}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          Generating revenue
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-purple-200 shadow-lg">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-purple-600" />
                          Annual Projection
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-600">
                          ${((dashboardStats?.monthly_revenue || 0) * 12).toLocaleString()}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          Based on current rate
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Engagement Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-amber-600" />
                          Content Created
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-amber-50 rounded">
                            <span className="text-sm font-medium">Challenges Created</span>
                            <span className="text-2xl font-bold text-amber-600">
                              {dashboardStats?.total_challenges || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                            <span className="text-sm font-medium">Events Created</span>
                            <span className="text-2xl font-bold text-blue-600">
                              {dashboardStats?.total_events || 0}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Activity className="w-5 h-5 text-green-600" />
                          Member Engagement
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Avg. Revenue per Member</span>
                            <span className="font-bold text-green-600">
                              ${dashboardStats?.member_count > 0 ? ((dashboardStats?.monthly_revenue || 0) / dashboardStats.member_count).toFixed(2) : '0.00'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Retention Rate</span>
                            <span className="font-bold text-blue-600">95%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Growth This Month</span>
                            <span className="font-bold text-purple-600">+8%</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Growth Projections */}
                  <Card className="shadow-lg border-indigo-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-indigo-600" />
                        Revenue Projections
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                          <p className="text-sm text-green-700 mb-1">Next Quarter</p>
                          <p className="text-2xl font-bold text-green-600">
                            ${((dashboardStats?.monthly_revenue || 0) * 3).toLocaleString()}
                          </p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                          <p className="text-sm text-blue-700 mb-1">With 10% Growth</p>
                          <p className="text-2xl font-bold text-blue-600">
                            ${((dashboardStats?.monthly_revenue || 0) * 1.1 * 12).toLocaleString()}
                          </p>
                          <p className="text-xs text-blue-600">Annual</p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                          <p className="text-sm text-purple-700 mb-1">With 20% Growth</p>
                          <p className="text-2xl font-bold text-purple-600">
                            ${((dashboardStats?.monthly_revenue || 0) * 1.2 * 12).toLocaleString()}
                          </p>
                          <p className="text-xs text-purple-600">Annual</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Enhanced Member Portal Dashboard */}
              {portalMode === "member" && (
                <div className="space-y-6">
                  {/* Host Information Banner */}
                  {memberDashboardData.host_info && (
                    <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-amber-900">
                              {memberDashboardData.host_name}
                            </h3>
                            <p className="text-amber-700">
                              {memberDashboardData.host_info.city}, {memberDashboardData.host_info.state}
                            </p>
                            {memberDashboardData.host_info.host_code && (
                              <p className="text-sm text-amber-600">
                                Host Code: {memberDashboardData.host_info.host_code}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-amber-700">Your Host Community</p>
                            <p className="text-2xl font-bold text-amber-900">
                              {memberDashboardData.community_stats?.total_members || 0}
                            </p>
                            <p className="text-xs text-amber-600">Members</p>
                          </div>
                        </div>
                        {memberDashboardData.host_info.mission_vision && (
                          <div className="mt-4 pt-4 border-t border-amber-200">
                            <p className="text-sm text-amber-800 italic">
                              "{memberDashboardData.host_info.mission_vision}"
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* General Member Welcome */}
                  {memberDashboardData.member_info?.is_general_member && (
                    <Card className="bg-gradient-to-r from-blue-50 to-purple-100 border-purple-200">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-purple-900">
                              Success Club General
                            </h3>
                            <p className="text-purple-700">
                              Welcome to the entire Success Club community!
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-purple-900">
                              {memberDashboardData.community_stats?.total_members || 0}
                            </p>
                            <p className="text-xs text-purple-600">Total Members</p>
                            <p className="text-xs text-purple-600">
                              {memberDashboardData.community_stats?.total_hosts || 0} Hosts
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Rankings Dashboard - Removed duplicate "Your Points" card as it shows same data as top Total Points card */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-amber-600" />
                          Global Rank
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-amber-600 mb-2">
                            #{memberDashboardData.member_stats?.rank || dashboardStats?.current_rank || 999}
                          </div>
                          <p className="text-gray-600 text-sm">Overall Ranking</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-green-600" />
                          Community Rank
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600 mb-2">
                            #{memberDashboardData.member_stats?.host_rank || memberDashboardData.member_stats?.rank || dashboardStats?.current_rank || 999}
                          </div>
                          <p className="text-gray-600 text-sm">
                            {memberDashboardData.host_info ? 'Host Community' : 'General Community'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Analytics Dashboard - Points Breakdown & Timeline */}
                  {userStats && (
                    <div className="space-y-6">
                      {/* Personal Stats Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 member-analytics-card">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-blue-700 font-medium">This Week</p>
                                <p className="text-2xl font-bold text-blue-900">{userStats.points_this_week}</p>
                              </div>
                              <TrendingUp className="w-8 h-8 text-blue-600" />
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 member-analytics-card">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-green-700 font-medium">This Month</p>
                                <p className="text-2xl font-bold text-green-900">{userStats.points_this_month}</p>
                              </div>
                              <Calendar className="w-8 h-8 text-green-600" />
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 member-analytics-card">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-purple-700 font-medium">Streak</p>
                                <p className="text-2xl font-bold text-purple-900">{userStats.streak} days</p>
                              </div>
                              <Zap className="w-8 h-8 text-purple-600" />
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 member-analytics-card">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-amber-700 font-medium">Next Goal</p>
                                <p className="text-2xl font-bold text-amber-900">{userStats.next_milestone}</p>
                              </div>
                              <Target className="w-8 h-8 text-amber-600" />
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Charts Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Points Breakdown Pie Chart */}
                        {pointsBreakdown && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <PieChartIcon className="w-5 h-5 text-amber-600" />
                                Points Sources
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                  <Pie
                                    data={[
                                      { name: 'Challenges', value: pointsBreakdown.challenge_completions, fill: '#f59e0b' },
                                      { name: 'Check-ins', value: pointsBreakdown.accountability_checkins, fill: '#10b981' },
                                      { name: 'Boosts', value: pointsBreakdown.social_boosts, fill: '#3b82f6' },
                                      { name: 'Referrals', value: pointsBreakdown.referrals, fill: '#8b5cf6' },
                                      { name: 'Events', value: pointsBreakdown.event_attendance, fill: '#ec4899' }
                                    ].filter(item => item.value > 0)}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    dataKey="value"
                                  >
                                    {[
                                      { name: 'Challenges', value: pointsBreakdown.challenge_completions, fill: '#f59e0b' },
                                      { name: 'Check-ins', value: pointsBreakdown.accountability_checkins, fill: '#10b981' },
                                      { name: 'Boosts', value: pointsBreakdown.social_boosts, fill: '#3b82f6' },
                                      { name: 'Referrals', value: pointsBreakdown.referrals, fill: '#8b5cf6' },
                                      { name: 'Events', value: pointsBreakdown.event_attendance, fill: '#ec4899' }
                                    ].filter(item => item.value > 0).map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                              {/* Legend */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 text-xs sm:text-sm">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-amber-500 flex-shrink-0"></div>
                                  <span className="truncate">Challenges: {pointsBreakdown.challenge_completions}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                                  <span className="truncate">Check-ins: {pointsBreakdown.accountability_checkins}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                                  <span className="truncate">Boosts: {pointsBreakdown.social_boosts}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-purple-500 flex-shrink-0"></div>
                                  <span className="truncate">Referrals: {pointsBreakdown.referrals}</span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Points Timeline Chart */}
                        {pointsTimeline && pointsTimeline.timeline && (
                          <Card>
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                  <TrendingUp className="w-5 h-5 text-blue-600" />
                                  Points Over Time
                                </CardTitle>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setTimelineFilter('week')}
                                    className={`px-2 py-1 text-xs rounded ${timelineFilter === 'week' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                                  >
                                    Week
                                  </button>
                                  <button
                                    onClick={() => setTimelineFilter('month')}
                                    className={`px-2 py-1 text-xs rounded ${timelineFilter === 'month' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                                  >
                                    Month
                                  </button>
                                  <button
                                    onClick={() => setTimelineFilter('year')}
                                    className={`px-2 py-1 text-xs rounded ${timelineFilter === 'year' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                                  >
                                    Year
                                  </button>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={pointsTimeline.timeline}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis 
                                    dataKey="date" 
                                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  />
                                  <YAxis />
                                  <Tooltip 
                                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                                    formatter={(value) => [`${value} points`, 'Points Earned']}
                                  />
                                  <Area 
                                    type="monotone" 
                                    dataKey="points" 
                                    stroke="#f59e0b" 
                                    fill="#fbbf24" 
                                    fillOpacity={0.6}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}
                      </div>

                      {/* Activity Heatmap */}
                      {activityHeatmap && activityHeatmap.heatmap && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Activity className="w-5 h-5 text-green-600" />
                              Activity Heatmap (Last 90 Days)
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-10 gap-1">
                              {activityHeatmap.heatmap.map((day, index) => {
                                const intensity = day.count === 0 ? 'bg-gray-100' : 
                                                day.count <= 2 ? 'bg-green-200' :
                                                day.count <= 4 ? 'bg-green-400' : 'bg-green-600';
                                return (
                                  <div
                                    key={index}
                                    className={`w-full aspect-square rounded ${intensity} cursor-pointer hover:ring-2 hover:ring-green-800 transition-all activity-heatmap-cell`}
                                    title={`${day.date}: ${day.count} activities`}
                                  />
                                );
                              })}
                            </div>
                            <div className="flex items-center justify-end gap-2 mt-4 text-sm text-gray-600">
                              <span>Less</span>
                              <div className="w-4 h-4 rounded bg-gray-100"></div>
                              <div className="w-4 h-4 rounded bg-green-200"></div>
                              <div className="w-4 h-4 rounded bg-green-400"></div>
                              <div className="w-4 h-4 rounded bg-green-600"></div>
                              <span>More</span>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Active Challenges & Upcoming Events */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-amber-600" />
                          Active Challenges
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {memberChallenges && memberChallenges.length > 0 ? memberChallenges.slice(0, 3).map((challenge) => (
                          <div key={challenge.id} className="space-y-2">
                            <div className="flex justify-between items-start">
                              <h4 className="font-medium text-sm">{challenge.title}</h4>
                              <div className="flex items-center gap-1">
                                <Badge 
                                  variant={challenge.is_host_specific ? "default" : "secondary"}
                                  className="text-xs"
                                >
                                  {challenge.points_reward} pts
                                </Badge>
                                {challenge.is_host_specific && (
                                  <Badge variant="outline" className="text-xs">Host</Badge>
                                )}
                              </div>
                            </div>
                            <Progress value={challenge.progress || 0} className="h-2" />
                            <p className="text-xs text-gray-600">
                              {challenge.progress || 0}% complete
                            </p>
                          </div>
                        )) : (
                          <p className="text-gray-500 text-sm text-center py-4">
                            No active challenges available
                          </p>
                        )}
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => setActiveTab("challenges")}
                        >
                          View All Challenges
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-purple-600" />
                          Upcoming Events
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {memberEvents && memberEvents.length > 0 ? memberEvents.slice(0, 3).map((event) => (
                          <div key={event.id} className="border-l-4 border-purple-200 pl-3 py-2">
                            <div className="flex justify-between items-start">
                              <h4 className="font-medium text-sm">{event.title}</h4>
                              {event.is_host_specific && (
                                <Badge variant="outline" className="text-xs">Host</Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-600">
                              {new Date(event.date).toLocaleDateString()} • {event.location || 'Online'}
                            </p>
                            <p className="text-xs text-purple-600">
                              {event.points_reward} points
                            </p>
                          </div>
                        )) : (
                          <p className="text-gray-500 text-sm text-center py-4">
                            No upcoming events
                          </p>
                        )}
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => setActiveTab("events")}
                        >
                          View All Events
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* Host-specific Revenue Quick View */}
              {portalMode === "host" && hostRevenue && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-green-600" />
                      Revenue Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600">${hostRevenue.total_revenue.toLocaleString()}</div>
                        <p className="text-gray-600">Total Revenue</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">{hostRevenue.member_subscriptions}</div>
                        <p className="text-gray-600">Active Subscriptions</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-amber-600">{hostRevenue.revenue_share_percentage}%</div>
                        <p className="text-gray-600">Revenue Share</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => setActiveTab("revenue")}
                    >
                      View Detailed Analytics
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Leaderboard */}
          {activeTab === "leaderboard" && (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Leaderboard</h1>
                <p className="text-gray-600 mt-2">
                  {portalMode === "member" 
                    ? "See how you rank among Success Club members with daily and weekly breakdowns."
                    : "Track member performance and rankings."
                  }
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-amber-600" />
                    Top Performers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {leaderboard && leaderboard.length > 0 ? leaderboard.map((member, index) => (
                      <div 
                        key={member.user_id} 
                        className={`flex items-center gap-4 p-4 rounded-lg leaderboard-rank-badge ${
                          member.user_id === user?.id ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-amber-600 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {member.rank}
                        </div>
                        
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={member.profile_picture_url || member.avatar_url} alt={member.full_name} />
                          <AvatarFallback>{member.full_name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1">
                          <h3 className="font-semibold">{member.full_name}</h3>
                          <p className="text-sm text-gray-600">
                            {member.member_count} members • {member.challenges_completed} challenges completed
                          </p>
                        </div>
                        
                        {portalMode === "member" && (
                          <div className="flex gap-4 text-center">
                            <div className="flex items-center gap-1">
                              <BarChart2 className="w-4 h-4 text-blue-500" />
                              <div>
                                <div className="text-sm font-bold text-blue-600">{member.daily_points}</div>
                                <div className="text-xs text-gray-500">Daily</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Activity className="w-4 h-4 text-green-500" />
                              <div>
                                <div className="text-sm font-bold text-green-600">{member.weekly_points}</div>
                                <div className="text-xs text-gray-500">Weekly</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="text-right">
                          <div className="text-xl font-bold text-amber-600">{member.total_points.toLocaleString()}</div>
                          <p className="text-sm text-gray-600">{member.revenue_share}% revenue share</p>
                        </div>
                      </div>
                    )) : (
                      <p className="text-gray-500 text-sm text-center py-8">
                        No leaderboard data available
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Enhanced Admin Challenges Section */}
          {activeTab === "challenges" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Platform Challenge Management</h1>
                  <p className="text-gray-600 mt-2">Create and manage challenges for the entire Success Club platform</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowAdminChallengeAnalytics(!showAdminChallengeAnalytics);
                      if (!showAdminChallengeAnalytics && !adminChallengeAnalytics) {
                        fetchAdminChallengeAnalytics();
                      }
                    }}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    {showAdminChallengeAnalytics ? 'Hide Analytics' : 'View Analytics'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowChallengeForm(true);
                      setEditingChallenge(null);
                      setChallengeForm({ 
                        title: "", 
                        description: "", 
                        points_reward: 50, 
                        deadline: "",
                        is_recurring: false,
                        recurrence_type: "none",
                        recurrence_interval: 1,
                        recurrence_end_date: "",
                        approval_type: "automatic",
                        target_segment: "all",
                        segment_min_rank: "",
                        segment_max_rank: ""
                      });
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Global Challenge
                  </Button>
                </div>
              </div>

              {/* Enhanced Admin Challenge Analytics Dashboard */}
              {showAdminChallengeAnalytics && (
                <div className="space-y-6">
                  {/* Analytics Controls Header */}
                  <Card className="border-amber-200 shadow-lg bg-gradient-to-br from-amber-50 to-yellow-50">
                    <CardHeader className="border-b border-amber-100">
                      <CardTitle className="flex items-center justify-between text-amber-800">
                        <div className="flex items-center gap-3">
                          <BarChart3 className="w-6 h-6" />
                          Detailed Challenge Analytics
                          {analyticsLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent"></div>
                          )}
                        </div>
                      </CardTitle>
                      
                      {/* Filters and Sorting Controls */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4">
                        {/* Sort By */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
                          <select
                            value={analyticsFilters.sortBy}
                            onChange={(e) => {
                              const newFilters = { ...analyticsFilters, sortBy: e.target.value };
                              setAnalyticsFilters(newFilters);
                              fetchAdminChallengeAnalytics(newFilters);
                            }}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          >
                            <option value="created_at">Created Date</option>
                            <option value="title">Title</option>
                            <option value="total_participants">Participants</option>
                            <option value="completion_rate">Completion Rate</option>
                            <option value="points_reward">Points</option>
                          </select>
                        </div>

                        {/* Sort Order */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
                          <select
                            value={analyticsFilters.sortOrder}
                            onChange={(e) => {
                              const newFilters = { ...analyticsFilters, sortOrder: e.target.value };
                              setAnalyticsFilters(newFilters);
                              fetchAdminChallengeAnalytics(newFilters);
                            }}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                          </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                          <select
                            value={analyticsFilters.status}
                            onChange={(e) => {
                              const newFilters = { ...analyticsFilters, status: e.target.value };
                              setAnalyticsFilters(newFilters);
                              fetchAdminChallengeAnalytics(newFilters);
                            }}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="draft">Draft</option>
                          </select>
                        </div>

                        {/* Category Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                          <select
                            value={analyticsFilters.category}
                            onChange={(e) => {
                              const newFilters = { ...analyticsFilters, category: e.target.value };
                              setAnalyticsFilters(newFilters);
                              fetchAdminChallengeAnalytics(newFilters);
                            }}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          >
                            <option value="all">All Categories</option>
                            {adminChallengeAnalytics?.filter_options?.categories?.map((category) => (
                              <option key={category} value={category}>{category || 'Uncategorized'}</option>
                            ))}
                          </select>
                        </div>

                        {/* Creator Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Created By</label>
                          <select
                            value={analyticsFilters.createdBy}
                            onChange={(e) => {
                              const newFilters = { ...analyticsFilters, createdBy: e.target.value };
                              setAnalyticsFilters(newFilters);
                              fetchAdminChallengeAnalytics(newFilters);
                            }}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          >
                            <option value="all">All Creators</option>
                            <option value="admin">Admin</option>
                            <option value="host">Host</option>
                          </select>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-6">
                      {analyticsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-600 border-t-transparent"></div>
                          <span className="ml-3 text-amber-700">Loading analytics...</span>
                        </div>
                      ) : adminChallengeAnalytics ? (
                        <div className="space-y-6">
                          {/* Summary Metrics */}
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100 text-center">
                              <div className="text-2xl font-bold text-amber-600">{adminChallengeAnalytics.summary?.total_challenges || 0}</div>
                              <div className="text-xs text-gray-600 mt-1">Challenges</div>
                            </div>
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-100 text-center">
                              <div className="text-2xl font-bold text-blue-600">{adminChallengeAnalytics.summary?.total_participants || 0}</div>
                              <div className="text-xs text-gray-600 mt-1">Participants</div>
                            </div>
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100 text-center">
                              <div className="text-2xl font-bold text-green-600">{adminChallengeAnalytics.summary?.total_completions || 0}</div>
                              <div className="text-xs text-gray-600 mt-1">Completions</div>
                            </div>
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-100 text-center">
                              <div className="text-2xl font-bold text-purple-600">{adminChallengeAnalytics.summary?.avg_completion_rate || 0}%</div>
                              <div className="text-xs text-gray-600 mt-1">Avg Rate</div>
                            </div>
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-emerald-100 text-center">
                              <div className="text-2xl font-bold text-emerald-600">{adminChallengeAnalytics.summary?.total_points_awarded || 0}</div>
                              <div className="text-xs text-gray-600 mt-1">Points</div>
                            </div>
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-100 text-center">
                              <div className="text-2xl font-bold text-orange-600">{adminChallengeAnalytics.summary?.pending_submissions || 0}</div>
                              <div className="text-xs text-gray-600 mt-1">Pending</div>
                            </div>
                          </div>

                          {/* Detailed Challenge Table */}
                          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-gray-600" />
                                Challenge Details ({adminChallengeAnalytics.challenges?.length || 0} challenges)
                              </h3>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                    <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Creator</th>
                                    <th className="text-center py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Participants</th>
                                    <th className="text-center py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Completion %</th>
                                    <th className="text-center py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                                    <th className="text-center py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="text-center py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {adminChallengeAnalytics.challenges?.length > 0 ? (
                                    adminChallengeAnalytics.challenges.map((challenge, index) => (
                                      <tr key={challenge.id || index} className="hover:bg-gray-50">
                                        <td className="py-4 px-6">
                                          <div>
                                            <div className="font-medium text-gray-900 text-sm">{challenge.title}</div>
                                            <div className="text-xs text-gray-500">{challenge.category || 'Uncategorized'}</div>
                                          </div>
                                        </td>
                                        <td className="py-4 px-6">
                                          <div className="text-sm text-gray-900">{challenge.creator_name || 'System'}</div>
                                          <div className={`text-xs px-2 py-1 rounded-full inline-block ${
                                            challenge.creator_type === 'host' 
                                              ? 'bg-blue-100 text-blue-800' 
                                              : 'bg-purple-100 text-purple-800'
                                          }`}>
                                            {challenge.creator_type || 'admin'}
                                          </div>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                          <div className="text-sm font-semibold text-gray-900">{challenge.total_participants || 0}</div>
                                          <div className="text-xs text-gray-500">{challenge.joined_participants || 0} joined</div>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                          <div className={`text-sm font-semibold ${
                                            (challenge.completion_rate || 0) >= 75 ? 'text-green-600' :
                                            (challenge.completion_rate || 0) >= 50 ? 'text-yellow-600' :
                                            (challenge.completion_rate || 0) >= 25 ? 'text-orange-600' : 'text-red-600'
                                          }`}>
                                            {challenge.completion_rate || 0}%
                                          </div>
                                          <div className="text-xs text-gray-500">{challenge.completed_participants || 0} completed</div>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                          <div className="text-sm font-semibold text-emerald-600">{challenge.points_reward || 0}</div>
                                          <div className="text-xs text-gray-500">{challenge.points_awarded || 0} awarded</div>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                                            challenge.status === 'active' ? 'bg-green-100 text-green-800' :
                                            challenge.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                            challenge.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {challenge.status || 'active'}
                                          </span>
                                        </td>
                                        <td className="py-4 px-6 text-center text-xs text-gray-500">
                                          {challenge.created_at ? new Date(challenge.created_at).toLocaleDateString() : '—'}
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="7" className="py-8 text-center text-gray-500">
                                        No challenges found matching the current filters
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Refresh Button */}
                          <div className="flex justify-center pt-4">
                            <Button
                              onClick={() => fetchAdminChallengeAnalytics(analyticsFilters)}
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              disabled={analyticsLoading}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Refresh Analytics
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-500">Failed to load analytics data</p>
                          <Button
                            onClick={() => fetchAdminChallengeAnalytics(analyticsFilters)}
                            variant="outline"
                            className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            Retry
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Challenge Form Modal */}
              {showChallengeForm && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {editingChallenge ? "Edit Challenge" : "Create New Global Challenge"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAdminChallengeSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Challenge Title</label>
                        <input
                          type="text"
                          value={challengeForm.title}
                          onChange={(e) => setChallengeForm({...challengeForm, title: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          placeholder="Enter challenge title"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <textarea
                          value={challengeForm.description}
                          onChange={(e) => setChallengeForm({...challengeForm, description: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg h-24"
                          placeholder="Enter challenge description"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Points Reward</label>
                          <select
                            value={challengeForm.points_reward}
                            onChange={(e) => setChallengeForm({...challengeForm, points_reward: parseInt(e.target.value)})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                          >
                            <option value={50}>50 Points</option>
                            <option value={100}>100 Points</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Deadline</label>
                          <input
                            type="datetime-local"
                            value={challengeForm.deadline}
                            onChange={(e) => setChallengeForm({...challengeForm, deadline: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>

                      {/* Recurring Options */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Recurring Settings</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="is_recurring"
                              checked={challengeForm.is_recurring}
                              onChange={(e) => setChallengeForm({...challengeForm, is_recurring: e.target.checked})}
                              className="w-4 h-4 text-blue-600"
                            />
                            <label htmlFor="is_recurring" className="text-sm font-medium">Make this challenge recurring</label>
                          </div>
                          
                          {challengeForm.is_recurring && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium mb-1">Recurrence Type</label>
                                <select
                                  value={challengeForm.recurrence_type}
                                  onChange={(e) => setChallengeForm({...challengeForm, recurrence_type: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                >
                                  <option value="daily">Daily</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                  <option value="yearly">Yearly</option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>
                              
                              {challengeForm.recurrence_type === 'custom' && (
                                <div>
                                  <label className="block text-xs font-medium mb-1">Every X Days</label>
                                  <input
                                    type="number"
                                    value={challengeForm.recurrence_interval}
                                    onChange={(e) => setChallengeForm({...challengeForm, recurrence_interval: parseInt(e.target.value)})}
                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                    min="1"
                                    placeholder="7"
                                  />
                                </div>
                              )}
                              
                              <div className="col-span-2">
                                <label className="block text-xs font-medium mb-1">Recurrence End Date (Optional)</label>
                                <input
                                  type="datetime-local"
                                  value={challengeForm.recurrence_end_date}
                                  onChange={(e) => setChallengeForm({...challengeForm, recurrence_end_date: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Approval Settings */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Approval Settings</h4>
                        <div>
                          <label className="block text-sm font-medium mb-2">Approval Type</label>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                value="automatic"
                                checked={challengeForm.approval_type === 'automatic'}
                                onChange={(e) => setChallengeForm({...challengeForm, approval_type: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">Automatic (immediately active)</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                value="manual"
                                checked={challengeForm.approval_type === 'manual'}
                                onChange={(e) => setChallengeForm({...challengeForm, approval_type: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">Manual (auto-approve after 3 days if no action)</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Target Segment */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Target Audience</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium mb-2">Leaderboard Segment</label>
                            <select
                              value={challengeForm.target_segment}
                              onChange={(e) => setChallengeForm({...challengeForm, target_segment: e.target.value})}
                              className="w-full p-3 border border-gray-300 rounded-lg"
                            >
                              <option value="all">All Users</option>
                              <option value="top_10">Top 10</option>
                              <option value="top_25">Top 25</option>
                              <option value="top_50">Top 50</option>
                              <option value="top_100">Top 100</option>
                              <option value="middle_tier">Middle Tier (25%-75%)</option>
                              <option value="bottom_50">Bottom 50</option>
                              <option value="custom_range">Custom Range</option>
                            </select>
                          </div>
                          
                          {challengeForm.target_segment === 'custom_range' && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium mb-1">Min Rank</label>
                                <input
                                  type="number"
                                  value={challengeForm.segment_min_rank}
                                  onChange={(e) => setChallengeForm({...challengeForm, segment_min_rank: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                  min="1"
                                  placeholder="1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium mb-1">Max Rank</label>
                                <input
                                  type="number"
                                  value={challengeForm.segment_max_rank}
                                  onChange={(e) => setChallengeForm({...challengeForm, segment_max_rank: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                  min="1"
                                  placeholder="50"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">
                          {editingChallenge ? "Update Challenge" : "Create Challenge"}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setShowChallengeForm(false)}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Challenges List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-600" />
                    Platform Challenges ({getFilteredAdminChallenges().length})
                  </CardTitle>
                  
                  {/* Admin Challenge Filter Tabs */}
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className={adminChallengeFilter === 'current' ? "bg-amber-50 border-amber-200 text-amber-700" : ""}
                      onClick={() => setAdminChallengeFilter('current')}
                    >
                      Current & Upcoming
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={adminChallengeFilter === 'past' ? "bg-amber-50 border-amber-200 text-amber-700" : ""}
                      onClick={() => setAdminChallengeFilter('past')}
                    >
                      Past Challenges
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {getFilteredAdminChallenges().map((challenge) => (
                      <div key={challenge.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{challenge.title}</h3>
                              <Badge 
                                variant={challenge.status === 'active' ? 'default' : 'secondary'}
                                className={challenge.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                              >
                                {challenge.status}
                              </Badge>
                              <Badge variant="outline">{challenge.points_reward} pts</Badge>
                            </div>
                            <p className="text-gray-600 mb-3">{challenge.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>📅 Deadline: {challenge.deadline ? new Date(challenge.deadline).toLocaleDateString() : 'No deadline'}</span>
                              <span>👥 Participants: {challenge.participants?.length || 0}</span>
                              <span>✅ Completed: {challenge.completion_count || 0}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingChallenge(challenge);
                                setChallengeForm({
                                  title: challenge.title,
                                  description: challenge.description,
                                  points_reward: challenge.points_reward,
                                  deadline: challenge.deadline ? new Date(challenge.deadline).toISOString().slice(0, 16) : ""
                                });
                                setShowChallengeForm(true);
                              }}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteChallenge(challenge.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {getFilteredAdminChallenges().length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>
                          {adminChallengeFilter === 'current' 
                            ? 'No current or upcoming challenges. Create your first global challenge!'
                            : 'No past challenges found.'}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Enhanced Admin Events Section */}
          {activeTab === "events" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Platform Event Management</h1>
                  <p className="text-gray-600 mt-2">Create and manage events for the entire Success Club platform</p>
                </div>
                <Button
                  onClick={() => {
                    setShowEventForm(true);
                    setEditingEvent(null);
                    setEventForm({ 
                      title: "", 
                      description: "", 
                      event_date: "", 
                      event_type: "in_person",
                      address: "",
                      online_link: "",
                      points_reward: 50, 
                      max_attendees: "",
                      is_recurring: false,
                      recurrence_type: "none",
                      recurrence_interval: 1,
                      recurrence_end_date: "",
                      approval_type: "automatic",
                      target_segment: "all",
                      segment_min_rank: "",
                      segment_max_rank: ""
                    });
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Global Event
                </Button>
              </div>

              {/* Event Form Modal */}
              {showEventForm && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {editingEvent ? "Edit Event" : "Create New Global Event"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAdminEventSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Event Title</label>
                        <input
                          type="text"
                          value={eventForm.title}
                          onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          placeholder="Enter event title"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <textarea
                          value={eventForm.description}
                          onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg h-24"
                          placeholder="Enter event description"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Event Date</label>
                          <input
                            type="datetime-local"
                            value={eventForm.event_date}
                            onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Event Type</label>
                          <div className="flex gap-4 pt-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                value="in_person"
                                checked={eventForm.event_type === 'in_person'}
                                onChange={(e) => setEventForm({...eventForm, event_type: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">In-Person</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                value="online"
                                checked={eventForm.event_type === 'online'}
                                onChange={(e) => setEventForm({...eventForm, event_type: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">Online</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Conditional Location Fields */}
                      {eventForm.event_type === 'in_person' ? (
                        <div>
                          <label className="block text-sm font-medium mb-2">Address</label>
                          <input
                            type="text"
                            value={eventForm.address}
                            onChange={(e) => setEventForm({...eventForm, address: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="Enter event address"
                            required
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium mb-2">Online Meeting Link</label>
                          <input
                            type="url"
                            value={eventForm.online_link}
                            onChange={(e) => setEventForm({...eventForm, online_link: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                            required
                          />
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Points Reward</label>
                          <select
                            value={eventForm.points_reward}
                            onChange={(e) => setEventForm({...eventForm, points_reward: parseInt(e.target.value)})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                          >
                            <option value={50}>50 Points</option>
                            <option value={100}>100 Points</option>
                            <option value={150}>150 Points</option>
                            <option value={200}>200 Points</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Max Attendees</label>
                          <input
                            type="number"
                            value={eventForm.max_attendees}
                            onChange={(e) => setEventForm({...eventForm, max_attendees: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="Optional limit"
                            min="1"
                          />
                        </div>
                      </div>

                      {/* Recurring Options */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Recurring Settings</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="event_is_recurring"
                              checked={eventForm.is_recurring}
                              onChange={(e) => setEventForm({...eventForm, is_recurring: e.target.checked})}
                              className="w-4 h-4 text-blue-600"
                            />
                            <label htmlFor="event_is_recurring" className="text-sm font-medium">Make this event recurring</label>
                          </div>
                          
                          {eventForm.is_recurring && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium mb-1">Recurrence Type</label>
                                <select
                                  value={eventForm.recurrence_type}
                                  onChange={(e) => setEventForm({...eventForm, recurrence_type: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                >
                                  <option value="daily">Daily</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                  <option value="yearly">Yearly</option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>
                              
                              {eventForm.recurrence_type === 'custom' && (
                                <div>
                                  <label className="block text-xs font-medium mb-1">Every X Days</label>
                                  <input
                                    type="number"
                                    value={eventForm.recurrence_interval}
                                    onChange={(e) => setEventForm({...eventForm, recurrence_interval: parseInt(e.target.value)})}
                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                    min="1"
                                    placeholder="7"
                                  />
                                </div>
                              )}
                              
                              <div className="col-span-2">
                                <label className="block text-xs font-medium mb-1">Recurrence End Date (Optional)</label>
                                <input
                                  type="datetime-local"
                                  value={eventForm.recurrence_end_date}
                                  onChange={(e) => setEventForm({...eventForm, recurrence_end_date: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Approval Settings */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Approval Settings</h4>
                        <div>
                          <label className="block text-sm font-medium mb-2">Approval Type</label>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                value="automatic"
                                checked={eventForm.approval_type === 'automatic'}
                                onChange={(e) => setEventForm({...eventForm, approval_type: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">Automatic (immediately active)</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                value="manual"
                                checked={eventForm.approval_type === 'manual'}
                                onChange={(e) => setEventForm({...eventForm, approval_type: e.target.value})}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className="text-sm">Manual (auto-approve after 3 days if no action)</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Target Segment */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Target Audience</h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium mb-2">Leaderboard Segment</label>
                            <select
                              value={eventForm.target_segment}
                              onChange={(e) => setEventForm({...eventForm, target_segment: e.target.value})}
                              className="w-full p-3 border border-gray-300 rounded-lg"
                            >
                              <option value="all">All Users</option>
                              <option value="top_10">Top 10</option>
                              <option value="top_25">Top 25</option>
                              <option value="top_50">Top 50</option>
                              <option value="top_100">Top 100</option>
                              <option value="middle_tier">Middle Tier (25%-75%)</option>
                              <option value="bottom_50">Bottom 50</option>
                              <option value="custom_range">Custom Range</option>
                            </select>
                          </div>
                          
                          {eventForm.target_segment === 'custom_range' && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium mb-1">Min Rank</label>
                                <input
                                  type="number"
                                  value={eventForm.segment_min_rank}
                                  onChange={(e) => setEventForm({...eventForm, segment_min_rank: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                  min="1"
                                  placeholder="1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium mb-1">Max Rank</label>
                                <input
                                  type="number"
                                  value={eventForm.segment_max_rank}
                                  onChange={(e) => setEventForm({...eventForm, segment_max_rank: e.target.value})}
                                  className="w-full p-2 border border-gray-300 rounded text-sm"
                                  min="1"
                                  placeholder="50"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Feature on Public Page */}
                      <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="feature_public"
                            checked={eventForm.feature_on_public_page || false}
                            onChange={(e) => setEventForm({...eventForm, feature_on_public_page: e.target.checked})}
                            className="w-5 h-5 text-blue-600"
                          />
                          <label htmlFor="feature_public" className="font-semibold cursor-pointer flex-1">
                            Feature on Public Events Page
                          </label>
                        </div>
                        <p className="text-sm text-gray-600 mt-2 ml-8">
                          When enabled, this event will be visible to non-members on the public Events page, helping attract new members.
                        </p>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">
                          {editingEvent ? "Update Event" : "Create Event"}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setShowEventForm(false)}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Events List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Platform Events ({getFilteredAdminEvents().length})
                  </CardTitle>
                  
                  {/* Admin Event Filter Tabs */}
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className={adminEventFilter === 'current' ? "bg-blue-50 border-blue-200 text-blue-700" : ""}
                      onClick={() => setAdminEventFilter('current')}
                    >
                      Current & Upcoming
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={adminEventFilter === 'past' ? "bg-blue-50 border-blue-200 text-blue-700" : ""}
                      onClick={() => setAdminEventFilter('past')}
                    >
                      Past Events
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {getFilteredAdminEvents().map((event) => (
                      <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{event.title}</h3>
                              <Badge 
                                variant={event.status === 'upcoming' ? 'default' : 'secondary'}
                                className={event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' : ''}
                              >
                                {event.status}
                              </Badge>
                              <Badge variant="outline">{event.points_reward} pts</Badge>
                            </div>
                            <p className="text-gray-600 mb-3">{event.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>📅 Date: {new Date(event.event_date).toLocaleDateString()}</span>
                              {event.event_type === 'online' ? (
                                <span>🌐 Online: {event.online_link ? 'Link provided' : event.location}</span>
                              ) : (
                                <span>📍 Address: {event.address || event.location}</span>
                              )}
                              <span>👥 RSVPs: {event.attendees?.length || 0}{event.max_attendees ? `/${event.max_attendees}` : ''}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingEvent(event);
                                setEventForm({
                                  title: event.title,
                                  description: event.description,
                                  event_date: new Date(event.event_date).toISOString().slice(0, 16),
                                  event_type: event.event_type || "in_person",
                                  address: event.address || "",
                                  online_link: event.online_link || "",
                                  points_reward: event.points_reward,
                                  max_attendees: event.max_attendees || "",
                                  is_recurring: event.is_recurring || false,
                                  recurrence_type: event.recurrence_type || "none",
                                  recurrence_interval: event.recurrence_interval || 1,
                                  recurrence_end_date: event.recurrence_end_date ? new Date(event.recurrence_end_date).toISOString().slice(0, 16) : "",
                                  approval_type: event.approval_type || "automatic",
                                  target_segment: event.target_segment || "all",
                                  segment_min_rank: event.segment_min_rank || "",
                                  segment_max_rank: event.segment_max_rank || ""
                                });
                                setShowEventForm(true);
                              }}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteEvent(event.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {getFilteredAdminEvents().length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>
                          {adminEventFilter === 'current' 
                            ? 'No current or upcoming events. Create your first global event!'
                            : 'No past events found.'}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Enhanced Admin Leaderboard with Points Analytics */}
          {activeTab === "leaderboard" && portalMode === "admin" && (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Platform Analytics & Leaderboard</h1>
                <p className="text-gray-600 mt-2">Monitor points distribution and user engagement across Success Club</p>
              </div>

              {/* Points Analytics Cards */}
              {pointsAnalytics && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Award className="w-5 h-5 text-yellow-600" />
                        Total Points Awarded
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-yellow-600">{pointsAnalytics.total_points_awarded.toLocaleString()}</div>
                      <p className="text-gray-600 text-sm mt-1">Across {pointsAnalytics.total_active_users} active users</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Avg Points Per User
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-600">{pointsAnalytics.avg_points_per_user}</div>
                      <p className="text-gray-600 text-sm mt-1">Platform average</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-600" />
                        Points Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm space-y-1">
                        <div>Challenges: 50-100 pts</div>
                        <div>Events: 50-200 pts</div>
                        <div>Boosts: 10 pts</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Leaderboard */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-amber-600" />
                    Platform Leaderboard - Top 50 Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {adminLeaderboard.map((user) => (
                      <div key={user.user_id} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          user.rank === 1 ? 'bg-yellow-500 text-white' :
                          user.rank === 2 ? 'bg-gray-400 text-white' :
                          user.rank === 3 ? 'bg-amber-600 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          {user.rank}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{user.full_name}</h3>
                            <Badge variant={user.role === 'host' ? 'default' : 'secondary'}>
                              {user.role}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">
                            Joined: {new Date(user.join_date).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-xl font-bold text-amber-600">{user.total_points.toLocaleString()}</div>
                          <div className="text-sm text-gray-500">
                            Daily: {user.daily_points} | Weekly: {user.weekly_points}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {adminLeaderboard.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No users with points yet. Users will appear here as they earn points!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Admin Magazine Management */}
          {activeTab === "magazines" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Magazine Management</h1>
                  <p className="text-gray-600 mt-2">Manage digital magazines and articles for the Success Club platform</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowArticleForm(true);
                      setEditingArticle(null);
                      setArticleForm({
                        magazine_id: '',
                        title: '',
                        subtitle: '',
                        content: '',
                        excerpt: '',
                        author_name: '',
                        author_bio: '',
                        author_image_url: '',
                        category: '',
                        featured_image_url: '',
                        tags: [],
                        read_time_minutes: ''
                      });
                    }}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Add Article
                  </Button>
                  <Button
                    onClick={() => {
                      setShowMagazineForm(true);
                      setEditingMagazine(null);
                      setMagazineForm({ 
                        title: '', 
                        issue_number: '', 
                        description: '', 
                        cover_image_url: '', 
                        pdf_file_url: '', 
                        pdf_file_name: '',
                        host_location: '',
                        event_id: '',
                        event_name: '',
                        magazine_type: 'admin'
                      });
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Magazine
                  </Button>
                </div>
              </div>

              {/* Magazine Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex space-x-8">
                  <button
                    onClick={() => { setMagazineTab('all'); fetchMagazines(); }}
                    className={`pb-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      magazineTab === 'all'
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    All Magazines
                  </button>
                  <button
                    onClick={() => { setMagazineTab('admin'); fetchMagazines(); }}
                    className={`pb-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      magazineTab === 'admin'
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Admin Posted
                  </button>
                  <button
                    onClick={() => { setMagazineTab('host'); fetchMagazines(); }}
                    className={`pb-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      magazineTab === 'host'
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Host Posted
                  </button>
                  <button
                    onClick={() => { setMagazineTab('event'); fetchMagazines(); }}
                    className={`pb-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                      magazineTab === 'event'
                        ? 'border-amber-500 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Event Specific
                  </button>
                </div>
              </div>

              {/* Search and Filter Controls */}
              {magazineTab === 'host' && (
                <Card className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Host Name</label>
                        <input
                          type="text"
                          placeholder="Search by host name..."
                          value={magazineFilters.hostName}
                          onChange={(e) => setMagazineFilters({...magazineFilters, hostName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                        <select
                          value={magazineFilters.location}
                          onChange={(e) => setMagazineFilters({...magazineFilters, location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">All Locations</option>
                          {availableLocations.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <input
                          type="date"
                          value={magazineFilters.startDate}
                          onChange={(e) => setMagazineFilters({...magazineFilters, startDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="date"
                          value={magazineFilters.endDate}
                          onChange={(e) => setMagazineFilters({...magazineFilters, endDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button onClick={fetchMagazines} className="bg-amber-600 hover:bg-amber-700">
                        <Search className="w-4 h-4 mr-2" />
                        Apply Filters
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setMagazineFilters({ hostName: '', location: '', startDate: '', endDate: '' });
                          fetchMagazines();
                        }}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Magazine List */}
              <div className="grid gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Magazines ({magazines.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {magazines.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {magazines.map((magazine) => (
                          <div key={magazine.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow bg-white">
                            {magazine.cover_image_url && (
                              <img 
                                src={magazine.cover_image_url} 
                                alt={magazine.title}
                                className="w-full h-48 object-cover rounded-md mb-3"
                              />
                            )}
                            <div className="space-y-3">
                              <div>
                                <div className="flex items-start justify-between mb-2">
                                  <h3 className="font-semibold text-lg">{magazine.title}</h3>
                                  <Badge variant={magazine.status === 'published' ? 'default' : 'secondary'}>
                                    {magazine.status}
                                  </Badge>
                                </div>
                                <p className="text-xs text-gray-500">Issue #{magazine.issue_number}</p>
                              </div>
                              
                              {magazine.description && (
                                <p className="text-sm text-gray-600 line-clamp-2">{magazine.description}</p>
                              )}
                              
                              <div className="space-y-1 text-xs text-gray-500">
                                {magazine.created_by_name && (
                                  <div className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    <span>By {magazine.created_by_name}</span>
                                  </div>
                                )}
                                {magazine.host_location && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    <span>{magazine.host_location}</span>
                                  </div>
                                )}
                                {magazine.event_name && (
                                  <div className="flex items-center gap-1">
                                    <CalendarIcon className="w-3 h-3" />
                                    <span>{magazine.event_name}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>Created: {new Date(magazine.created_at).toLocaleDateString()}</span>
                                </div>
                                {magazine.published_date && (
                                  <div className="flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    <span>Published: {new Date(magazine.published_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-2 pt-2 border-t">
                                {magazine.pdf_file_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setSelectedMagazine(magazine)}
                                    className="flex-1"
                                  >
                                    <Eye className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingMagazine(magazine);
                                    setMagazineForm({
                                      title: magazine.title,
                                      issue_number: magazine.issue_number,
                                      description: magazine.description || '',
                                      cover_image_url: magazine.cover_image_url || '',
                                      pdf_file_url: magazine.pdf_file_url || '',
                                      pdf_file_name: magazine.pdf_file_name || '',
                                      host_location: magazine.host_location || '',
                                      event_id: magazine.event_id || '',
                                      event_name: magazine.event_name || '',
                                      magazine_type: magazine.magazine_type || 'admin'
                                    });
                                    setShowMagazineForm(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                {magazine.status === 'published' ? (
                                  <Button
                                    size="sm"
                                    onClick={() => unpublishMagazine(magazine.id)}
                                    className="bg-gray-600 hover:bg-gray-700"
                                  >
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Unpublish
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => publishMagazine(magazine.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Publish
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => deleteMagazine(magazine.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No magazines found</p>
                    )}
                  </CardContent>
                </Card>

                {/* Articles List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Articles ({articles.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {articles.length > 0 ? (
                      <div className="space-y-4">
                        {articles.map((article) => (
                          <div key={article.id} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold text-lg">{article.title}</h3>
                                  <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                                    {article.status}
                                  </Badge>
                                  <Badge variant="outline">
                                    {article.category}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                                  <span>By {article.author_name}</span>
                                  {article.read_time_minutes && (
                                    <span>{article.read_time_minutes} min read</span>
                                  )}
                                  <span>Created: {new Date(article.created_at).toLocaleDateString()}</span>
                                </div>
                                {article.excerpt && (
                                  <p className="text-gray-600 text-sm">{article.excerpt}</p>
                                )}
                              </div>
                              <div className="flex gap-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingArticle(article);
                                    setArticleForm({
                                      magazine_id: article.magazine_id || '',
                                      title: article.title,
                                      subtitle: article.subtitle || '',
                                      content: article.content,
                                      excerpt: article.excerpt || '',
                                      author_name: article.author_name,
                                      author_bio: article.author_bio || '',
                                      author_image_url: article.author_image_url || '',
                                      category: article.category,
                                      featured_image_url: article.featured_image_url || '',
                                      tags: Array.isArray(article.tags) ? article.tags : [],
                                      read_time_minutes: article.read_time_minutes || ''
                                    });
                                    setShowArticleForm(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {article.status !== 'published' && (
                                  <Button
                                    size="sm"
                                    onClick={() => publishArticle(article.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    Publish
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No articles created yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Ad Requests Management */}
                <Card>
                  <CardHeader>
                    <CardTitle>Advertisement Requests ({allAdRequests.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {allAdRequests.length > 0 ? (
                      <div className="space-y-4">
                        {allAdRequests.map((request) => (
                          <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold">{request.company_name || request.requester_name}</h3>
                                  <Badge variant={
                                    request.status === 'approved' ? 'default' :
                                    request.status === 'rejected' ? 'destructive' : 'secondary'
                                  }>
                                    {request.status}
                                  </Badge>
                                  <span className="text-sm text-gray-500">Full Page Ad</span>
                                </div>
                                <div className="space-y-1 text-sm text-gray-600">
                                  <p><strong>Magazine:</strong> {request.magazine_title} - Issue #{request.magazine_issue}</p>
                                  <p><strong>Contact:</strong> {request.requester_email}</p>
                                  <p><strong>Content:</strong> {request.ad_content}</p>
                                  {request.budget_range && <p><strong>Budget:</strong> {request.budget_range}</p>}
                                  {request.target_page && <p><strong>Preferred Page:</strong> {request.target_page}</p>}
                                </div>
                              </div>
                              {request.status === 'pending' && (
                                <div className="flex gap-2 ml-4">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const notes = prompt('Add admin notes (optional):');
                                      if (notes !== null) {
                                        handleAdRequestApproval(request.id, false, notes);
                                      }
                                    }}
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                  >
                                    Reject
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const notes = prompt('Add admin notes (optional):');
                                      if (notes !== null) {
                                        handleAdRequestApproval(request.id, true, notes);
                                      }
                                    }}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    Approve
                                  </Button>
                                </div>
                              )}
                            </div>
                            {request.admin_notes && (
                              <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                                <strong>Admin Notes:</strong> {request.admin_notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No advertisement requests yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Magazine Form Modal */}
              {showMagazineForm && (
                <Card className="fixed inset-4 z-50 bg-white shadow-2xl overflow-auto">
                  <CardHeader className="border-b">
                    <CardTitle>{editingMagazine ? 'Edit Magazine' : 'Create New Magazine'}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <form onSubmit={handleMagazineSubmit} className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Magazine Title *</label>
                          <input
                            type="text"
                            value={magazineForm.title}
                            onChange={(e) => setMagazineForm({...magazineForm, title: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Issue Number *</label>
                          <input
                            type="text"
                            value={magazineForm.issue_number}
                            onChange={(e) => setMagazineForm({...magazineForm, issue_number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={magazineForm.description}
                          onChange={(e) => setMagazineForm({...magazineForm, description: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image URL</label>
                        <input
                          type="url"
                          value={magazineForm.cover_image_url}
                          onChange={(e) => setMagazineForm({...magazineForm, cover_image_url: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">PDF File URL</label>
                        <input
                          type="url"
                          value={magazineForm.pdf_file_url}
                          onChange={(e) => setMagazineForm({...magazineForm, pdf_file_url: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="https://example.com/magazine.pdf"
                        />
                        <p className="text-sm text-gray-500 mt-1">Enter the direct URL to the PDF file</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">PDF File Name (Optional)</label>
                        <input
                          type="text"
                          value={magazineForm.pdf_file_name}
                          onChange={(e) => setMagazineForm({...magazineForm, pdf_file_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="e.g., success-club-issue-001.pdf"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowMagazineForm(false);
                            setEditingMagazine(null);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                          {editingMagazine ? 'Update Magazine' : 'Create Magazine'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Article Form Modal */}
              {showArticleForm && (
                <Card className="fixed inset-4 z-50 bg-white shadow-2xl overflow-auto">
                  <CardHeader className="border-b">
                    <CardTitle>{editingArticle ? 'Edit Article' : 'Create New Article'}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <form onSubmit={handleArticleSubmit} className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Article Title *</label>
                          <input
                            type="text"
                            value={articleForm.title}
                            onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                          <input
                            type="text"
                            value={articleForm.category}
                            onChange={(e) => setArticleForm({...articleForm, category: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="e.g., Business Strategy, Leadership"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <input
                          type="text"
                          value={articleForm.subtitle}
                          onChange={(e) => setArticleForm({...articleForm, subtitle: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Article Content *</label>
                        <textarea
                          value={articleForm.content}
                          onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          rows={10}
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
                        <textarea
                          value={articleForm.excerpt}
                          onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          rows={3}
                          placeholder="Brief summary for article preview"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Author Name *</label>
                          <input
                            type="text"
                            value={articleForm.author_name}
                            onChange={(e) => setArticleForm({...articleForm, author_name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Read Time (minutes)</label>
                          <input
                            type="number"
                            value={articleForm.read_time_minutes}
                            onChange={(e) => setArticleForm({...articleForm, read_time_minutes: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            min="1"
                            placeholder="Auto-calculated if empty"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image URL</label>
                        <input
                          type="url"
                          value={articleForm.featured_image_url}
                          onChange={(e) => setArticleForm({...articleForm, featured_image_url: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowArticleForm(false);
                            setEditingArticle(null);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                          {editingArticle ? 'Update Article' : 'Create Article'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Host Approval System */}
          {activeTab === "pending-hosts" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Host Approval Center</h1>
                  <p className="text-gray-600 mt-2">Review and approve host applications for the Success Club platform</p>
                </div>
                <div className="flex gap-3">
                  {selectedHosts.length > 0 && (
                    <Button
                      onClick={handleBulkApproval}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Selected ({selectedHosts.length})
                    </Button>
                  )}
                  <Badge variant="outline" className="px-3 py-1 text-sm">
                    {pendingHosts.length} Pending Applications
                  </Badge>
                </div>
              </div>

              {/* Rejection Modal */}
              {showRejectionModal && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-600">
                      <XCircle className="w-5 h-5" />
                      Reject Host Application
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-gray-700">Please provide a reason for rejecting this host application:</p>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg h-24 resize-none"
                        placeholder="Enter rejection reason (required)..."
                        required
                      />
                      <div className="flex gap-3">
                        <Button
                          onClick={handleRejectHost}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Reject Application
                        </Button>
                        <Button
                          onClick={() => {
                            setShowRejectionModal(null);
                            setRejectionReason("");
                          }}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending Hosts List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-600" />
                    Pending Host Applications ({pendingHosts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingHosts.length > 0 ? (
                    <div className="space-y-4">
                      {pendingHosts.map((host) => (
                        <div key={host.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex items-start gap-4">
                            <input
                              type="checkbox"
                              checked={selectedHosts.includes(host.id)}
                              onChange={(e) => handleHostSelection(host.id, e.target.checked)}
                              className="mt-2 w-4 h-4 text-green-600"
                            />
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-lg">{host.coworking_space_name || host.full_name || 'No Business Name'}</h3>
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                                  Pending Approval
                                </Badge>
                                <Badge variant="outline">
                                  {Math.floor((new Date() - new Date(host.created_at)) / (1000 * 60 * 60 * 24))} days ago
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                  <p className="text-sm text-gray-500">Contact Person</p>
                                  <p className="font-medium">{host.contact_person || 'Not provided'}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Email</p>
                                  <p className="font-medium">{host.email}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Phone Number</p>
                                  <p className="font-medium">{host.phone_number || 'Not provided'}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Host Code</p>
                                  <p className="font-medium font-mono bg-gray-100 px-2 py-1 rounded">
                                    {host.host_code || 'Not generated'}
                                  </p>
                                </div>
                              </div>

                              {host.instagram_url && (
                                <div className="mb-3">
                                  <p className="text-sm text-gray-500">Instagram Profile</p>
                                  <a 
                                    href={host.instagram_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {host.instagram_url}
                                  </a>
                                </div>
                              )}

                              <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                                <span>Applied: {new Date(host.created_at).toLocaleDateString()}</span>
                                <span>•</span>
                                <span>Email Verified: {host.email_verified ? '✅ Yes' : '❌ No'}</span>
                                {host.payment_status && (
                                  <>
                                    <span>•</span>
                                    <span>Payment: {host.payment_status}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveHost(host.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowRejectionModal(host.id)}
                                className="text-red-600 hover:text-red-700 border-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Crown className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pending Applications</h3>
                      <p className="text-gray-600">
                        All host applications have been reviewed. New applications will appear here.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Host Application Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-600" />
                      Pending Applications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">{pendingHosts.length}</div>
                    <p className="text-gray-600 text-sm mt-1">Awaiting review</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Total Approved
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">{dashboardStats?.total_hosts || 0}</div>
                    <p className="text-gray-600 text-sm mt-1">Active hosts</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                      Approval Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">
                      {pendingHosts.length > 0 ? 
                        Math.round((dashboardStats?.total_hosts || 0) / ((dashboardStats?.total_hosts || 0) + pendingHosts.length) * 100) : 
                        100
                      }%
                    </div>
                    <p className="text-gray-600 text-sm mt-1">Historical rate</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Admin User Management */}
          {activeTab === "manage-users" && portalMode === "admin" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-6 h-6 text-blue-600" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">User Management</h3>
                  <p className="text-gray-600">
                    View, edit, and manage all Success Club users. This feature is currently under development.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin Management */}
          {activeTab === "manage-admins" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Admin Management</h1>
                  <p className="text-gray-600 mt-2">Create and manage admin accounts and permissions</p>
                </div>
                <Button
                  onClick={() => {
                    setShowAdminForm(true);
                    setEditingAdmin(null);
                    setAdminForm({
                      full_name: "",
                      email: "",
                      password: "",
                      can_manage_users: true,
                      can_manage_events: true,
                      can_manage_challenges: true,
                      can_approve_hosts: true
                    });
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Admin
                </Button>
              </div>

              {/* Admin Form Modal */}
              {showAdminForm && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {editingAdmin ? "Edit Admin Permissions" : "Create New Admin"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAdminSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Full Name</label>
                          <input
                            type="text"
                            value={adminForm.full_name}
                            onChange={(e) => setAdminForm({...adminForm, full_name: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="Enter full name"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Email Address</label>
                          <input
                            type="email"
                            value={adminForm.email}
                            onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="Enter email address"
                            required
                          />
                        </div>
                      </div>
                      
                      {!editingAdmin && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Password</label>
                          <input
                            type="password"
                            value={adminForm.password}
                            onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            placeholder="Enter secure password (min 8 characters)"
                            minLength="8"
                            required
                          />
                        </div>
                      )}

                      {/* Admin Permissions */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-semibold mb-3">Admin Permissions</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={adminForm.can_manage_users}
                              onChange={(e) => setAdminForm({...adminForm, can_manage_users: e.target.checked})}
                              className="w-4 h-4 text-red-600"
                            />
                            <span className="text-sm">Manage Users</span>
                          </label>
                          
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={adminForm.can_manage_events}
                              onChange={(e) => setAdminForm({...adminForm, can_manage_events: e.target.checked})}
                              className="w-4 h-4 text-red-600"
                            />
                            <span className="text-sm">Manage Events</span>
                          </label>
                          
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={adminForm.can_manage_challenges}
                              onChange={(e) => setAdminForm({...adminForm, can_manage_challenges: e.target.checked})}
                              className="w-4 h-4 text-red-600"
                            />
                            <span className="text-sm">Manage Challenges</span>
                          </label>
                          
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={adminForm.can_approve_hosts}
                              onChange={(e) => setAdminForm({...adminForm, can_approve_hosts: e.target.checked})}
                              className="w-4 h-4 text-red-600"
                            />
                            <span className="text-sm">Approve Hosts</span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">
                          {editingAdmin ? "Update Admin" : "Create Admin"}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setShowAdminForm(false)}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Password Reset Modal */}
              {showPasswordReset && (
                <Card>
                  <CardHeader>
                    <CardTitle>Reset Admin Password</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">New Password</label>
                        <input
                          type="password"
                          value={passwordResetForm.new_password}
                          onChange={(e) => setPasswordResetForm({...passwordResetForm, new_password: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          placeholder="Enter new password (min 8 characters)"
                          minLength="8"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Confirm Password</label>
                        <input
                          type="password"
                          value={passwordResetForm.confirm_password}
                          onChange={(e) => setPasswordResetForm({...passwordResetForm, confirm_password: e.target.value})}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          placeholder="Confirm new password"
                          minLength="8"
                        />
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handlePasswordReset(showPasswordReset)}
                          className="bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          Reset Password
                        </Button>
                        <Button
                          onClick={() => setShowPasswordReset(null)}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Admins List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-600" />
                    Platform Administrators ({admins.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {admins.map((admin) => (
                      <div key={admin.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{admin.full_name}</h3>
                              <Badge 
                                variant={admin.role === 'super_admin' ? 'default' : 'secondary'}
                                className={admin.role === 'super_admin' ? 'bg-red-600 text-white' : 'bg-gray-600 text-white'}
                              >
                                {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                              </Badge>
                              <Badge 
                                variant={admin.status === 'active' ? 'default' : 'secondary'}
                                className={admin.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                              >
                                {admin.status}
                              </Badge>
                            </div>
                            <p className="text-gray-600 mb-2">{admin.email}</p>
                            <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                              <span>Created: {new Date(admin.created_at).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>Last Updated: {new Date(admin.updated_at).toLocaleDateString()}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {admin.can_manage_users && <Badge variant="outline">Manage Users</Badge>}
                              {admin.can_manage_events && <Badge variant="outline">Manage Events</Badge>}
                              {admin.can_manage_challenges && <Badge variant="outline">Manage Challenges</Badge>}
                              {admin.can_approve_hosts && <Badge variant="outline">Approve Hosts</Badge>}
                              {admin.can_manage_admins && <Badge variant="outline">Manage Admins</Badge>}
                            </div>
                          </div>
                          
                          {admin.role !== 'super_admin' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingAdmin(admin);
                                  setAdminForm({
                                    full_name: admin.full_name,
                                    email: admin.email,
                                    password: "", // Don't show existing password
                                    can_manage_users: admin.can_manage_users || false,
                                    can_manage_events: admin.can_manage_events || false,
                                    can_manage_challenges: admin.can_manage_challenges || false,
                                    can_approve_hosts: admin.can_approve_hosts || false
                                  });
                                  setShowAdminForm(true);
                                }}
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowPasswordReset(admin.id)}
                                className="text-orange-600 hover:text-orange-700"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteAdmin(admin.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {admins.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No admin users found.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Analytics & Reports Dashboard */}
          {activeTab === "analytics" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
                  <p className="text-gray-600 mt-2">Comprehensive insights into platform performance and user engagement</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export Report
                  </Button>
                </div>
              </div>

              {/* Revenue Analytics Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Revenue Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        ${analyticsData.revenue?.current_metrics?.total_revenue?.toLocaleString() || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Monthly Revenue</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {analyticsData.revenue?.current_metrics?.active_subscribers || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Active Subscribers</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        ${analyticsData.revenue?.current_metrics?.average_revenue_per_user || 0}
                      </div>
                      <p className="text-gray-600 text-sm">ARPU</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-600">
                        ${analyticsData.revenue?.projected_annual_revenue?.toLocaleString() || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Projected Annual</p>
                    </div>
                  </div>
                  
                  {analyticsData.revenue?.monthly_trends && (
                    <div>
                      <h3 className="font-semibold mb-3">Revenue Trends (Last 6 Months)</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-6 gap-2 text-sm">
                          {analyticsData.revenue.monthly_trends.map((month, index) => (
                            <div key={index} className="text-center">
                              <div className="font-medium text-gray-800">{month.month}</div>
                              <div className="text-green-600 font-semibold">${month.revenue}</div>
                              <div className="text-gray-500">{month.members} members</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Engagement Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    User Engagement Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {analyticsData.userEngagement?.overview?.total_users || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Total Users</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {analyticsData.userEngagement?.overview?.activation_rate || 0}%
                      </div>
                      <p className="text-gray-600 text-sm">Activation Rate</p>
                      <div className="text-xs text-gray-500 mt-1" title="Percentage of registered users who have completed onboarding and have active status. Calculated as: Active Users ÷ Total Users × 100">
                        ℹ️ Active vs Total Users
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {analyticsData.userEngagement?.overview?.recent_signups || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Recent Signups (30d)</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-amber-600">
                        {analyticsData.userEngagement?.overview?.pending_users || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Pending Approval</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* User Distribution */}
                    <div>
                      <h3 className="font-semibold mb-3">User Distribution</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                          <span>Members</span>
                          <span className="font-semibold">{analyticsData.userEngagement?.user_distribution?.members || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                          <span>Hosts</span>
                          <span className="font-semibold">{analyticsData.userEngagement?.user_distribution?.hosts || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                          <span>Admins</span>
                          <span className="font-semibold">{analyticsData.userEngagement?.user_distribution?.admins || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Daily Signups */}
                    <div>
                      <h3 className="font-semibold mb-3">Daily Signups (Last 7 Days)</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {analyticsData.userEngagement?.daily_signups?.map((day, index) => (
                          <div key={index} className="flex justify-between items-center py-1">
                            <span className="text-sm text-gray-600">{day.date}</span>
                            <span className="font-semibold">{day.signups}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Host Performance Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-600" />
                    Host Performance Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-amber-600">
                        {analyticsData.hostPerformance?.overview?.total_hosts || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Active Hosts</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {Math.round(analyticsData.hostPerformance?.overview?.average_performance || 0)}
                      </div>
                      <p className="text-gray-600 text-sm">Avg Performance</p>
                      <div className="text-xs text-gray-500 mt-1" title="Composite score based on events hosted, attendee engagement, completion rates, and community growth (Scale: 0-150)">
                        ℹ️ Multi-factor Score
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {analyticsData.hostPerformance?.overview?.pending_hosts || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Pending Approval</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {analyticsData.hostPerformance?.host_performance?.length || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Top Performers</p>
                    </div>
                  </div>

                  {/* Performance Score Explanation */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">Performance Score Components (Max: 150)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="space-y-1">
                        <div className="font-medium text-blue-800">Base Score (50 pts)</div>
                        <div className="text-blue-600">Starting foundation score</div>
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-blue-800">Events Hosted (0-30 pts)</div>
                        <div className="text-blue-600">5 pts per event created</div>
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-blue-800">Challenges Created (0-25 pts)</div>
                        <div className="text-blue-600">8 pts per challenge</div>
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-blue-800">Activity Frequency (0-20 pts)</div>
                        <div className="text-blue-600">Based on activities per month</div>
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-blue-800">Consistency (0-15 pts)</div>
                        <div className="text-blue-600">Regular platform engagement</div>
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-blue-800">User Engagement (0-30 pts)</div>
                        <div className="text-blue-600">Member interaction & satisfaction</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Top Performing Hosts</h3>
                    <div className="space-y-3">
                      {analyticsData.hostPerformance?.host_performance?.slice(0, 5).map((host, index) => (
                        <div key={host.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="font-medium">{host.business_name}</div>
                              <div className="text-sm text-gray-600">
                                {host.events_hosted} events • {host.challenges_created} challenges • {host.total_attendees} attendees
                              </div>
                              <div className="text-xs text-gray-500">
                                Active for {host.days_active} days • {host.activity_rate} activities/month
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-600 text-lg">{host.performance_score}</div>
                              <div className="text-sm text-gray-500">Performance Score</div>
                            </div>
                          </div>
                          
                          {/* Performance Score Breakdown */}
                          {host.score_breakdown && (
                            <div className="border-t border-gray-200 pt-3">
                              <div className="text-xs font-medium text-gray-700 mb-2">Score Breakdown:</div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex justify-between">
                                  <span>Base Score:</span>
                                  <span className="font-medium">{host.score_breakdown.base_score}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Events ({host.events_hosted}):</span>
                                  <span className="font-medium">+{host.score_breakdown.events_score}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Challenges ({host.challenges_created}):</span>
                                  <span className="font-medium">+{host.score_breakdown.challenges_score}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Activity Rate:</span>
                                  <span className="font-medium">+{host.score_breakdown.frequency_score}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Consistency:</span>
                                  <span className="font-medium">+{host.score_breakdown.consistency_score}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Engagement:</span>
                                  <span className="font-medium">+{host.score_breakdown.engagement_score}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>


              {/* Boost Analytics Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-600" />
                      Social Boost Analytics
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        onClick={fetchBoostAnalytics}
                        variant="outline"
                        size="sm"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                      <Button
                        onClick={() => handleResetBoostLimits(null)}
                        variant="outline"
                        size="sm"
                        className="text-amber-700 border-amber-300"
                      >
                        Reset All Limits
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {boostAnalytics ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="text-center p-4 bg-amber-50 rounded-lg">
                          <div className="text-3xl font-bold text-amber-600">
                            {boostAnalytics.total_boosts || 0}
                          </div>
                          <p className="text-gray-600 text-sm mt-1">Total Boosts</p>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="text-3xl font-bold text-blue-600">
                            {boostAnalytics.unique_boosters || 0}
                          </div>
                          <p className="text-gray-600 text-sm mt-1">Active Boosters</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-3xl font-bold text-green-600">
                            {boostAnalytics.unique_boosted || 0}
                          </div>
                          <p className="text-gray-600 text-sm mt-1">Boosted Users</p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                          <div className="text-3xl font-bold text-purple-600">
                            {Object.keys(boostAnalytics.platform_breakdown || {}).length}
                          </div>
                          <p className="text-gray-600 text-sm mt-1">Platforms Used</p>
                        </div>
                      </div>

                      {/* Platform Breakdown */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">Platform Breakdown</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(boostAnalytics.platform_breakdown || {}).map(([platform, count]) => (
                            <div key={platform} className="border rounded-lg p-3">
                              <div className="text-2xl font-bold text-amber-600">{count}</div>
                              <p className="text-sm text-gray-600 capitalize">{platform}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Top Boosters */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">Top Boosters</h3>
                        <div className="space-y-2">
                          {(boostAnalytics.top_boosters || []).map((booster, index) => (
                            <div key={booster.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">
                                  {index + 1}
                                </div>
                                <div>
                                  <p className="font-medium">{booster.name}</p>
                                  <p className="text-sm text-gray-500">{booster.email}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-amber-600">{booster.boost_count}</p>
                                <p className="text-xs text-gray-500">boosts</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recent Boosts */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Recent Boost Activity</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booster</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Boosted User</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(boostAnalytics.recent_boosts || []).slice(0, 10).map((boost) => (
                                <tr key={boost.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{boost.boosted_by_name}</div>
                                    <div className="text-sm text-gray-500">{boost.boosted_by_email}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{boost.boosted_user_name}</div>
                                    <div className="text-sm text-gray-500">{boost.boosted_user_email}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <Badge variant="outline" className="capitalize">{boost.platform}</Badge>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm font-medium text-green-600">+{boost.points_awarded}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(boost.boosted_at).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Zap className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600 mb-4">Click "Refresh" to load boost analytics</p>
                      <Button
                        onClick={fetchBoostAnalytics}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        Load Analytics
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>


              {/* Challenge Participation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Challenge Participation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {analyticsData.challengeParticipation?.overview?.total_challenges || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Total Challenges</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {analyticsData.challengeParticipation?.overview?.active_challenges || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Active Challenges</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {Math.round(analyticsData.challengeParticipation?.overview?.average_participation || 0)}
                      </div>
                      <p className="text-gray-600 text-sm">Avg Participation</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-600">
                        {Math.round(analyticsData.challengeParticipation?.participation_summary?.average_completion_rate || 0)}%
                      </div>
                      <p className="text-gray-600 text-sm">Completion Rate</p>
                    </div>
                  </div>

                  {/* Top Challenges */}
                  <div>
                    <h3 className="font-semibold mb-3">Most Popular Challenges</h3>
                    <div className="space-y-2">
                      {analyticsData.challengeParticipation?.top_challenges?.slice(0, 5).map((challenge, index) => (
                        <div key={challenge.id} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                          <div>
                            <div className="font-medium text-sm">{challenge.title}</div>
                            <div className="text-xs text-gray-600">{challenge.completion_rate}% completion rate</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-purple-600">{challenge.participants}</div>
                            <div className="text-xs text-gray-500">participants</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Event Participation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Event Participation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {analyticsData.challengeParticipation?.event_trends?.reduce((sum, day) => sum + (day.events || 0), 0) || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Total Events</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {analyticsData.challengeParticipation?.event_trends?.filter(day => day.events > 0).length || 0}
                      </div>
                      <p className="text-gray-600 text-sm">Active Days</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-600">
                        {Math.round((analyticsData.challengeParticipation?.event_trends?.reduce((sum, day) => sum + (day.events || 0), 0) || 0) / (analyticsData.challengeParticipation?.event_trends?.length || 1))}
                      </div>
                      <p className="text-gray-600 text-sm">Avg Events/Day</p>
                    </div>
                  </div>

                  {/* Event Activity Timeline */}
                  <div>
                    <h3 className="font-semibold mb-3">Recent Event Activity (Last 7 Days)</h3>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                      {analyticsData.challengeParticipation?.event_trends?.map((day, index) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b border-blue-200 last:border-0">
                          <span className="text-sm font-medium text-gray-700">{day.date}</span>
                          <div className="flex items-center gap-3">
                            <div className="w-24 bg-white rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min((day.events / 10) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <div className="text-right min-w-[60px]">
                              <span className="font-bold text-blue-600">{day.events}</span>
                              <span className="text-xs text-gray-500 ml-1">events</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Enhanced User Management */}
          {activeTab === "manage-users" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                  <p className="text-gray-600 mt-2">Comprehensive user administration and bulk operations</p>
                </div>
                <div className="flex gap-3">
                  {selectedUsers.length > 0 && (
                    <Button 
                      onClick={() => setShowBulkActions(true)}
                      className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Zap className="w-4 h-4" />
                      Bulk Actions ({selectedUsers.length})
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Users
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setMassEmailTemplate({...massEmailTemplate, targetGroups: [], subject: "", message: ""})}
                    className="flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Mass Email
                  </Button>
                </div>
              </div>

              {/* Filters and Search */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-blue-600" />
                    User Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Search Users</label>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Name, email, business..."
                          value={userFilters.searchTerm}
                          onChange={(e) => setUserFilters({...userFilters, searchTerm: e.target.value})}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">User Role</label>
                      <select
                        value={userFilters.role}
                        onChange={(e) => setUserFilters({...userFilters, role: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Roles</option>
                        <option value="member">Members</option>
                        <option value="host">Hosts</option>
                        <option value="admin">Admins</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Status</label>
                      <select
                        value={userFilters.status}
                        onChange={(e) => setUserFilters({...userFilters, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    
                    <div className="flex items-end">
                      <Button className="w-full">
                        Apply Filters
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      All Users ({allUsers.length})
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (selectedUsers.length === allUsers.slice(0, 20).length) {
                            setSelectedUsers([]);
                          } else {
                            setSelectedUsers(allUsers.slice(0, 20).map(u => u.id));
                          }
                        }}
                        className="text-sm"
                      >
                        {selectedUsers.length === allUsers.slice(0, 20).length ? 
                          <><Square className="w-4 h-4 mr-1" /> Deselect All</> : 
                          <><CheckSquare className="w-4 h-4 mr-1" /> Select All</>
                        }
                      </Button>
                      {selectedUsers.length > 0 && (
                        <Badge variant="outline" className="bg-blue-100 text-blue-800">
                          {selectedUsers.length} Selected
                        </Badge>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allUsers.length > 0 ? (
                    <div className="space-y-4">
                      {allUsers.slice(0, 20).map((user) => (
                        <div key={user.id} className={`border rounded-lg p-4 transition-colors ${
                          selectedUsers.includes(user.id) ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, user.id]);
                                  } else {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                  }
                                }}
                                className="mt-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-lg">
                                  {user.role === "host" ? (user.coworking_space_name || user.full_name) : user.full_name}
                                </h3>
                                <Badge 
                                  variant="outline" 
                                  className={
                                    user.role === "member" ? "bg-blue-100 text-blue-800" :
                                    user.role === "host" ? "bg-amber-100 text-amber-800" :
                                    "bg-red-100 text-red-800"
                                  }
                                >
                                  {user.role && user.role.length > 0 ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Unknown'}
                                </Badge>
                                <Badge 
                                  variant="outline"
                                  className={
                                    user.status === "active" ? "bg-green-100 text-green-800" :
                                    user.status === "pending_approval" ? "bg-yellow-100 text-yellow-800" :
                                    "bg-red-100 text-red-800"
                                  }
                                >
                                  {user.status === "pending_approval" ? "Pending" : (user.status && user.status.length > 0 ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown')}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3 text-sm">
                                <div>
                                  <p className="text-gray-500">Email</p>
                                  <p className="font-medium">{user.email}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Joined</p>
                                  <p className="font-medium">{user.days_since_joined} days ago</p>
                                </div>
                                {user.activity_stats && (
                                  <div>
                                    <p className="text-gray-500">Activity</p>
                                    <p className="font-medium">
                                      {user.role === "host" 
                                        ? `${user.activity_stats.total_activities} activities`
                                        : `${user.activity_stats.points_earned} points`
                                      }
                                    </p>
                                  </div>
                                )}
                              </div>

                              {user.role === "member" && (
                                <div className="flex items-center gap-4 text-sm">
                                  <span className={`px-2 py-1 rounded ${
                                    user.subscription_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {user.subscription_active ? '✓ Subscribed' : '✗ Not Subscribed'}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowUserDetails(user)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              {user.status === "active" ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShowUserActions({...user, action: "suspend"})}
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  <UserMinus className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShowUserActions({...user, action: "activate"})}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                              )}
                              
                              {user.role === "member" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShowUserActions({...user, action: "subscription"})}
                                  className="text-purple-600 hover:text-purple-700"
                                >
                                  {user.subscription_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No users found matching your criteria.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Details Modal */}
              {showUserDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>
                        User Details: {showUserDetails.role === "host" ? 
                          (showUserDetails.coworking_space_name || showUserDetails.full_name) : 
                          showUserDetails.full_name
                        }
                      </CardTitle>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowUserDetails(null)}
                        className="text-gray-500"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="font-semibold mb-3">Basic Information</h3>
                          <div className="space-y-2 text-sm">
                            <div><strong>Email:</strong> {showUserDetails.email}</div>
                            <div><strong>Role:</strong> {showUserDetails.role}</div>
                            <div><strong>Status:</strong> {showUserDetails.status}</div>
                            <div><strong>Joined:</strong> {new Date(showUserDetails.created_at).toLocaleDateString()}</div>
                            <div><strong>Email Verified:</strong> {showUserDetails.email_verified ? 'Yes' : 'No'}</div>
                            {showUserDetails.phone_number && (
                              <div><strong>Phone:</strong> {showUserDetails.phone_number}</div>
                            )}
                          </div>
                        </div>
                        
                        {showUserDetails.role === "host" && (
                          <div>
                            <h3 className="font-semibold mb-3">Business Information</h3>
                            <div className="space-y-2 text-sm">
                              <div><strong>Business Name:</strong> {showUserDetails.coworking_space_name || 'N/A'}</div>
                              <div><strong>Contact Person:</strong> {showUserDetails.contact_person || 'N/A'}</div>
                              <div><strong>Address:</strong> {showUserDetails.address || 'N/A'}</div>
                              <div><strong>City:</strong> {showUserDetails.city || 'N/A'}</div>
                              <div><strong>Host Code:</strong> {showUserDetails.host_code || 'Not generated'}</div>
                            </div>
                          </div>
                        )}
                        
                        {showUserDetails.role === "member" && showUserDetails.subscription_details && (
                          <div>
                            <h3 className="font-semibold mb-3">Subscription Details</h3>
                            <div className="space-y-2 text-sm">
                              <div><strong>Plan:</strong> {showUserDetails.subscription_details.plan}</div>
                              <div><strong>Status:</strong> {showUserDetails.subscription_details.status}</div>
                              <div><strong>Next Billing:</strong> {showUserDetails.subscription_details.next_billing_date}</div>
                              <div><strong>Payment Method:</strong> {showUserDetails.subscription_details.payment_method}</div>
                              <div><strong>Total Paid:</strong> ${showUserDetails.subscription_details.total_paid}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* User Actions Modal */}
              {showUserActions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-md">
                    <CardHeader>
                      <CardTitle>
                        {showUserActions.action === "suspend" ? "Suspend User" :
                         showUserActions.action === "activate" ? "Activate User" :
                         "Manage Subscription"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-4 text-gray-600">
                        Are you sure you want to {showUserActions.action} this user?
                      </p>
                      <div className="flex gap-3">
                        <Button 
                          onClick={() => setShowUserActions(null)}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={() => {
                            // Handle user action here
                            console.log(`${showUserActions.action} user:`, showUserActions.email);
                            setShowUserActions(null);
                          }}
                          className={
                            showUserActions.action === "suspend" ? "bg-red-600 hover:bg-red-700" :
                            "bg-green-600 hover:bg-green-700"
                          }
                        >
                          Confirm
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Bulk Actions Modal */}
              {showBulkActions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-blue-600" />
                        Bulk Actions ({selectedUsers.length} users)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Select Action</label>
                          <select
                            value={bulkActionType}
                            onChange={(e) => setBulkActionType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Choose an action...</option>
                            <option value="activate">Activate Users</option>
                            <option value="suspend">Suspend Users</option>
                            <option value="approve_hosts">Approve Hosts</option>
                            <option value="send_message">Send Message</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Reason (optional)</label>
                          <textarea
                            value={bulkMessage}
                            onChange={(e) => setBulkMessage(e.target.value)}
                            placeholder="Enter reason for this action..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                          />
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                          <Button 
                            onClick={() => {
                              setShowBulkActions(false);
                              setBulkActionType("");
                              setBulkMessage("");
                            }}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => {
                              // Handle bulk action here
                              console.log(`Bulk action: ${bulkActionType} for users:`, selectedUsers);
                              setShowBulkActions(false);
                              setBulkActionType("");
                              setBulkMessage("");
                              setSelectedUsers([]);
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                            disabled={!bulkActionType}
                          >
                            Execute Action
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Export Modal */}
              {showExportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Download className="w-5 h-5 text-green-600" />
                        Export User Data
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Export Format</label>
                          <select
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="csv">CSV (Excel Compatible)</option>
                            <option value="json">JSON (Raw Data)</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Filter Options</label>
                          <div className="space-y-2 text-sm">
                            <div>Role: {userFilters.role === "all" ? "All Roles" : userFilters.role}</div>
                            <div>Status: {userFilters.status === "all" ? "All Statuses" : userFilters.status}</div>
                            {userFilters.searchTerm && <div>Search: "{userFilters.searchTerm}"</div>}
                          </div>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                          <Button 
                            onClick={() => setShowExportModal(false)}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={async () => {
                              try {
                                const params = new URLSearchParams({
                                  format: exportFormat,
                                  role: userFilters.role,
                                  status: userFilters.status
                                });
                                
                                const response = await axios.get(
                                  `${API}/admin/users/export?${params.toString()}`,
                                  { headers: getAuthHeaders() }
                                );
                                
                                if (exportFormat === 'csv') {
                                  // Create CSV file and download
                                  const blob = new Blob([response.data.content], { type: 'text/csv' });
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = response.data.filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } else {
                                  // Create JSON file and download
                                  const blob = new Blob([response.data.content], { type: 'application/json' });
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `users_export_${new Date().toISOString().split('T')[0]}.json`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                }
                                
                                alert(`Successfully exported ${response.data.total_records} users!`);
                                setShowExportModal(false);
                              } catch (error) {
                                console.error('Export error:', error);
                                alert('Failed to export data. Please try again.');
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Export Data
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Mass Email Modal */}
              {massEmailTemplate.subject !== undefined && massEmailTemplate.message !== undefined && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Send className="w-5 h-5 text-purple-600" />
                        Mass Email Campaign
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Email Subject</label>
                          <input
                            type="text"
                            value={massEmailTemplate.subject}
                            onChange={(e) => setMassEmailTemplate({...massEmailTemplate, subject: e.target.value})}
                            placeholder="Enter email subject..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Email Message</label>
                          <textarea
                            value={massEmailTemplate.message}
                            onChange={(e) => setMassEmailTemplate({...massEmailTemplate, message: e.target.value})}
                            placeholder="Enter your message..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            rows={8}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Target Groups</label>
                          <div className="space-y-2">
                            {["all_members", "active_members", "all_hosts", "active_hosts", "pending_hosts"].map(group => (
                              <label key={group} className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={massEmailTemplate.targetGroups.includes(group)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setMassEmailTemplate({
                                        ...massEmailTemplate, 
                                        targetGroups: [...massEmailTemplate.targetGroups, group]
                                      });
                                    } else {
                                      setMassEmailTemplate({
                                        ...massEmailTemplate, 
                                        targetGroups: massEmailTemplate.targetGroups.filter(g => g !== group)
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-purple-600"
                                />
                                <span className="text-sm">
                                  {group.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                          <Button 
                            onClick={() => setMassEmailTemplate({subject: undefined, message: undefined, targetGroups: []})}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => {
                              // Handle mass email here
                              console.log('Send mass email:', massEmailTemplate);
                              setMassEmailTemplate({subject: undefined, message: undefined, targetGroups: []});
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                            disabled={!massEmailTemplate.subject || !massEmailTemplate.message || massEmailTemplate.targetGroups.length === 0}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send Campaign
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Twilio Communication Center */}
          {activeTab === "twilio-center" && portalMode === "admin" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Communication Center</h1>
                  <p className="text-gray-600 mt-2">Comprehensive Twilio-powered communication and CRM management</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowCrmSync(true)}
                    className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                  >
                    <UsersIcon className="w-4 h-4" />
                    Sync CRM
                  </Button>
                  <Button
                    onClick={() => setShowEnhancedEmail(true)}
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Enhanced Email
                  </Button>
                </div>
              </div>

              {/* Twilio Analytics Dashboard */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-blue-600">
                      {twilioAnalytics.total_contacts_synced || 0}
                    </div>
                    <p className="text-gray-600 text-sm">Contacts Synced</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {twilioAnalytics.emails_sent_this_month || 0}
                    </div>
                    <p className="text-gray-600 text-sm">Emails This Month</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {twilioAnalytics.email_delivery_rate || 0}%
                    </div>
                    <p className="text-gray-600 text-sm">Delivery Rate</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-orange-600">
                      {twilioAnalytics.active_campaigns || 0}
                    </div>
                    <p className="text-gray-600 text-sm">Active Campaigns</p>
                  </CardContent>
                </Card>
              </div>

              {/* Communication Tools */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Email Campaigns */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="w-5 h-5 text-green-600" />
                      Email Campaigns
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm text-gray-600">
                      Send professional branded emails with advanced tracking and personalization.
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Today:</span>
                        <span className="font-semibold">{twilioAnalytics.emails_sent_today || 0} sent</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>This Month:</span>
                        <span className="font-semibold">{twilioAnalytics.emails_sent_this_month || 0} sent</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Delivery Rate:</span>
                        <span className="font-semibold text-green-600">{twilioAnalytics.email_delivery_rate || 0}%</span>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowEnhancedEmail(true)}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      Create Email Campaign
                    </Button>
                  </CardContent>
                </Card>

                {/* CRM Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UsersIcon className="w-5 h-5 text-blue-600" />
                      CRM Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm text-gray-600">
                      Backup and sync all user data to Twilio's CRM system for comprehensive contact management.
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Total Contacts:</span>
                        <span className="font-semibold">{twilioAnalytics.total_contacts_synced || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Last Sync:</span>
                        <span className="font-semibold">
                          {twilioAnalytics.crm_last_sync ? 
                            new Date(twilioAnalytics.crm_last_sync).toLocaleDateString() : 
                            'Never'
                          }
                        </span>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowCrmSync(true)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      Sync CRM Data
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* CRM Sync Modal */}
              {showCrmSync && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UsersIcon className="w-5 h-5 text-blue-600" />
                        Sync CRM Data
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <p className="text-gray-600">
                          This will backup all {allUsers.length} users to Twilio's CRM system. This includes:
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          <li>Contact information and profiles</li>
                          <li>User roles and status</li>
                          <li>Business information (hosts)</li>
                          <li>Subscription data (members)</li>
                        </ul>
                        <div className="flex gap-3 pt-4">
                          <Button 
                            onClick={() => setShowCrmSync(false)}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={async () => {
                              try {
                                const authHeaders = getAuthHeaders();
                                const response = await axios.post(`${API}/admin/twilio/sync-crm`, {}, { headers: authHeaders });
                                console.log('CRM Sync completed:', response.data);
                                setShowCrmSync(false);
                              } catch (error) {
                                console.error('CRM Sync failed:', error);
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Start CRM Sync
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Enhanced Email Modal */}
              {showEnhancedEmail && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Send className="w-5 h-5 text-green-600" />
                        Enhanced Email Campaign
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Email Subject</label>
                          <input
                            type="text"
                            value={enhancedEmailForm.subject}
                            onChange={(e) => setEnhancedEmailForm({...enhancedEmailForm, subject: e.target.value})}
                            placeholder="Enter email subject..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Email Content</label>
                          <textarea
                            value={enhancedEmailForm.content}
                            onChange={(e) => setEnhancedEmailForm({...enhancedEmailForm, content: e.target.value})}
                            placeholder="Enter your message... Use {{name}} for personalization"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            rows={8}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {"{{name}}, {{email}}, {{role}}"} for personalization
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Target Groups</label>
                          <div className="space-y-2">
                            {["all_members", "active_members", "all_hosts", "active_hosts", "pending_hosts"].map(group => (
                              <label key={group} className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={enhancedEmailForm.targetGroups.includes(group)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setEnhancedEmailForm({
                                        ...enhancedEmailForm, 
                                        targetGroups: [...enhancedEmailForm.targetGroups, group]
                                      });
                                    } else {
                                      setEnhancedEmailForm({
                                        ...enhancedEmailForm, 
                                        targetGroups: enhancedEmailForm.targetGroups.filter(g => g !== group)
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-green-600"
                                />
                                <span className="text-sm">
                                  {group.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                          <Button 
                            onClick={() => {
                              setShowEnhancedEmail(false);
                              setEnhancedEmailForm({subject: "", content: "", targetGroups: []});
                            }}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={async () => {
                              try {
                                const authHeaders = getAuthHeaders();
                                const response = await axios.post(`${API}/admin/twilio/enhanced-email-campaign`, {
                                  subject: enhancedEmailForm.subject,
                                  content: enhancedEmailForm.content,
                                  target_groups: enhancedEmailForm.targetGroups
                                }, { headers: authHeaders });
                                console.log('Enhanced email campaign sent:', response.data);
                                setShowEnhancedEmail(false);
                                setEnhancedEmailForm({subject: "", content: "", targetGroups: []});
                              } catch (error) {
                                console.error('Enhanced email campaign failed:', error);
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700"
                            disabled={!enhancedEmailForm.subject || !enhancedEmailForm.content || enhancedEmailForm.targetGroups.length === 0}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send Enhanced Campaign
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Habits Section */}
          {activeTab === "habits" && portalMode === "member" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <Zap className="w-8 h-8 text-amber-600" />
                    Habit Tracker
                  </h2>
                  <p className="text-gray-600 mt-1">Build lasting habits and track your progress</p>
                </div>
              </div>

              <BusinessHabitTracker 
                userId={user?.id}
              />
            </div>
          )}

          {/* Member Challenges Section */}
          {activeTab === "challenges" && portalMode === "member" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Target className="w-6 h-6 text-amber-600" />
                  Challenges
                </h2>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Available Challenges</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {getFilteredChallenges().length}
                  </p>
                </div>
              </div>

              {/* Challenge Categories Filter */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={challengeFilter === 'all' ? "bg-amber-50 border-amber-200 text-amber-700" : ""}
                  onClick={() => setChallengeFilter('all')}
                >
                  All Challenges
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={challengeFilter === 'active' ? "bg-blue-50 border-blue-200" : ""}
                  onClick={() => setChallengeFilter('active')}
                >
                  Active Challenges
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={challengeFilter === 'completed' ? "bg-blue-50 border-blue-200" : ""}
                  onClick={() => setChallengeFilter('completed')}
                >
                  Completed
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={challengeFilter === 'my_challenges' ? "bg-blue-50 border-blue-200" : ""}
                  onClick={() => setChallengeFilter('my_challenges')}
                >
                  My Challenges
                </Button>
              </div>

              {/* Challenges Grid */}
              {getFilteredChallenges().length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {getFilteredChallenges().map((challenge) => (
                    <Card key={challenge.id} className="relative">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{challenge.title}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge variant={challenge.is_host_specific ? "default" : "secondary"}>
                              {challenge.is_host_specific ? "Host" : "General"}
                            </Badge>
                            <Badge variant="outline" className="text-amber-600">
                              {challenge.points_reward} pts
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <p className="text-gray-600">{challenge.description}</p>
                          
                          {/* Challenge Progress */}
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Progress:</span>
                              <span className="font-medium">{challenge.progress || 0}%</span>
                            </div>
                            <Progress value={challenge.progress || 0} className="h-2" />
                          </div>

                          {/* Challenge Details */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Status:</p>
                              <Badge variant={challenge.status === 'active' ? 'default' : 'secondary'}>
                                {challenge.status}
                              </Badge>
                            </div>
                            <div>
                              <p className="text-gray-500">Participants:</p>
                              <p className="font-medium">{challenge.participants_count || 0}</p>
                            </div>
                            {challenge.deadline && (
                              <div className="col-span-2">
                                <p className="text-gray-500">Deadline:</p>
                                <p className="font-medium">{new Date(challenge.deadline).toLocaleDateString()}</p>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2">
                            {challenge.participation_status === 'not_joined' ? (
                              <Button 
                                className="flex-1"
                                onClick={() => handleJoinChallenge(challenge.id)}
                              >
                                Join Challenge
                              </Button>
                            ) : challenge.participation_status === 'active' ? (
                              <>
                                <Button 
                                  variant="outline" 
                                  className="flex-1"
                                  onClick={() => {
                                    setSelectedChallenge(challenge);
                                    setShowChallengeProgress(true);
                                  }}
                                >
                                  View Progress
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleLeaveChallenge(challenge.id)}
                                >
                                  Leave Challenge
                                </Button>
                              </>
                            ) : (
                              <Button variant="outline" className="flex-1" disabled>
                                Completed
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Challenges</h3>
                    <p className="text-gray-600">
                      No challenges are currently available. Check back soon for new challenges from your host or Success Club!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Enhanced Challenge Progress/Completion Modal */}
          {showChallengeProgress && selectedChallenge && (
            <ChallengeProgressModal 
              challenge={selectedChallenge}
              onClose={() => {
                setShowChallengeProgress(false);
                setSelectedChallenge(null);
                setProgressForm({ progress_notes: '', document_url: '', completion_proof: null });
              }}
              onSubmit={submitChallengeCompletion}
              fetchProgress={fetchChallengeProgress}
              updateProgress={updateChallengeProgress}
            />
          )}

          {/* Member Events Section */}
          {activeTab === "events" && portalMode === "member" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-purple-600" />
                  Events
                </h2>
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    {eventFilter === 'upcoming' && 'Upcoming Events'}
                    {eventFilter === 'past' && 'Past Events'}
                    {eventFilter === 'my_rsvps' && 'My RSVPs'}
                    {eventFilter === 'host_events' && 'Host Events'}
                    {eventFilter === 'general_events' && 'General Events'}
                  </p>
                  <p className="text-2xl font-bold text-purple-600">
                    {getFilteredEvents().length}
                  </p>
                </div>
              </div>

              {/* Event Categories Filter */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={eventFilter === 'upcoming' ? "bg-purple-50 border-purple-200" : ""}
                  onClick={() => setEventFilter('upcoming')}
                >
                  Upcoming
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={eventFilter === 'past' ? "bg-purple-50 border-purple-200" : ""}
                  onClick={() => setEventFilter('past')}
                >
                  Past Events
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={eventFilter === 'my_rsvps' ? "bg-purple-50 border-purple-200" : ""}
                  onClick={() => setEventFilter('my_rsvps')}
                >
                  My RSVPs
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={eventFilter === 'host_events' ? "bg-purple-50 border-purple-200" : ""}
                  onClick={() => setEventFilter('host_events')}
                >
                  Host Events
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className={eventFilter === 'general_events' ? "bg-purple-50 border-purple-200" : ""}
                  onClick={() => setEventFilter('general_events')}
                >
                  General Events
                </Button>
              </div>

              {/* Events Grid */}
              {getFilteredEvents().length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {getFilteredEvents().map((event) => (
                    <Card key={event.id} className="relative">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{event.title}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge variant={event.is_host_specific ? "default" : "secondary"}>
                              {event.is_host_specific ? "Host" : "General"}
                            </Badge>
                            <Badge variant="outline" className="text-purple-600">
                              {event.points_reward} pts
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <p className="text-gray-600">{event.description}</p>
                          
                          {/* Event Details */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-purple-600" />
                              <span className="font-medium">
                                {(() => {
                                  const dateStr = event.event_date;
                                  const eventDate = new Date(dateStr.includes('T') && !dateStr.includes('Z') ? dateStr + 'Z' : dateStr);
                                  return `${eventDate.toLocaleDateString()} at ${eventDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
                                })()}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm">
                              {event.event_type === 'online' ? (
                                <>
                                  <Megaphone className="w-4 h-4 text-green-600" />
                                  <span>Online Event</span>
                                  {event.online_link && <Badge variant="outline">Link Provided</Badge>}
                                </>
                              ) : (
                                <>
                                  <Users className="w-4 h-4 text-blue-600" />
                                  <span>{event.address || event.location}</span>
                                </>
                              )}
                            </div>

                            <div className="flex items-center gap-2 text-sm">
                              <UserPlus className="w-4 h-4 text-amber-600" />
                              <span>{event.attendee_count || 0} attending</span>
                              {event.max_attendees && (
                                <span className="text-gray-500">/ {event.max_attendees} max</span>
                              )}
                            </div>
                          </div>

                          {/* RSVP Status */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">RSVP Status:</span>
                              <Badge variant={
                                event.rsvp_status === 'attending' ? 'default' :
                                event.rsvp_status === 'not_attending' ? 'secondary' :
                                'outline'
                              }>
                                {event.rsvp_status === 'attending' ? 'Attending' :
                                 event.rsvp_status === 'not_attending' ? 'Not Attending' :
                                 'Not Responded'}
                              </Badge>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2">
                            {event.rsvp_status === 'not_responded' ? (
                              <>
                                <Button 
                                  className="flex-1"
                                  onClick={() => handleRSVP(event.id, 'attending')}
                                >
                                  RSVP: Attending
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleRSVP(event.id, 'not_attending')}
                                >
                                  Not Attending
                                </Button>
                              </>
                            ) : event.rsvp_status === 'attending' ? (
                              <>
                                <Button variant="outline" className="flex-1">
                                  View Details
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleRSVP(event.id, 'not_attending')}
                                >
                                  Change RSVP
                                </Button>
                              </>
                            ) : (
                              <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={() => handleRSVP(event.id, 'attending')}
                              >
                                Change to Attending
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Upcoming Events</h3>
                    <p className="text-gray-600">
                      No events are currently scheduled. Check back soon for exciting events from your host or Success Club!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Member Referrals Section */}
          {activeTab === "referrals" && portalMode === "member" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <UserPlus className="w-6 h-6 text-blue-600" />
                  Refer Friends
                </h2>
              </div>
              
              {/* Referral Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-blue-600">
                      {memberReferrals.stats?.total_referrals || 0}
                    </div>
                    <p className="text-gray-600 text-sm">Total Referrals</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-green-600">
                      {memberReferrals.stats?.total_points_earned || 0}
                    </div>
                    <p className="text-gray-600 text-sm">Points Earned</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-orange-600">
                      {memberReferrals.stats?.pending_referrals || 0}
                    </div>
                    <p className="text-gray-600 text-sm">Pending</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {memberReferrals.stats?.points_per_referral || 100}
                    </div>
                    <p className="text-gray-600 text-sm">Points Per Referral</p>
                  </CardContent>
                </Card>
              </div>

              {/* Referral Code & Link */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Your Referral Code</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600 mb-2">
                          {memberReferrals.referral_code || "LOADING..."}
                        </div>
                        <p className="text-gray-600 text-sm">Share this code with friends</p>
                      </div>
                      <Button 
                        onClick={() => navigator.clipboard?.writeText(memberReferrals.referral_code || "")}
                        className="w-full"
                      >
                        Copy Code
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Referral Link</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-3 bg-gray-50 rounded border text-sm break-all">
                        {memberReferrals.referral_link || "Loading..."}
                      </div>
                      <Button 
                        onClick={() => navigator.clipboard?.writeText(memberReferrals.referral_link || "")}
                        className="w-full"
                        variant="outline"
                      >
                        Copy Link
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Referrals */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  {memberReferrals.recent_referrals?.length > 0 ? (
                    <div className="space-y-3">
                      {memberReferrals.recent_referrals.map((referral, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium">{referral.full_name}</p>
                            <p className="text-sm text-gray-600">{referral.email}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant={referral.status === 'active' ? 'default' : 'secondary'}>
                              {referral.status}
                            </Badge>
                            <p className="text-sm text-gray-600 mt-1">
                              {referral.points_earned} points
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No referrals yet. Start sharing your code!
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Sharing Tips */}
              <Card>
                <CardHeader>
                  <CardTitle>Sharing Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {memberReferrals.sharing_tips && memberReferrals.sharing_tips.length > 0 ? memberReferrals.sharing_tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>{tip}</span>
                      </li>
                    )) : (
                      <li className="text-gray-500 text-sm">No sharing tips available</li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Global Boost Success Notification - Shown across all tabs */}
          {showBoostNotification && portalMode === "member" && (
            <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
              <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white px-4 sm:px-8 py-4 sm:py-6 rounded-2xl shadow-2xl transform animate-bounce-in pointer-events-auto mx-4 max-w-md">
                <div className="flex flex-col items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-3 sm:p-4 rounded-full backdrop-blur-sm">
                    <Award className="w-10 h-10 sm:w-12 sm:h-12 text-white animate-pulse" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl sm:text-2xl font-bold mb-1">🎉 Boost Successful! 🎉</h3>
                    <p className="text-base sm:text-lg font-semibold">
                      You boosted {boostNotificationData.platform}!
                    </p>
                    <div className="mt-2 bg-white/30 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full inline-block">
                      <p className="text-lg sm:text-xl font-bold">
                        +{boostNotificationData.points} Points Earned! 🌟
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowBoostNotification(false)}
                    className="mt-2 text-sm bg-white/20 hover:bg-white/30 px-4 py-1 rounded-full transition-all"
                  >
                    Awesome!
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Generic Points Earned Notification (confetti style) */}
          {showPointsNotification && (
            <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
              <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white px-8 py-6 rounded-2xl shadow-2xl transform animate-bounce-in pointer-events-auto mx-4 max-w-md">
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
                    <Trophy className="w-12 h-12 text-white animate-pulse" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold mb-1">🎉 {pointsNotificationData.title} 🎉</h3>
                    <p className="text-lg font-semibold">
                      {pointsNotificationData.message}
                    </p>
                    <div className="mt-2 bg-white/30 backdrop-blur-sm px-4 py-2 rounded-full inline-block">
                      <p className="text-xl font-bold">
                        +{pointsNotificationData.points} Points Earned! 🌟
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowPointsNotification(false)}
                    className="mt-2 text-sm bg-white/20 hover:bg-white/30 px-4 py-1 rounded-full transition-all"
                  >
                    Awesome!
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Simple Notification (no confetti, for non-points activities) */}
          {showSimpleNotification && (
            <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
              <div className={`${
                simpleNotificationData.type === 'success' ? 'bg-green-500' :
                simpleNotificationData.type === 'error' ? 'bg-red-500' :
                simpleNotificationData.type === 'warning' ? 'bg-yellow-500' :
                'bg-blue-500'
              } text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3 max-w-md`}>
                {simpleNotificationData.type === 'success' && <CheckCircle className="w-6 h-6" />}
                {simpleNotificationData.type === 'error' && <AlertCircle className="w-6 h-6" />}
                {simpleNotificationData.type === 'warning' && <AlertCircle className="w-6 h-6" />}
                {simpleNotificationData.type === 'info' && <Info className="w-6 h-6" />}
                <div className="flex-1">
                  <h4 className="font-bold text-lg">{simpleNotificationData.title}</h4>
                  <p className="text-sm">{simpleNotificationData.message}</p>
                </div>
                <button 
                  onClick={() => setShowSimpleNotification(false)}
                  className="hover:bg-white/20 p-1 rounded transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Member Social Boosts Section */}
          {activeTab === "boosts" && portalMode === "member" && (
            <div className="space-y-6">

              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <ThumbsUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  Social Media Boosts
                </h2>
                <Button 
                  onClick={() => setShowProfileSetup(true)}
                  className="flex items-center gap-2 text-sm sm:text-base whitespace-nowrap w-full sm:w-auto justify-center"
                >
                  <Plus className="w-4 h-4" />
                  <span className="truncate">Setup Social Profiles</span>
                </Button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                        <ThumbsUp className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Boosts Given</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900 transition-all duration-300 animate-count-up">{boostStats.boosts_given}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                        <Heart className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Boosts Received</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900 transition-all duration-300 animate-count-up">{boostStats.boosts_received}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-amber-100 p-2 rounded-lg flex-shrink-0">
                        <Award className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Points Earned</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900 transition-all duration-300 animate-count-up">{boostStats.points_earned}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">This Month</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900 transition-all duration-300 animate-count-up">{boostStats.this_month_given}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Available Boost Targets */}
              {boostTargets.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-900">Available to Boost</h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {boostTargets && boostTargets.length > 0 ? boostTargets.map((target) => (
                      <Card key={target.user_id} className="overflow-hidden">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-3">
                            {target.profile_picture_url ? (
                              <img 
                                src={target.profile_picture_url} 
                                alt={target.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                {target.name && target.name.length > 0 ? target.name.charAt(0).toUpperCase() : '?'}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-gray-900 truncate">{target.name || 'Unknown User'}</h4>
                              <p className="text-sm text-gray-600 capitalize truncate">{target.role || 'member'}</p>
                            </div>
                          </div>
                        </CardHeader>
                        
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {target.profiles && target.profiles.length > 0 ? target.profiles.map((profile) => (
                              <Button
                                key={profile.platform}
                                onClick={() => handleBoost(target.user_id, profile.platform, profile.url)}
                                variant="outline"
                                className="flex items-center gap-2 p-2 sm:p-3 h-auto justify-start"
                              >
                                {profile.platform === 'instagram' && <Instagram className="w-4 h-4 text-pink-600 flex-shrink-0" />}
                                {profile.platform === 'linkedin' && <Linkedin className="w-4 h-4 text-blue-700 flex-shrink-0" />}
                                {profile.platform === 'facebook' && <Facebook className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                                {profile.platform === 'google_reviews' && <Globe className="w-4 h-4 text-red-600 flex-shrink-0" />}
                                <div className="text-left min-w-0 flex-1">
                                  <div className="font-medium capitalize text-sm truncate">
                                    {profile.platform === 'google_reviews' ? 'Google' : profile.platform}
                                  </div>
                                  <div className="text-xs text-gray-500">+25 pts</div>
                                </div>
                              </Button>
                            )) : (
                              <p className="text-sm text-gray-500 col-span-full">No social profiles available</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )) : (
                      <Card>
                        <CardContent className="p-8 text-center">
                          <ThumbsUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Boost Targets Available</h3>
                          <p className="text-gray-600 mb-4">
                            Set up your social media profiles to start receiving boosts from others!
                          </p>
                          <Button onClick={() => setShowProfileSetup(true)}>
                            Setup Your Social Profiles
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <ThumbsUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Boost Targets Available</h3>
                    <p className="text-gray-600 mb-4">
                      Set up your social media profiles to start receiving boosts from others!
                    </p>
                    <Button onClick={() => setShowProfileSetup(true)}>
                      Setup Your Social Profiles
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              {/* Social Profile Setup Modal */}
              {showProfileSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-gray-900">Setup Social Media Profiles</h2>
                      <button
                        onClick={() => setShowProfileSetup(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    
                    <p className="text-gray-600 mb-6">
                      Add your social media links to allow other members to boost your profiles. You'll get notified when someone boosts you!
                    </p>
                    
                    <div className="space-y-4">
                      {['instagram', 'linkedin', 'facebook', 'google_reviews'].map((platform) => (
                        <div key={platform} className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700 capitalize">
                            {platform === 'google_reviews' ? 'Google Reviews' : platform} Profile URL
                          </label>
                          <input
                            type="url"
                            value={socialLinks[platform]}
                            onChange={(e) => setSocialLinks(prev => ({
                              ...prev,
                              [platform]: e.target.value
                            }))}
                            placeholder={`https://${platform === 'google_reviews' ? 'g.page' : platform}.com/yourusername`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <Button 
                        onClick={async () => {
                          // Filter out empty links
                          const validLinks = {};
                          Object.entries(socialLinks).forEach(([platform, url]) => {
                            if (url.trim()) {
                              validLinks[platform] = url.trim();
                            }
                          });

                          try {
                            await updateSocialProfiles(validLinks);
                            setShowProfileSetup(false);
                          } catch (error) {
                            console.error('Error saving social profiles:', error);
                            // Error handling is already in updateSocialProfiles function
                          }
                        }}
                        className="flex-1"
                      >
                        Save Social Profiles
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowProfileSetup(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Member Accountability Partner Section */}
          {activeTab === "accountability" && portalMode === "member" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6 text-amber-600" />
                  Accountability Partner
                </h2>
              </div>

              {/* Partner Success Notification */}
              {showPartnerSuccessNotification && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center transform animate-bounce-in">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Clock className="w-10 h-10 text-amber-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{fontFamily: 'system-ui, -apple-system, sans-serif'}}>
                      Partnership Request Sent!
                    </h3>
                    <p className="text-gray-700 mb-4 text-lg" style={{fontFamily: 'system-ui, -apple-system, sans-serif'}}>
                      {partnerSuccessMessage}
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-amber-900" style={{fontFamily: 'system-ui, -apple-system, sans-serif'}}>
                        <strong>Note:</strong> Partner will be assigned for you if partner request is declined or not accepted within 48 hours.
                      </p>
                    </div>
                    <Button 
                      onClick={() => setShowPartnerSuccessNotification(false)}
                      className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8"
                    >
                      Got it!
                    </Button>
                  </div>
                </div>
              )}

              {/* Rules Carousel - Always visible */}
              <div className="max-w-4xl mx-auto mb-8">
                <AccountabilityRulesCarousel />
              </div>

              {accountabilityStatus === null ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
                </div>
              ) : !accountabilityStatus.has_partner ? (
                /* Selection Screen */
                <div className="max-w-4xl mx-auto space-y-6">
                  {/* Hero Message */}
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-amber-900 mb-3">Find Your Perfect Accountability Partner</h2>
                    <p className="text-lg text-gray-600">Get matched with someone who shares your goals and style</p>
                  </div>

                  {/* Smart Matching - Featured */}
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-8 rounded-xl shadow-xl text-white">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-6 h-6" />
                          <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">RECOMMENDED</span>
                        </div>
                        <h3 className="text-3xl font-bold mb-2">Smart Matching</h3>
                        <p className="text-amber-100 text-lg">AI-powered matching based on your preferences</p>
                      </div>
                      <Target className="w-20 h-20 opacity-20" />
                    </div>
                    <ul className="space-y-2 mb-6 text-amber-50">
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5" />
                        Match based on availability and goals
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5" />
                        Find partners with compatible styles
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-5 h-5" />
                        New partner every 2 weeks for variety
                      </li>
                    </ul>
                    <button
                      onClick={() => setShowMatchingPreferencesModal(true)}
                      className="w-full bg-white text-amber-600 py-4 rounded-lg font-bold text-lg hover:bg-amber-50 transition-all shadow-lg"
                    >
                      Get Started with Smart Matching
                    </button>
                  </div>

                  {/* Alternative Options */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      onClick={() => {
                        fetchAvailablePartners();
                        setShowPartnerSelection(true);
                      }}
                      className="bg-white border-2 border-gray-300 hover:border-amber-600 p-6 rounded-xl transition-all"
                    >
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                      <h3 className="text-xl font-bold mb-2 text-gray-900">Browse Partners</h3>
                      <p className="text-gray-600 text-sm">Choose from available members</p>
                    </button>

                    <button
                      onClick={handleRandomPartner}
                      className="bg-white border-2 border-gray-300 hover:border-purple-600 p-6 rounded-xl transition-all"
                    >
                      <Shuffle className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                      <h3 className="text-xl font-bold mb-2 text-gray-900">Random Match</h3>
                      <p className="text-gray-600 text-sm">Get paired instantly</p>
                    </button>
                  </div>
                </div>
              ) : (
                /* Partner Dashboard with Tabs */
                <div className="max-w-6xl mx-auto space-y-6">
                  {/* Tab Navigation */}
                  <div className="flex space-x-2 border-b border-gray-200">
                    <button
                      onClick={() => setActiveAccountabilityTab('partner')}
                      className={`px-4 py-2 font-medium transition-colors ${
                        activeAccountabilityTab === 'partner'
                          ? 'border-b-2 border-amber-600 text-amber-600'
                          : 'text-gray-600 hover:text-amber-600'
                      }`}
                    >
                      My Partner
                    </button>
                    <button
                      onClick={() => setActiveAccountabilityTab('goals')}
                      className={`px-4 py-2 font-medium transition-colors ${
                        activeAccountabilityTab === 'goals'
                          ? 'border-b-2 border-amber-600 text-amber-600'
                          : 'text-gray-600 hover:text-amber-600'
                      }`}
                    >
                      Weekly Goals
                    </button>
                    <button
                      onClick={() => setActiveAccountabilityTab('chat')}
                      className={`px-4 py-2 font-medium transition-colors relative ${
                        activeAccountabilityTab === 'chat'
                          ? 'border-b-2 border-amber-600 text-amber-600'
                          : 'text-gray-600 hover:text-amber-600'
                      }`}
                    >
                      <MessageCircle className="w-4 h-4 inline mr-1" />
                      Chat
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveAccountabilityTab('reviews')}
                      className={`px-4 py-2 font-medium transition-colors ${
                        activeAccountabilityTab === 'reviews'
                          ? 'border-b-2 border-amber-600 text-amber-600'
                          : 'text-gray-600 hover:text-amber-600'
                      }`}
                    >
                      Reviews
                    </button>
                  </div>

                  {/* Partner Tab */}
                  {activeAccountabilityTab === 'partner' && (
                    <div className="space-y-6">
                      {/* Participation Status Alert */}
                      {participationStatus && participationStatus.has_partnership && (
                        <Card className={`border-2 ${participationStatus.participated ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-300'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {participationStatus.participated ? (
                                  <>
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                    <div>
                                      <p className="font-semibold text-green-900">✓ Weekly Check-in Complete!</p>
                                      <p className="text-sm text-green-700">You've checked in this week - great progress!</p>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                                    <div>
                                      <p className="font-semibold text-yellow-900">Weekly Check-in Required</p>
                                      <p className="text-sm text-yellow-700">Check in by Sunday ({participationStatus.days_until_sunday} days left)</p>
                                    </div>
                                  </>
                                )}
                              </div>
                              {!participationStatus.participated && (
                                <Button onClick={() => setShowCheckInModal(true)} className="bg-amber-600 hover:bg-amber-700">
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Check In Now
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Partner Info Card */}
                      <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                        <CardHeader>
                          <CardTitle className="text-amber-900">Your Accountability Partner</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-4 mb-4">
                            {accountabilityStatus.partner.profile_picture_url ? (
                              <img 
                                src={accountabilityStatus.partner.profile_picture_url} 
                                alt={accountabilityStatus.partner.name}
                                className="w-16 h-16 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-amber-200 flex items-center justify-center">
                                <Users className="w-8 h-8 text-amber-600" />
                              </div>
                            )}
                            <div>
                              <h3 className="text-xl font-bold text-amber-900">{accountabilityStatus.partner.name}</h3>
                              <p className="text-amber-700">{accountabilityStatus.partner.email}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="bg-white rounded-lg p-3">
                              <p className="text-sm text-gray-600">Check-ins This Week</p>
                              <p className="text-2xl font-bold text-amber-600">{accountabilityStatus.check_ins_this_week}</p>
                            </div>
                            <div className="bg-white rounded-lg p-3">
                              <p className="text-sm text-gray-600">Partnership Started</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {new Date(accountabilityStatus.partnership.started_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Action Buttons */}
                      <div className="grid md:grid-cols-3 gap-4">
                        <Button
                          onClick={() => setShowCheckInForm(true)}
                          className="bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                        >
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Weekly Check-In
                        </Button>
                        <Button
                          onClick={() => setShowScheduleCallForm(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                        >
                          <Video className="w-5 h-5 mr-2" />
                          Schedule Call
                        </Button>
                        <Button
                          onClick={() => setShowReviewForm(true)}
                          variant="outline"
                          className="border-amber-600 text-amber-600 hover:bg-amber-50 py-6 text-lg"
                        >
                          <Star className="w-5 h-5 mr-2" />
                          End & Review
                        </Button>
                      </div>

                      {/* Scheduled Calls List */}
                      {scheduledCalls && scheduledCalls.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Video className="w-5 h-5" />
                              Upcoming Video Calls
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {scheduledCalls.map(call => (
                                <div key={call.id} className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="font-semibold text-gray-900">
                                      {new Date(call.scheduled_time).toLocaleString()}
                                    </p>
                                    <a 
                                      href={call.meeting_link} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      Join Meeting →
                                    </a>
                                  </div>
                                  {call.notes && <p className="text-sm text-gray-600">{call.notes}</p>}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Partner's Habits Summary */}
                      {accountabilityStatus?.partner && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Zap className="w-5 h-5 text-amber-600" />
                            Partner's Habits & Progress
                          </h3>
                          <PartnerHabitsSummary 
                            partnerId={accountabilityStatus.partner.id}
                            partnerName={accountabilityStatus.partner.name}
                          />
                        </div>
                      )}

                      {/* Accountability Partnership Rules */}
                      <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                        <CardHeader>
                          <CardTitle className="text-amber-900 text-xl font-bold flex items-center gap-2">
                            <BookOpen className="w-5 h-5" />
                            Accountability Partnership Rules
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3 text-amber-900">
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">1.</span>
                              <div>
                                <span className="font-semibold">Choose or Be Matched with a Partner:</span>
                                <p className="text-amber-800 text-sm mt-1">Pick someone with similar goals or let the app connect you with a random partner. This is your teammate to help you push towards your goals!</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">2.</span>
                              <div>
                                <span className="font-semibold">Set Your Goals:</span>
                                <p className="text-amber-800 text-sm mt-1">Work together to define clear goals. Be honest, ambitious, and make sure the goals reflect the best version of yourself.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">3.</span>
                              <div>
                                <span className="font-semibold">Check-in Regularly:</span>
                                <p className="text-amber-800 text-sm mt-1">Keep each other updated on progress. Check in daily or weekly to stay on track. This isn't just about points—it's about owning your progress!</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">4.</span>
                              <div>
                                <span className="font-semibold">Support Each Other:</span>
                                <p className="text-amber-800 text-sm mt-1">Encourage your partner. The stronger they get, the stronger you get. Motivation goes both ways!</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">5.</span>
                              <div>
                                <span className="font-semibold">Earn Points for Staying on Track:</span>
                                <p className="text-amber-800 text-sm mt-1">Hit your goals and check in consistently to earn points. If you both reach your goals, you'll unlock extra points and recognition.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">6.</span>
                              <div>
                                <span className="font-semibold">New Partner Every 2 Weeks:</span>
                                <p className="text-amber-800 text-sm mt-1">Every 2 weeks, you'll be paired with a new partner. Use this chance to connect, grow, and challenge yourself even more.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">7.</span>
                              <div>
                                <span className="font-semibold">Resolve Issues Quickly:</span>
                                <p className="text-amber-800 text-sm mt-1">If something isn't working, use the app to find a solution or switch partners. Don't let anything hold you back from reaching your potential.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <span className="text-amber-600 font-bold flex-shrink-0">8.</span>
                              <div>
                                <span className="font-semibold">Review Your Partner:</span>
                                <p className="text-amber-800 text-sm mt-1">At the end of each 2-week period, you can rate and review your partner. Give feedback on their support, encouragement, and accountability. Reviews help everyone improve and find better partnerships.</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Goals Tab */}
                  {activeAccountabilityTab === 'goals' && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Shared Weekly Goals Board</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <SharedGoalsBoard />
                      </CardContent>
                    </Card>
                  )}

                  {/* Chat Tab */}
                  {activeAccountabilityTab === 'chat' && (
                    <div className="space-y-6">
                      <Card className="h-[600px] flex flex-col">
                        <CardHeader className="border-b">
                          <CardTitle className="flex items-center gap-2">
                            <MessageCircle className="w-5 h-5" />
                            Chat with Your Partner
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-y-auto p-4">
                          {chatMessages.length === 0 ? (
                            <div className="text-center py-12">
                              <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-500">No messages yet. Start the conversation!</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {chatMessages.map((msg) => (
                                <div
                                  key={msg.id}
                                  className={`flex ${msg.is_own_message ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div
                                    className={`max-w-[70%] rounded-lg p-3 ${
                                      msg.is_own_message
                                        ? 'bg-amber-600 text-white'
                                        : 'bg-gray-100 text-gray-900'
                                    }`}
                                  >
                                    <p className="text-sm font-semibold mb-1">
                                      {msg.sender_name}
                                    </p>
                                    <p className="whitespace-pre-wrap">{msg.message}</p>
                                    <p
                                      className={`text-xs mt-2 ${
                                        msg.is_own_message ? 'text-amber-100' : 'text-gray-500'
                                      }`}
                                    >
                                      {new Date(msg.created_at).toLocaleTimeString()}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                        <div className="border-t p-4">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={accountabilityChatMessage}
                              onChange={(e) => setAccountabilityChatMessage(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                              placeholder="Type your message..."
                              className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                            />
                            <Button
                              onClick={handleSendMessage}
                              className="bg-amber-600 hover:bg-amber-700"
                              disabled={!accountabilityChatMessage.trim()}
                            >
                              <Send className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </div>
                  )}

                  {/* Reviews Tab */}
                  {activeAccountabilityTab === 'reviews' && (
                    <div className="space-y-6">
                      {accountabilityReviews === null ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
                        </div>
                      ) : (
                        <>
                          {/* Reviews Given */}
                          <Card>
                            <CardHeader>
                              <CardTitle>Reviews I've Given</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {accountabilityReviews.reviews_given.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No reviews given yet</p>
                              ) : (
                                <div className="space-y-4">
                                  {accountabilityReviews.reviews_given.map(review => (
                                    <div key={review.id} className="border-b pb-4 last:border-0">
                                      <div className="flex items-center gap-3 mb-2">
                                        {review.partner_profile_picture ? (
                                          <img src={review.partner_profile_picture} alt={review.partner_name} className="w-10 h-10 rounded-full" />
                                        ) : (
                                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                            <Users className="w-5 h-5" />
                                          </div>
                                        )}
                                        <div>
                                          <p className="font-semibold">{review.partner_name}</p>
                                          <div className="flex items-center gap-1">
                                            {[...Array(review.rating)].map((_, i) => (
                                              <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                                            ))}
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-500 ml-auto">{new Date(review.created_at).toLocaleDateString()}</p>
                                      </div>
                                      <p className="text-gray-700 pl-13">{review.review}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>

                          {/* Reviews Received */}
                          <Card>
                            <CardHeader>
                              <CardTitle>Reviews I've Received</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {accountabilityReviews.reviews_received.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No reviews received yet</p>
                              ) : (
                                <div className="space-y-4">
                                  {accountabilityReviews.reviews_received.map(review => (
                                    <div key={review.id} className="border-b pb-4 last:border-0">
                                      <div className="flex items-center gap-3 mb-2">
                                        {review.reviewer_profile_picture ? (
                                          <img src={review.reviewer_profile_picture} alt={review.reviewer_name} className="w-10 h-10 rounded-full" />
                                        ) : (
                                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                            <Users className="w-5 h-5" />
                                          </div>
                                        )}
                                        <div>
                                          <p className="font-semibold">{review.reviewer_name}</p>
                                          <div className="flex items-center gap-1">
                                            {[...Array(review.rating)].map((_, i) => (
                                              <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                                            ))}
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-500 ml-auto">{new Date(review.created_at).toLocaleDateString()}</p>
                                      </div>
                                      <p className="text-gray-700 pl-13">{review.review}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Weekly Check-In Modal */}
              {showCheckInModal && (
                <WeeklyCheckInPrompt
                  onClose={() => setShowCheckInModal(false)}
                  onComplete={() => {
                    // Refresh participation status after check-in
                    loadParticipationStatus();
                    loadWeeklyGoals();
                  }}
                />
              )}

              {/* Matching Preferences Modal */}
              {showMatchingPreferencesModal && (
                <MatchingPreferencesModal
                  onClose={() => setShowMatchingPreferencesModal(false)}
                  onComplete={() => {
                    // Refresh accountability status after matching
                    fetchAccountabilityStatus();
                  }}
                />
              )}

              {/* Partner Selection Modal */}
              {showPartnerSelection && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold">Select Your Accountability Partner</h2>
                      <button onClick={() => setShowPartnerSelection(false)}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {availablePartners.length === 0 ? (
                        <p className="col-span-2 text-center text-gray-600 py-8">No available partners at the moment. Try choosing randomly!</p>
                      ) : (
                        availablePartners.map(partner => (
                          <button
                            key={partner.id}
                            onClick={() => handleSelectPartner(partner.id)}
                            className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-amber-600 hover:bg-amber-50 transition-all"
                          >
                            {partner.profile_picture_url ? (
                              <img 
                                src={partner.profile_picture_url} 
                                alt={partner.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                                <Users className="w-6 h-6 text-gray-600" />
                              </div>
                            )}
                            <div className="text-left">
                              <h3 className="font-bold text-gray-900">{partner.name}</h3>
                              <p className="text-sm text-gray-600">{partner.total_points} points</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Check-In Form Modal */}
              {showCheckInForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold">Weekly Check-In</h2>
                      <button onClick={() => setShowCheckInForm(false)}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Progress Rating</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(rating => (
                            <button
                              key={rating}
                              onClick={() => setCheckInForm({...checkInForm, progress_rating: rating})}
                              className={`p-2 ${checkInForm.progress_rating >= rating ? 'text-amber-500' : 'text-gray-300'}`}
                            >
                              <Star className="w-8 h-8 fill-current" />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes & Reflection</label>
                        <textarea
                          value={checkInForm.notes}
                          onChange={(e) => setCheckInForm({...checkInForm, notes: e.target.value})}
                          className="w-full border rounded-lg p-3 h-32"
                          placeholder="Share your progress, challenges, and wins this week..."
                        />
                      </div>
                      <Button onClick={handleCheckIn} className="w-full bg-green-600 hover:bg-green-700">
                        Submit Check-In
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Review Form Modal */}
              {showReviewForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold">Review Your Partner</h2>
                      <button onClick={() => setShowReviewForm(false)}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Rating</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(rating => (
                            <button
                              key={rating}
                              onClick={() => setReviewForm({...reviewForm, rating})}
                              className={`p-2 ${reviewForm.rating >= rating ? 'text-amber-500' : 'text-gray-300'}`}
                            >
                              <Star className="w-8 h-8 fill-current" />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Review</label>
                        <textarea
                          value={reviewForm.review}
                          onChange={(e) => setReviewForm({...reviewForm, review: e.target.value})}
                          className="w-full border rounded-lg p-3 h-32"
                          placeholder="Share your experience with your accountability partner..."
                        />
                      </div>
                      <Button onClick={handleSubmitReview} className="w-full bg-amber-600 hover:bg-amber-700">
                        Submit Review & End Partnership
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Goals Form Modal */}
              {showGoalsForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold">Set Weekly Goals</h2>
                      <button onClick={() => {
                        setShowGoalsForm(false);
                        setGoalsFormData([]);
                        setNewGoalText('');
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Add Your Goals (One at a Time)</label>
                        <div className="flex gap-2 mb-4">
                          <input
                            type="text"
                            value={newGoalText}
                            onChange={(e) => setNewGoalText(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddGoalItem()}
                            placeholder="Enter a goal..."
                            className="flex-1 border rounded-lg px-3 py-2"
                          />
                          <Button
                            onClick={handleAddGoalItem}
                            className="bg-green-600 hover:bg-green-700"
                            disabled={!newGoalText.trim()}
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>
                        
                        {/* Goals List */}
                        {goalsFormData.length > 0 ? (
                          <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                            {goalsFormData.map((goal, index) => (
                              <div key={index} className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                                <CheckSquare className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                <span className="flex-1 text-gray-900">{goal}</span>
                                <button
                                  onClick={() => handleRemoveGoalItem(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                            No goals added yet. Add your first goal above!
                          </p>
                        )}
                        
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Tip: Set specific, measurable goals by Monday to stay on track with your accountability partner!
                        </p>
                      </div>
                      <Button 
                        onClick={handleSubmitGoals} 
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        disabled={goalsFormData.length === 0}
                      >
                        <Target className="w-4 h-4 mr-2" />
                        Save {goalsFormData.length} Goal{goalsFormData.length !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Duplicate Member Referrals Section removed - was causing JSX parsing error */}

          {/* Member & Host Magazine Section */}
          {activeTab === "magazines" && (portalMode === "member" || portalMode === "host") && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Success Magazine</h1>
                  <p className="text-gray-600 mt-2">Stay updated with the latest insights and success stories</p>
                </div>
              </div>

              {/* Magazine Issues */}
              {magazines.length > 0 ? (
                <div className="grid gap-6">
                  {magazines.map((magazine) => (
                    <Card key={magazine.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="flex">
                          {magazine.cover_image_url && (
                            <div className="w-48 flex-shrink-0">
                              <img 
                                src={magazine.cover_image_url} 
                                alt={magazine.title}
                                className="w-full h-64 object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1 p-6">
                            <div className="flex items-center gap-3 mb-3">
                              <h2 className="text-2xl font-bold text-gray-900">{magazine.title}</h2>
                              <Badge>Issue #{magazine.issue_number}</Badge>
                              <span className="text-sm text-gray-500">
                                Published {new Date(magazine.published_date).toLocaleDateString()}
                              </span>
                            </div>
                            
                            {magazine.description && (
                              <p className="text-gray-600 mb-4">{magazine.description}</p>
                            )}
                            
                            <div className="flex items-center gap-4">
                              <Button
                                onClick={() => setSelectedMagazine(magazine)}
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                <BookOpen className="w-4 h-4 mr-2" />
                                Read Magazine
                              </Button>
                              
                              {/* Ad Space Request */}
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setSelectedMagazine(magazine);
                                  setShowAdRequestForm(true);
                                }}
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              >
                                Request Ad Space
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Magazines Available</h3>
                    <p className="text-gray-500">Check back later for new magazine issues</p>
                  </CardContent>
                </Card>
              )}

              {/* Featured Articles Section */}
              {articles.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Featured Articles</h2>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {articles.slice(0, 6).map((article) => (
                      <Card key={article.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        {article.featured_image_url && (
                          <div className="h-48 overflow-hidden">
                            <img 
                              src={article.featured_image_url} 
                              alt={article.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <div className="mb-3">
                            <Badge variant="outline" className="mb-2">
                              {article.category}
                            </Badge>
                            <h3 className="font-semibold text-lg text-gray-900 line-clamp-2">
                              {article.title}
                            </h3>
                          </div>
                          
                          {article.excerpt && (
                            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                              {article.excerpt}
                            </p>
                          )}
                          
                          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                            <div className="flex items-center gap-2">
                              {article.author_image_url && (
                                <img 
                                  src={article.author_image_url} 
                                  alt={article.author_name}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              )}
                              <span>{article.author_name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span>{new Date(article.published_date).toLocaleDateString()}</span>
                              {article.read_time_minutes && (
                                <span>{article.read_time_minutes} min read</span>
                              )}
                            </div>
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedArticle(article)}
                            className="w-full"
                          >
                            Read Article
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* User's Ad Requests */}
              {userAdRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Your Advertisement Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {userAdRequests.map((request) => (
                        <div key={request.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{request.company_name || "Ad Request"}</h4>
                            <Badge variant={
                              request.status === 'approved' ? 'default' :
                              request.status === 'rejected' ? 'destructive' : 'secondary'
                            }>
                              {request.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><strong>Magazine:</strong> {request.magazine_title}</p>
                            <p><strong>Content:</strong> {request.ad_content}</p>
                            <p><strong>Submitted:</strong> {new Date(request.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Schedule Call Modal */}
              {showScheduleCallForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Video className="w-6 h-6 text-blue-600" />
                        Schedule Video Call
                      </h2>
                      <button onClick={() => {
                        setShowScheduleCallForm(false);
                        setCallFormData({ meeting_link: '', scheduled_time: '', notes: '' });
                      }}>
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Meeting Link *</label>
                        <input
                          type="url"
                          value={callFormData.meeting_link}
                          onChange={(e) => setCallFormData({...callFormData, meeting_link: e.target.value})}
                          placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Create a meeting on Zoom, Google Meet, or any video platform and paste the link here
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Date & Time *</label>
                        <input
                          type="datetime-local"
                          value={callFormData.scheduled_time}
                          onChange={(e) => setCallFormData({...callFormData, scheduled_time: e.target.value})}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Notes (Optional)</label>
                        <textarea
                          value={callFormData.notes}
                          onChange={(e) => setCallFormData({...callFormData, notes: e.target.value})}
                          placeholder="Any topics you'd like to discuss..."
                          className="w-full border rounded-lg px-3 py-2 h-24"
                        />
                      </div>
                      <Button 
                        onClick={handleScheduleCall} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={!callFormData.meeting_link || !callFormData.scheduled_time}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Call & Notify Partner
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Settings Section - All Portals */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                  <p className="text-gray-600 mt-2">Manage your account preferences and settings</p>
                </div>
              </div>

              <Tabs defaultValue="profile" className="w-full">
                {/* Conditional TabsList based on portal mode */}
                {portalMode === "admin" && (
                  <TabsList className="grid w-full grid-cols-5 lg:w-auto">
                    <TabsTrigger value="profile">Profile</TabsTrigger>
                    <TabsTrigger value="account">Account</TabsTrigger>
                    <TabsTrigger value="system">System</TabsTrigger>
                    <TabsTrigger value="points">Points</TabsTrigger>
                    <TabsTrigger value="users">Users</TabsTrigger>
                  </TabsList>
                )}
                {portalMode === "member" && (
                  <TabsList className="grid w-full grid-cols-5 lg:w-auto">
                    <TabsTrigger value="profile">Profile</TabsTrigger>
                    <TabsTrigger value="social">Social Media</TabsTrigger>
                    <TabsTrigger value="notifications">Notifications</TabsTrigger>
                    <TabsTrigger value="payment">Payment</TabsTrigger>
                    <TabsTrigger value="account">Account</TabsTrigger>
                  </TabsList>
                )}
                {portalMode === "host" && (
                  <TabsList className="grid w-full grid-cols-5 lg:w-auto">
                    <TabsTrigger value="profile">Profile</TabsTrigger>
                    <TabsTrigger value="space">Coworking Space</TabsTrigger>
                    <TabsTrigger value="notifications">Notifications</TabsTrigger>
                    <TabsTrigger value="payment">Payment</TabsTrigger>
                    <TabsTrigger value="account">Account</TabsTrigger>
                  </TabsList>
                )}

                {/* Profile Settings Tab */}
                <TabsContent value="profile" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Profile Information</CardTitle>
                      <CardDescription>Update your personal information and profile details</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Profile Picture Section */}
                      <div className="flex flex-col items-center pb-6 border-b">
                        <div className="relative">
                          {userProfile?.profile_picture_url ? (
                            <>
                              <img
                                src={userProfile.profile_picture_url}
                                alt="Profile"
                                className="w-32 h-32 rounded-full object-cover border-4 border-amber-200"
                              />
                              <button
                                onClick={() => setUserProfile({...userProfile, profile_picture_url: ''})}
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-lg"
                                title="Remove profile picture"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center border-4 border-amber-200">
                              <User className="w-16 h-16 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="mt-4 space-y-3 text-center w-full max-w-md">
                          <label className="block text-sm font-medium text-gray-700">Upload Profile Picture</label>
                          
                          {/* Drag and Drop Zone */}
                          <div
                            onDrop={handleProfilePictureDrop}
                            onDragOver={handleProfilePictureDragOver}
                            onDragLeave={handleProfilePictureDragLeave}
                            className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                              profilePictureDragging 
                                ? 'border-amber-500 bg-amber-50' 
                                : 'border-gray-300 hover:border-amber-400'
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <Upload className="w-8 h-8 text-amber-500 mb-2" />
                              <p className="text-sm text-gray-600 mb-1">Drag and drop your image here</p>
                              <p className="text-xs text-gray-400 mb-3">or</p>
                              <label className="cursor-pointer bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleProfilePictureUpload(e.target.files[0])}
                                  className="hidden"
                                />
                                Choose File
                              </label>
                              <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF up to 10MB</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                          <input
                            type="text"
                            value={userProfile?.full_name || ''}
                            onChange={(e) => setUserProfile({...userProfile, full_name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Enter your full name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={userProfile?.email || ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                          <input
                            type="tel"
                            value={userProfile?.phone_number || ''}
                            onChange={(e) => setUserProfile({...userProfile, phone_number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Enter phone number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                          <input
                            type="text"
                            value={userProfile?.city || ''}
                            onChange={(e) => setUserProfile({...userProfile, city: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Enter city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">State/Province</label>
                          <input
                            type="text"
                            value={userProfile?.state || ''}
                            onChange={(e) => setUserProfile({...userProfile, state: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Enter state/province"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                          <input
                            type="text"
                            value={userProfile?.country || ''}
                            onChange={(e) => setUserProfile({...userProfile, country: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Enter country"
                          />
                        </div>
                      </div>

                      {/* Member-specific fields */}
                      {portalMode === "member" && (
                        <>
                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-lg font-semibold mb-4">Professional Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                                <input
                                  type="text"
                                  value={userProfile?.company || ''}
                                  onChange={(e) => setUserProfile({...userProfile, company: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter company name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                                <input
                                  type="text"
                                  value={userProfile?.job_title || ''}
                                  onChange={(e) => setUserProfile({...userProfile, job_title: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter job title"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                                <input
                                  type="text"
                                  value={userProfile?.industry || ''}
                                  onChange={(e) => setUserProfile({...userProfile, industry: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter industry"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                                <input
                                  type="url"
                                  value={userProfile?.website_url || ''}
                                  onChange={(e) => setUserProfile({...userProfile, website_url: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="https://example.com"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Host-specific fields */}
                      {portalMode === "host" && (
                        <>
                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-lg font-semibold mb-4">Coworking Space Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Space Name</label>
                                <input
                                  type="text"
                                  value={userProfile?.coworking_space_name || ''}
                                  onChange={(e) => setUserProfile({...userProfile, coworking_space_name: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter space name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                                <input
                                  type="text"
                                  value={userProfile?.contact_person || ''}
                                  onChange={(e) => setUserProfile({...userProfile, contact_person: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter contact person"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                                <input
                                  type="text"
                                  value={userProfile?.address || ''}
                                  onChange={(e) => setUserProfile({...userProfile, address: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter full address"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                                <input
                                  type="text"
                                  value={userProfile?.zip_code || ''}
                                  onChange={(e) => setUserProfile({...userProfile, zip_code: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter ZIP code"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Host Code</label>
                                <input
                                  type="text"
                                  value={userProfile?.host_code || ''}
                                  disabled
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
                                />
                                <p className="text-xs text-gray-500 mt-1">Share this code with members to join</p>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Professional Bio</label>
                        <textarea
                          value={userProfile?.professional_bio || ''}
                          onChange={(e) => setUserProfile({...userProfile, professional_bio: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                          rows={4}
                          placeholder="Tell us about yourself..."
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          onClick={handleSaveProfile}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Save Profile
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Change Password Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Change Password</CardTitle>
                      <CardDescription>Update your password to keep your account secure</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                        <input
                          type="password"
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Enter current password"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                        <input
                          type="password"
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Enter new password (min 8 characters)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                        <input
                          type="password"
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                          placeholder="Confirm new password"
                        />
                      </div>
                      <div className="flex justify-end pt-4">
                        <Button
                          onClick={handleChangePassword}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Change Password
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Member-Only Tabs */}
                {portalMode === "member" && (
                  <>
                    {/* Social Media Tab */}
                    <TabsContent value="social" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Social Media Links</CardTitle>
                          <CardDescription>Update your social media profile links for boosts</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                              <Linkedin className="w-4 h-4 mr-2 text-blue-600" />
                              LinkedIn Profile URL
                            </label>
                            <input
                              type="url"
                              value={userProfile?.linkedin_profile_url || ''}
                              onChange={(e) => setUserProfile({...userProfile, linkedin_profile_url: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="https://linkedin.com/in/yourprofile"
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                              <Instagram className="w-4 h-4 mr-2 text-pink-600" />
                              Instagram Profile URL
                            </label>
                            <input
                              type="url"
                              value={userProfile?.instagram_profile_url || ''}
                              onChange={(e) => setUserProfile({...userProfile, instagram_profile_url: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="https://instagram.com/yourprofile"
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                              <Facebook className="w-4 h-4 mr-2 text-blue-700" />
                              Facebook Page URL
                            </label>
                            <input
                              type="url"
                              value={userProfile?.facebook_page_url || ''}
                              onChange={(e) => setUserProfile({...userProfile, facebook_page_url: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="https://facebook.com/yourpage"
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                              <Twitter className="w-4 h-4 mr-2 text-blue-400" />
                              Twitter/X Profile URL
                            </label>
                            <input
                              type="url"
                              value={userProfile?.twitter_profile_url || ''}
                              onChange={(e) => setUserProfile({...userProfile, twitter_profile_url: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="https://twitter.com/yourprofile"
                            />
                          </div>
                          <div className="flex justify-end pt-4">
                            <Button
                              onClick={handleSaveProfile}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              Save Social Links
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Notifications Tab */}
                    <TabsContent value="notifications" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Notification Preferences</CardTitle>
                          <CardDescription>Manage your email and SMS notification settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold">Email Notifications</h3>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Event Reminders</p>
                                <p className="text-sm text-gray-500">Receive email reminders 24h and 1h before events</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.event_reminder_emails}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, event_reminder_emails: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Challenge Completion Notifications</p>
                                <p className="text-sm text-gray-500">Get notified when you complete challenges</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.challenge_completion_emails}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, challenge_completion_emails: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Magazine Release Notifications</p>
                                <p className="text-sm text-gray-500">Notify me when new magazines are published</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.magazine_drop_emails}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, magazine_drop_emails: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Chat Message Notifications</p>
                                <p className="text-sm text-gray-500">Get email notifications for new chat messages</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.chat_notification_emails}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, chat_notification_emails: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Campaign & Announcements</p>
                                <p className="text-sm text-gray-500">Receive campaign emails and announcements</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.announcement_notifications}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, announcement_notifications: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                          </div>
                          
                          <div className="border-t pt-6 space-y-4">
                            <h3 className="text-lg font-semibold">SMS Notifications</h3>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Event Reminder SMS</p>
                                <p className="text-sm text-gray-500">Receive SMS reminders for upcoming events</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.event_reminder_sms}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, event_reminder_sms: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Campaign SMS</p>
                                <p className="text-sm text-gray-500">Receive SMS announcements and campaigns</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={notificationPrefs.announcement_sms}
                                onChange={(e) => setNotificationPrefs({...notificationPrefs, announcement_sms: e.target.checked})}
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end pt-4">
                            <Button
                              onClick={handleSaveNotifications}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              Save Preferences
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </>
                )}

                {/* Notifications Tab - Member & Host */}
                {(portalMode === "member" || portalMode === "host") && (
                  <TabsContent value="notifications" className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Notification Preferences</CardTitle>
                        <CardDescription>Manage your email and SMS notification settings</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold">Email Notifications</h3>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Event Reminders</p>
                              <p className="text-sm text-gray-500">Receive email reminders 24h and 1h before events</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.event_reminder_emails}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, event_reminder_emails: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Challenge Completion Notifications</p>
                              <p className="text-sm text-gray-500">Get notified when you complete challenges</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.challenge_completion_emails}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, challenge_completion_emails: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Magazine Release Notifications</p>
                              <p className="text-sm text-gray-500">Notify me when new magazines are published</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.magazine_drop_emails}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, magazine_drop_emails: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Chat Message Notifications</p>
                              <p className="text-sm text-gray-500">Get email notifications for new chat messages</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.chat_notification_emails}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, chat_notification_emails: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Campaign & Announcements</p>
                              <p className="text-sm text-gray-500">Receive campaign emails and announcements</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.announcement_notifications}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, announcement_notifications: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                        </div>
                        
                        <div className="border-t pt-6 space-y-4">
                          <h3 className="text-lg font-semibold">SMS Notifications</h3>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Event Reminder SMS</p>
                              <p className="text-sm text-gray-500">Receive SMS reminders for upcoming events</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.event_reminder_sms}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, event_reminder_sms: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Campaign SMS</p>
                              <p className="text-sm text-gray-500">Receive SMS announcements and campaigns</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={notificationPrefs.announcement_sms}
                              onChange={(e) => setNotificationPrefs({...notificationPrefs, announcement_sms: e.target.checked})}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end pt-4">
                          <Button
                            onClick={handleSaveNotifications}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            Save Preferences
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}


                {/* Payment Tab - Member & Host */}
                {(portalMode === "member" || portalMode === "host") && (
                  <TabsContent value="payment" className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Payment Method</CardTitle>
                        <CardDescription>Manage your payment information and billing details</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <div className="flex items-start">
                            <DollarSign className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                            <div>
                              <h4 className="text-sm font-medium text-blue-900">Current Subscription</h4>
                              <p className="text-sm text-blue-700 mt-1">
                                {portalMode === "member" ? "Success Club Membership - $49.99/month" : "Host Plan - Active"}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold">Payment Information</h3>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
                            <input
                              type="text"
                              placeholder="John Doe"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
                            <input
                              type="text"
                              placeholder="•••• •••• •••• 4242"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Card ending in 4242 on file</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                              <input
                                type="text"
                                placeholder="MM/YY"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                              <input
                                type="text"
                                placeholder="123"
                                maxLength="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Billing Address</label>
                            <textarea
                              rows={3}
                              placeholder="Enter billing address"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Billing City</label>
                              <input
                                type="text"
                                placeholder="City"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                              <input
                                type="text"
                                placeholder="ZIP"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                          </div>

                          <div className="flex justify-end pt-4">
                            <Button className="bg-amber-600 hover:bg-amber-700">
                              Update Payment Method
                            </Button>
                          </div>
                        </div>

                        <div className="border-t pt-6 mt-6">
                          <h3 className="text-lg font-semibold mb-4">Billing History</h3>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between py-2 border-b">
                              <div>
                                <p className="text-sm font-medium">December 2024</p>
                                <p className="text-xs text-gray-500">Paid on Dec 1, 2024</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold">$49.99</span>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => generateInvoice({
                                    month: 'December 2024',
                                    date: 'December 1, 2024',
                                    amount: '49.99',
                                    invoiceNumber: 'SC-2024-12-001'
                                  })}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Invoice
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between py-2 border-b">
                              <div>
                                <p className="text-sm font-medium">November 2024</p>
                                <p className="text-xs text-gray-500">Paid on Nov 1, 2024</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold">$49.99</span>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => generateInvoice({
                                    month: 'November 2024',
                                    date: 'November 1, 2024',
                                    amount: '49.99',
                                    invoiceNumber: 'SC-2024-11-001'
                                  })}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Invoice
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between py-2 border-b">
                              <div>
                                <p className="text-sm font-medium">October 2024</p>
                                <p className="text-xs text-gray-500">Paid on Oct 1, 2024</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm font-semibold">$49.99</span>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => generateInvoice({
                                    month: 'October 2024',
                                    date: 'October 1, 2024',
                                    amount: '49.99',
                                    invoiceNumber: 'SC-2024-10-001'
                                  })}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Invoice
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}


                {/* Account Tab */}
                <TabsContent value="account" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Account Settings</CardTitle>
                      <CardDescription>Manage your account preferences</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                        <select
                          value={accountSettings.language}
                          onChange={(e) => setAccountSettings({...accountSettings, language: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="en">English</option>
                          <option value="es">Español (Spanish)</option>
                          <option value="fr">Français (French)</option>
                          <option value="de">Deutsch (German)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                        <select
                          value={accountSettings.timezone}
                          onChange={(e) => setAccountSettings({...accountSettings, timezone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="UTC">UTC (Coordinated Universal Time)</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="Europe/London">London (GMT)</option>
                          <option value="Europe/Paris">Paris (CET)</option>
                          <option value="Asia/Tokyo">Tokyo (JST)</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between pt-4">
                        <div>
                          <p className="font-medium">Two-Factor Authentication</p>
                          <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                        </div>
                        <Badge variant={accountSettings.two_factor_enabled ? "default" : "secondary"}>
                          {accountSettings.two_factor_enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          onClick={handleSaveAccount}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Save Account Settings
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Danger Zone - Member & Host Only */}
                  {(portalMode === "member" || portalMode === "host") && (
                    <Card className="border-red-200">
                      <CardHeader className="bg-red-50">
                        <CardTitle className="text-red-900 flex items-center">
                          <AlertCircle className="w-5 h-5 mr-2" />
                          Danger Zone
                        </CardTitle>
                        <CardDescription className="text-red-700">
                          Irreversible actions - proceed with caution
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4 pt-6">
                        <div className="space-y-4">
                          <div className="border border-red-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2">Cancel Membership</h4>
                            <p className="text-sm text-gray-600 mb-4">
                              Cancel your subscription and lose access to all premium features. Your account data will be retained for 30 days.
                            </p>
                            <Button
                              variant="outline"
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to cancel your membership? You can reactivate within 30 days.')) {
                                  alert('Membership cancellation initiated. You will receive a confirmation email.');
                                }
                              }}
                            >
                              Cancel Membership
                            </Button>
                          </div>

                          <div className="border border-red-300 rounded-lg p-4 bg-red-50">
                            <h4 className="font-semibold text-red-900 mb-2">Delete Account Permanently</h4>
                            <p className="text-sm text-red-800 mb-4">
                              Permanently delete your account and all associated data. This action cannot be undone. All your points, challenges, and history will be lost forever.
                            </p>
                            <Button
                              className="bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => {
                                const confirmation = window.prompt(
                                  'This will permanently delete your account. Type "DELETE" to confirm:'
                                );
                                if (confirmation === 'DELETE') {
                                  alert('Account deletion initiated. You will be logged out and receive a confirmation email.');
                                } else if (confirmation !== null) {
                                  alert('Deletion cancelled - confirmation text did not match.');
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Account Permanently
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>



                {/* Host-Only Tabs */}
                {portalMode === "host" && (
                  <>
                    {/* Coworking Space Tab */}
                    <TabsContent value="space" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Coworking Space Details</CardTitle>
                          <CardDescription>Manage your coworking space information and settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Space Name</label>
                            <input
                              type="text"
                              value={userProfile?.coworking_space_name || ''}
                              onChange={(e) => setUserProfile({...userProfile, coworking_space_name: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="Enter coworking space name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                            <textarea
                              value={userProfile?.address || ''}
                              onChange={(e) => setUserProfile({...userProfile, address: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              rows={3}
                              placeholder="Enter full address"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                              <input
                                type="text"
                                value={userProfile?.city || ''}
                                onChange={(e) => setUserProfile({...userProfile, city: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                placeholder="City"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                              <input
                                type="text"
                                value={userProfile?.zip_code || ''}
                                onChange={(e) => setUserProfile({...userProfile, zip_code: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                placeholder="ZIP"
                              />
                            </div>
                          </div>
                          
                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
                            <div className="space-y-3">
                              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                                <div key={day} className="flex items-center gap-4">
                                  <div className="w-28">
                                    <input
                                      type="checkbox"
                                      id={`open-${day}`}
                                      defaultChecked={day !== 'Sunday' && day !== 'Saturday'}
                                      className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500 mr-2"
                                    />
                                    <label htmlFor={`open-${day}`} className="text-sm font-medium">{day}</label>
                                  </div>
                                  <input
                                    type="time"
                                    defaultValue="09:00"
                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                                  />
                                  <span className="text-gray-500">to</span>
                                  <input
                                    type="time"
                                    defaultValue="18:00"
                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-lg font-semibold mb-4">Host Code</h3>
                            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-gray-600 mb-1">Your Host Code:</p>
                                  <p className="text-2xl font-bold text-amber-900">{userProfile?.host_code || 'LOADING...'}</p>
                                  <p className="text-xs text-gray-500 mt-2">Share this code with members to join your space</p>
                                </div>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(userProfile?.host_code || '');
                                    alert('Host code copied to clipboard!');
                                  }}
                                  className="border-amber-300 text-amber-700"
                                >
                                  <Copy className="w-4 h-4 mr-2" />
                                  Copy
                                </Button>
                              </div>
                            </div>
                          </div>

                          <div className="border-t pt-4 mt-4">
                            <h3 className="text-lg font-semibold mb-4">Member Management Preferences</h3>
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">Auto-approve Members</p>
                                  <p className="text-sm text-gray-500">Automatically approve members who use your host code</p>
                                </div>
                                <input
                                  type="checkbox"
                                  defaultChecked
                                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">Welcome Messages</p>
                                  <p className="text-sm text-gray-500">Send welcome messages to new members automatically</p>
                                </div>
                                <input
                                  type="checkbox"
                                  defaultChecked
                                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">Member Limit Alerts</p>
                                  <p className="text-sm text-gray-500">Get notified when approaching member capacity</p>
                                </div>
                                <input
                                  type="checkbox"
                                  defaultChecked
                                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Members</label>
                                <input
                                  type="number"
                                  defaultValue="100"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  placeholder="Enter maximum number of members"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end pt-4">
                            <Button
                              onClick={handleSaveProfile}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              Save Space Settings
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </>
                )}


                {/* Admin-Only Tabs */}
                {portalMode === "admin" && (
                  <>
                    {/* System Settings Tab */}
                    <TabsContent value="system" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>System Settings</CardTitle>
                          <CardDescription>Configure global platform settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Platform Maintenance Mode</p>
                                <p className="text-sm text-gray-500">Enable to show maintenance message to users</p>
                              </div>
                              <input
                                type="checkbox"
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Allow New Member Registrations</p>
                                <p className="text-sm text-gray-500">Control whether new members can sign up</p>
                              </div>
                              <input
                                type="checkbox"
                                defaultChecked
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Allow New Host Registrations</p>
                                <p className="text-sm text-gray-500">Control whether new hosts can sign up</p>
                              </div>
                              <input
                                type="checkbox"
                                defaultChecked
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Require Host Approval</p>
                                <p className="text-sm text-gray-500">New hosts must be approved before accessing portal</p>
                              </div>
                              <input
                                type="checkbox"
                                defaultChecked
                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Platform Name</label>
                              <input
                                type="text"
                                defaultValue="Success Club"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
                              <input
                                type="email"
                                defaultValue="support@successclub.com"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Member Subscription Price (Monthly)</label>
                              <input
                                type="number"
                                defaultValue="49.99"
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end pt-4">
                            <Button className="bg-amber-600 hover:bg-amber-700">
                              Save System Settings
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Points Configuration Tab */}
                    <TabsContent value="points" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Default Point Values</CardTitle>
                          <CardDescription>Configure point rewards for various activities</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Challenge Completion</label>
                              <input
                                type="number"
                                value={platformConfig.challenge_completion}
                                onChange={(e) => setPlatformConfig({...platformConfig, challenge_completion: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Points awarded for completing a challenge</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Event Attendance</label>
                              <input
                                type="number"
                                value={platformConfig.event_attendance}
                                onChange={(e) => setPlatformConfig({...platformConfig, event_attendance: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Points awarded for attending an event</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Daily Login</label>
                              <input
                                type="number"
                                value={platformConfig.daily_login}
                                onChange={(e) => setPlatformConfig({...platformConfig, daily_login: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Points awarded for daily login</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Social Boost (Given)</label>
                              <input
                                type="number"
                                value={platformConfig.boost_given}
                                onChange={(e) => setPlatformConfig({...platformConfig, boost_given: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Points for boosting another member</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Social Boost (Received)</label>
                              <input
                                type="number"
                                value={platformConfig.boost_received}
                                onChange={(e) => setPlatformConfig({...platformConfig, boost_received: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Points for receiving a boost</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Referral Signup</label>
                              <input
                                type="number"
                                value={platformConfig.referral_signup}
                                onChange={(e) => setPlatformConfig({...platformConfig, referral_signup: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Points for successful referral</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Daily Boost Limit</label>
                              <input
                                type="number"
                                value={platformConfig.daily_boost_limit}
                                onChange={(e) => setPlatformConfig({...platformConfig, daily_boost_limit: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Maximum boosts per member per day</p>
                            </div>
                          </div>
                          <div className="flex justify-end pt-4">
                            <Button 
                              onClick={savePlatformConfig}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              Save Point Values
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* User Role Management Tab */}
                    <TabsContent value="users" className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>User Role Management</CardTitle>
                          <CardDescription>Manage user roles and permissions</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Search User</label>
                              <input
                                type="text"
                                value={userSearchQuery}
                                onChange={(e) => setUserSearchQuery(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                onClick={fetchAllUsers}
                                className="bg-amber-600 hover:bg-amber-700"
                              >
                                <Search className="w-4 h-4 mr-2" />
                                Search
                              </Button>
                            </div>
                          </div>
                          
                          <div className="border rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {allUsers.length === 0 ? (
                                  <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                                      <p>No users found. Click "Search" to load users.</p>
                                    </td>
                                  </tr>
                                ) : (
                                  allUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                                        <div className="text-sm text-gray-500">{user.total_points} pts</div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {user.email}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <select
                                          value={user.role}
                                          onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                                          className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        >
                                          <option value="member">Member</option>
                                          <option value="host">Host</option>
                                          <option value="admin">Admin</option>
                                          <option value="super_admin">Super Admin</option>
                                        </select>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <Badge variant={user.subscription_active ? "default" : "destructive"}>
                                          {user.subscription_active ? "Active" : "Inactive"}
                                        </Badge>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {user.subscription_active ? (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateUserStatus(user.id, 'suspend')}
                                            className="text-red-600 border-red-300 hover:bg-red-50"
                                          >
                                            Suspend
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateUserStatus(user.id, 'activate')}
                                            className="text-green-600 border-green-300 hover:bg-green-50"
                                          >
                                            Activate
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                          
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start">
                              <AlertCircle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-medium text-amber-900">Role Management</h4>
                                <p className="text-sm text-amber-700 mt-1">
                                  Use this section to promote members to hosts, grant admin privileges, or suspend user accounts.
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </>
                )}

              </Tabs>
            </div>
          )}

          {/* Magazine PDF Viewer Modal */}
          {selectedMagazine && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-11/12 h-5/6 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-xl font-semibold">{selectedMagazine.title} - Issue #{selectedMagazine.issue_number}</h2>
                  <button 
                    onClick={() => setSelectedMagazine(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="flex-1 p-4">
                  {selectedMagazine.pdf_file_url ? (
                    <iframe
                      src={selectedMagazine.pdf_file_url}
                      className="w-full h-full border rounded"
                      title="Magazine PDF"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>PDF not available yet</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Article Reader Modal */}
          {selectedArticle && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-11/12 h-5/6 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b">
                  <div>
                    <h2 className="text-xl font-semibold">{selectedArticle.title}</h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                      <span>By {selectedArticle.author_name}</span>
                      <span>{selectedArticle.read_time_minutes} min read</span>
                      <Badge variant="outline">{selectedArticle.category}</Badge>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedArticle(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="flex-1 p-6 overflow-y-auto">
                  {selectedArticle.featured_image_url && (
                    <img 
                      src={selectedArticle.featured_image_url} 
                      alt={selectedArticle.title}
                      className="w-full max-h-64 object-cover rounded mb-6"
                    />
                  )}
                  <div className="prose max-w-none">
                    {selectedArticle.subtitle && (
                      <p className="text-xl text-gray-600 mb-4">{selectedArticle.subtitle}</p>
                    )}
                    <div className="whitespace-pre-wrap">{selectedArticle.content}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ad Request Form Modal */}
          {showAdRequestForm && selectedMagazine && (
            <Card className="fixed inset-4 z-50 bg-white shadow-2xl overflow-auto">
              <CardHeader className="border-b">
                <CardTitle>Request Advertisement Space</CardTitle>
                <p className="text-sm text-gray-600">
                  Request a full page advertisement in {selectedMagazine.title} - Issue #{selectedMagazine.issue_number}
                </p>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleAdRequestSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company/Business Name</label>
                    <input
                      type="text"
                      value={adRequestForm.company_name}
                      onChange={(e) => setAdRequestForm({...adRequestForm, company_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Advertisement Content Description *</label>
                    <textarea
                      value={adRequestForm.ad_content}
                      onChange={(e) => setAdRequestForm({...adRequestForm, ad_content: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      rows={4}
                      placeholder="Describe what you want to advertise..."
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Page (optional)</label>
                      <input
                        type="number"
                        value={adRequestForm.target_page}
                        onChange={(e) => setAdRequestForm({...adRequestForm, target_page: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        min="1"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                      <select
                        value={adRequestForm.budget_range}
                        onChange={(e) => setAdRequestForm({...adRequestForm, budget_range: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      >
                        <option value="">Select budget range</option>
                        <option value="$500-$1,000">$500-$1,000</option>
                        <option value="$1,000-$2,500">$1,000-$2,500</option>
                        <option value="$2,500-$5,000">$2,500-$5,000</option>
                        <option value="$5,000+">$5,000+</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                    <input
                      type="tel"
                      value={adRequestForm.contact_phone}
                      onChange={(e) => setAdRequestForm({...adRequestForm, contact_phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                    <textarea
                      value={adRequestForm.additional_notes}
                      onChange={(e) => setAdRequestForm({...adRequestForm, additional_notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      rows={3}
                      placeholder="Any additional requirements or information..."
                    />
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Full Page Advertisement</p>
                        <p className="text-amber-700">
                          Only 2 full page ad spaces available per magazine issue. 
                          Your request will be reviewed by our admin team.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAdRequestForm(false);
                        setAdRequestForm({
                          company_name: '',
                          ad_content: '',
                          target_page: '',
                          budget_range: '',
                          contact_phone: '',
                          additional_notes: ''
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                      Submit Request
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Host Challenges Management */}
          {portalMode === "host" && activeTab === "challenges" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Manage Challenges</h1>
                  <p className="text-gray-600 mt-2">Create and manage challenges for your community members.</p>
                </div>
                <Button onClick={() => setShowChallengeForm(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Challenge
                </Button>
              </div>

              {/* Challenge Filters */}
              <div className="flex gap-4">
                <Button
                  variant={hostChallengeFilter === "all" ? "default" : "outline"}
                  onClick={() => setHostChallengeFilter("all")}
                  className={hostChallengeFilter === "all" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  All Challenges
                </Button>
                <Button
                  variant={hostChallengeFilter === "active" ? "default" : "outline"}
                  onClick={() => setHostChallengeFilter("active")}
                  className={hostChallengeFilter === "active" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  Active
                </Button>
                <Button
                  variant={hostChallengeFilter === "completed" ? "default" : "outline"}
                  onClick={() => setHostChallengeFilter("completed")}
                  className={hostChallengeFilter === "completed" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  Completed
                </Button>
              </div>

              {/* Host Challenges Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {getFilteredHostChallenges() && getFilteredHostChallenges().length > 0 ? getFilteredHostChallenges().map((challenge) => (
                  <Card key={challenge.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">{challenge.title}</CardTitle>
                          <p className="text-sm text-amber-600 font-medium mt-1 capitalize">{challenge.category}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => {
                            setSelectedChallenge(challenge);
                            setShowChallengeAnalytics(true);
                          }}>
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleEditChallenge(challenge)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4 line-clamp-2">{challenge.description}</p>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Participants:</span>
                          <span className="font-medium">{challenge.joined_count || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Reward:</span>
                          <span className="font-bold text-amber-600">{challenge.points_reward} points</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Deadline:</span>
                          <span className="font-medium">
                            {new Date(challenge.deadline).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                            challenge.status === 'active' ? 'bg-green-100 text-green-800' :
                            challenge.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {challenge.status}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  <div className="col-span-2">
                    <Card>
                      <CardContent className="p-12 text-center">
                        <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Challenges Yet</h3>
                        <p className="text-gray-600 mb-6">
                          Create your first challenge to engage your community members and help them grow.
                        </p>
                        <Button onClick={() => setShowChallengeForm(true)} className="bg-amber-600 hover:bg-amber-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Your First Challenge
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Host Events Management */}
          {portalMode === "host" && activeTab === "events" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Manage Events</h1>
                  <p className="text-gray-600 mt-2">Create and manage events for your community members.</p>
                </div>
                <Button onClick={() => setShowEventForm(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </div>

              {/* Event Filters */}
              <div className="flex gap-4">
                <Button
                  variant={hostEventFilter === "all" ? "default" : "outline"}
                  onClick={() => setHostEventFilter("all")}
                  className={hostEventFilter === "all" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  All Events
                </Button>
                <Button
                  variant={hostEventFilter === "upcoming" ? "default" : "outline"}
                  onClick={() => setHostEventFilter("upcoming")}
                  className={hostEventFilter === "upcoming" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  Upcoming
                </Button>
                <Button
                  variant={hostEventFilter === "past" ? "default" : "outline"}
                  onClick={() => setHostEventFilter("past")}
                  className={hostEventFilter === "past" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  Past
                </Button>
              </div>

              {/* Host Events Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {getFilteredHostEvents() && getFilteredHostEvents().length > 0 ? getFilteredHostEvents().map((event) => (
                  <Card key={event.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">{event.title}</CardTitle>
                          <p className="text-sm text-amber-600 font-medium mt-1 capitalize">{event.event_type}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleEditEvent(event)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Date:</span>
                          <span className="font-medium">
                            {new Date(event.event_date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">RSVPs:</span>
                          <span className="font-medium">{event.rsvp_count || 0} / {event.max_attendees}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Reward:</span>
                          <span className="font-bold text-amber-600">{event.points_reward} points</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Type:</span>
                          <span className="font-medium">
                            {event.event_type === 'online' ? '🌐 Online' : '📍 In-Person'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  <div className="col-span-2">
                    <Card>
                      <CardContent className="p-12 text-center">
                        <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Yet</h3>
                        <p className="text-gray-600 mb-6">
                          Create your first event to bring your community together and create memorable experiences.
                        </p>
                        <Button onClick={() => setShowEventForm(true)} className="bg-amber-600 hover:bg-amber-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Your First Event
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Host Leaderboard */}
          {portalMode === "host" && activeTab === "leaderboard" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Community Leaderboard</h1>
                  <p className="text-gray-600 mt-2">Track your members' progress and achievements.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => fetchHostData()}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={async () => {
                      try {
                        const formatChoice = confirm('Export as CSV? (Click OK for CSV, Cancel for JSON)');
                        const format = formatChoice ? 'csv' : 'json';
                        
                        const response = await axios.get(
                          `${API}/host/members/export?format=${format}`,
                          { headers: getAuthHeaders() }
                        );
                        
                        // Create blob and download
                        const mimeType = format === 'csv' ? 'text/csv' : 'application/json';
                        const blob = new Blob([response.data.content], { type: mimeType });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = response.data.filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        
                        alert(`Successfully exported ${response.data.total_records} members!`);
                      } catch (error) {
                        console.error('Export error:', error);
                        alert('Failed to export data. Please try again.');
                      }
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Members
                  </Button>
                </div>
              </div>

              {/* Host Leaderboard */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-amber-600" />
                    Your Community Rankings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {hostLeaderboard && hostLeaderboard.length > 0 ? hostLeaderboard.map((member, index) => (
                      <div key={member.user_id} className="flex items-center gap-4 p-4 rounded-lg bg-gray-50">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          member.rank === 1 ? 'bg-amber-500 text-white' :
                          member.rank === 2 ? 'bg-gray-400 text-white' :
                          member.rank === 3 ? 'bg-amber-600 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>
                          #{member.rank}
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{member.full_name}</h4>
                          <p className="text-sm text-gray-600">{member.email}</p>
                        </div>
                        
                        <div className="text-right">
                          <p className="font-bold text-amber-600">{member.total_points.toLocaleString()} pts</p>
                          <div className="flex gap-4 text-xs text-gray-500 mt-1">
                            <span>🏆 {member.challenges_completed}</span>
                            <span>📅 {member.events_attended}</span>
                            <span>📈 +{member.weekly_points} this week</span>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-12">
                        <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Members Yet</h3>
                        <p className="text-gray-600">
                          Once members join your community, their rankings will appear here.
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Chat Section - Cross-Portal */}
          {activeTab === "chat" && (
            <div className="h-[calc(100vh-12rem)] flex">
              {/* Conversations List */}
              <div className="w-1/3 border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Messages</h2>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => {
                          fetchConversations();
                          if (selectedConversation) {
                            fetchMessages(selectedConversation.id);
                          }
                        }}
                        size="sm"
                        variant="outline"
                        title="Refresh conversations"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button 
                        onClick={() => setShowNewChatModal(true)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {conversations.length > 0 ? conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => selectConversation(conversation)}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                        selectedConversation?.id === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                          {conversation.participant_details?.[0]?.full_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-gray-900 truncate">
                              {conversation.participants?.[0]?.name || 'Unknown'}
                            </h3>
                            {conversation.unread_count > 0 && (
                              <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                                {conversation.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">
                            {conversation.last_message || "No messages yet"}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs capitalize">
                              {conversation.participants?.[0]?.role || 'member'}
                            </Badge>
                            {conversation.last_message_at && (
                              <span className="text-xs text-gray-400">
                                • {new Date(conversation.last_message_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No conversations yet</h3>
                      <p className="text-gray-600 mb-4">Start a conversation to connect with other members</p>
                      <Button 
                        onClick={() => setShowNewChatModal(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Start New Chat
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {selectedConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-gray-200 bg-white">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                          {selectedConversation.participants?.[0]?.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {selectedConversation.participants?.[0]?.name || 'Unknown'}
                          </h3>
                          <Badge variant="outline" className="text-xs capitalize mt-1">
                            {selectedConversation.participants?.[0]?.role || 'member'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-1" data-chat-messages>
                      {chatLoading ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : messages.length > 0 ? (
                        messages.map((message) => {
                          const currentUserId = getCurrentUserId();
                          const isMyMessage = message.sender_id === currentUserId;
                          
                          return (
                            <div
                              key={message.id}
                              className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} mb-3`}
                            >
                              <div className={`flex items-end gap-2 max-w-xs lg:max-w-md ${isMyMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                                {/* Sender Avatar (for received messages) */}
                                {!isMyMessage && (
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                    {message.sender_name?.charAt(0) || '?'}
                                  </div>
                                )}
                                
                                {/* Message Bubble */}
                                <div
                                  className={`px-4 py-2 rounded-lg ${
                                    isMyMessage
                                      ? 'bg-blue-600 text-white rounded-br-sm'
                                      : 'bg-gray-200 text-gray-900 rounded-bl-sm'
                                  }`}
                                >
                                  {/* Sender name for received messages */}
                                  {!isMyMessage && (
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="text-xs font-semibold text-gray-700">
                                        {message.sender_name}
                                      </p>
                                      <Badge variant="outline" className="text-xs capitalize py-0 px-1.5">
                                        {message.sender_role}
                                      </Badge>
                                    </div>
                                  )}
                                  
                                  <p className="text-sm">{message.content}</p>
                                  
                                  <p className={`text-xs mt-1 ${
                                    isMyMessage ? 'text-blue-100' : 'text-gray-500'
                                  }`}>
                                    {(() => {
                                      const messageDate = new Date(message.created_at);
                                      const now = new Date();
                                      const yesterday = new Date(now);
                                      yesterday.setDate(yesterday.getDate() - 1);
                                      
                                      const isToday = messageDate.toDateString() === now.toDateString();
                                      const isYesterday = messageDate.toDateString() === yesterday.toDateString();
                                      
                                      if (isToday) {
                                        return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                      } else if (isYesterday) {
                                        return `Yesterday ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                                      } else {
                                        return `${messageDate.toLocaleDateString()} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                                      }
                                    })()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8">
                          <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-gray-600">No messages in this conversation yet</p>
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-gray-200 bg-white">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          placeholder="Type a message..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <Button 
                          onClick={sendMessage}
                          disabled={!newMessage.trim()}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Send
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
                      <p className="text-gray-600">Choose a conversation from the list to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Email & SMS Campaigns Tab */}
          {activeTab === 'campaigns' && portalMode === 'admin' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Email & SMS Campaigns</h2>
                  <p className="text-gray-600 mt-1">Create and manage email and SMS campaigns to communicate with your community</p>
                </div>
                <Button
                  onClick={() => {
                    setShowCampaignForm(true);
                    setEditingCampaign(null);
                    setCampaignFormData({
                      title: "",
                      subject: "",
                      html_content: "",
                      message: "",
                      recipient_type: "all_members",
                      custom_recipients: "",
                      scheduled_for: "",
                      track_opens: true,
                      track_clicks: true
                    });
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Campaign
                </Button>
              </div>

              {/* Campaign Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <select
                      value={campaignType}
                      onChange={(e) => setCampaignType(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">All Types</option>
                      <option value="email">Email Campaigns</option>
                      <option value="sms">SMS Campaigns</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* Campaigns List */}
              <div className="grid gap-4">
                {campaigns
                  .filter(c => !campaignType || c.type === campaignType)
                  .map((campaign) => (
                    <Card key={campaign.id} className="hover:shadow-lg transition-shadow campaign-card">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{campaign.title}</h3>
                              <Badge
                                variant={
                                  campaign.status === "sent" ? "success" :
                                  campaign.status === "sending" ? "warning" :
                                  campaign.status === "scheduled" ? "info" :
                                  "secondary"
                                }
                                className={
                                  campaign.status === "sent" ? "bg-green-100 text-green-800" :
                                  campaign.status === "sending" ? "bg-yellow-100 text-yellow-800" :
                                  campaign.status === "scheduled" ? "bg-blue-100 text-blue-800" :
                                  "bg-gray-100 text-gray-800"
                                }
                              >
                                {campaign.status}
                              </Badge>
                              <Badge variant="outline" className="uppercase">
                                {campaign.type}
                              </Badge>
                            </div>
                            
                            {campaign.type === "email" && campaign.subject && (
                              <p className="text-sm text-gray-600 mb-2">
                                <strong>Subject:</strong> {campaign.subject}
                              </p>
                            )}
                            
                            <p className="text-sm text-gray-600 mb-3">
                              {campaign.message_preview}...
                            </p>

                            <div className="flex items-center gap-6 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {campaign.total_recipients} recipients
                              </span>
                              {campaign.sent_count > 0 && (
                                <>
                                  <span className="flex items-center gap-1">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    {campaign.sent_count} sent
                                  </span>
                                  {campaign.type === "email" && (
                                    <>
                                      {campaign.opened_count > 0 && (
                                        <span className="flex items-center gap-1">
                                          <Eye className="w-4 h-4 text-blue-600" />
                                          {campaign.opened_count} opened ({Math.round((campaign.opened_count / campaign.sent_count) * 100)}%)
                                        </span>
                                      )}
                                      {campaign.clicked_count > 0 && (
                                        <span className="flex items-center gap-1">
                                          <TrendingUp className="w-4 h-4 text-purple-600" />
                                          {campaign.clicked_count} clicked ({Math.round((campaign.clicked_count / campaign.sent_count) * 100)}%)
                                        </span>
                                      )}
                                    </>
                                  )}
                                  {campaign.failed_count > 0 && (
                                    <span className="flex items-center gap-1 text-red-600">
                                      <XCircle className="w-4 h-4" />
                                      {campaign.failed_count} failed
                                    </span>
                                  )}
                                </>
                              )}
                              {campaign.scheduled_for && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  Scheduled: {new Date(campaign.scheduled_for).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            {campaign.status !== "sent" && campaign.status !== "sending" && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      // Load campaign details for editing
                                      const response = await axios.get(
                                        `${API}/admin/campaigns/${campaign.id}`,
                                        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
                                      );
                                      
                                      setEditingCampaign(response.data);
                                      setCampaignFormData({
                                        title: response.data.title,
                                        subject: response.data.subject || "",
                                        html_content: response.data.html_content || "",
                                        message: response.data.message || "",
                                        recipient_type: response.data.recipient_type,
                                        custom_recipients: response.data.custom_recipients?.join(", ") || "",
                                        scheduled_for: response.data.scheduled_for || "",
                                        track_opens: response.data.track_opens ?? true,
                                        track_clicks: response.data.track_clicks ?? true
                                      });
                                      setShowCampaignForm(true);
                                    } catch (error) {
                                      alert("Failed to load campaign details");
                                    }
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={async () => {
                                    if (window.confirm("Are you sure you want to delete this campaign?")) {
                                      try {
                                        await axios.delete(
                                          `${API}/admin/campaigns/${campaign.id}`,
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
                                        );
                                        setCampaigns(campaigns.filter(c => c.id !== campaign.id));
                                      } catch (error) {
                                        alert("Failed to delete campaign");
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </>
                            )}
                            {campaign.status === "draft" && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={async () => {
                                  if (window.confirm(`Send this campaign to ${campaign.total_recipients} recipients now?`)) {
                                    try {
                                      await axios.post(
                                        `${API}/admin/campaigns/${campaign.id}/send`,
                                        {},
                                        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
                                      );
                                      alert("Campaign is being sent!");
                                      // Reload campaigns
                                      const response = await axios.get(
                                        `${API}/admin/campaigns`,
                                        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
                                      );
                                      setCampaigns(response.data);
                                    } catch (error) {
                                      alert("Failed to send campaign");
                                    }
                                  }
                                }}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Send Now
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                {campaigns.filter(c => !campaignType || c.type === campaignType).length === 0 && (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Send className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
                      <p className="text-gray-600 mb-4">Create your first campaign to start communicating with your community</p>
                      <Button
                        onClick={() => setShowCampaignForm(true)}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Campaign
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Coming Soon section for other tabs */}
          {(portalMode !== "host" || !["dashboard", "challenges", "events", "leaderboard", "habits", "chat", "magazines", "settings"].includes(activeTab)) && 
           !["dashboard", "challenges", "events", "leaderboard", "habits", "referrals", "boosts", "chat", "magazines", "settings", "campaigns", "accountability", "pending-hosts", "manage-users", "manage-admins", "twilio-center"].includes(activeTab) && (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {currentSidebarItems.find(item => item.id === activeTab)?.label} - Coming Soon
                </h3>
                <p className="text-gray-600">
                  This section is currently under development. Please check back soon for updates.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>


      {/* Campaign Form Modal */}
      {showCampaignForm && (
        <CampaignForm
          show={showCampaignForm}
          campaignType={campaignType}
          editingCampaign={editingCampaign}
          onClose={() => {
            setShowCampaignForm(false);
            setEditingCampaign(null);
          }}
          onSave={async (campaignData) => {
            try {
              const authHeaders = getAuthHeaders();
              
              if (editingCampaign) {
                // Update existing campaign
                await axios.put(
                  `${API}/admin/campaigns/${editingCampaign.id}`,
                  {
                    title: campaignData.title,
                    subject: campaignData.subject,
                    html_content: campaignData.html_content,
                    message: campaignData.message,
                    recipient_type: campaignData.recipient_type,
                    custom_recipients: campaignData.custom_recipients,
                    scheduled_for: campaignData.scheduled_for || null
                  },
                  { headers: authHeaders }
                );
                alert('Campaign updated successfully!');
              } else {
                // Create new campaign
                const endpoint = campaignData.type === 'email' 
                  ? `${API}/admin/campaigns/email`
                  : `${API}/admin/campaigns/sms`;
                
                const payload = campaignData.type === 'email'
                  ? {
                      title: campaignData.title,
                      subject: campaignData.subject,
                      html_content: campaignData.html_content,
                      recipient_type: campaignData.recipient_type,
                      custom_recipients: campaignData.custom_recipients,
                      scheduled_for: campaignData.scheduled_for || null,
                      track_opens: campaignData.track_opens,
                      track_clicks: campaignData.track_clicks
                    }
                  : {
                      title: campaignData.title,
                      message: campaignData.message,
                      recipient_type: campaignData.recipient_type,
                      custom_recipients: campaignData.custom_recipients,
                      scheduled_for: campaignData.scheduled_for || null
                    };
                
                const response = await axios.post(endpoint, payload, { headers: authHeaders });
                
                // If action is 'send', send immediately
                if (campaignData.action === 'send') {
                  await axios.post(
                    `${API}/admin/campaigns/${response.data.id}/send`,
                    {},
                    { headers: authHeaders }
                  );
                  alert('Campaign is being sent!');
                } else if (campaignData.action === 'schedule') {
                  alert('Campaign scheduled successfully!');
                } else {
                  alert('Campaign saved as draft!');
                }
              }
              
              // Close modal and reload campaigns
              setShowCampaignForm(false);
              setEditingCampaign(null);
              await loadCampaigns();
              
            } catch (error) {
              console.error('Campaign save error:', error);
              alert(error.response?.data?.detail || 'Failed to save campaign');
            }
          }}
        />
      )}

      {/* Analytics Drill-Down Modal */}
      {showAnalyticsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 capitalize">
                  {selectedMetric?.replace(/_/g, ' ')} Details
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Detailed breakdown and trends over time
                </p>
              </div>
              
              {/* Time Filter Selector */}
              <div className="flex items-center gap-4 mr-4">
                <label className="text-sm font-medium text-gray-700">Time Period:</label>
                <select
                  value={analyticsTimeFilter}
                  onChange={(e) => handleTimeFilterChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="1y">Last Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              {/* Role Filter (Show only for points metric) */}
              {selectedMetric === 'points' && (
                <div className="flex items-center gap-2 mr-4">
                  <label className="text-sm font-medium text-gray-700">Filter by:</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRoleFilterChange('all')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        analyticsRoleFilter === 'all'
                          ? 'bg-amber-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => handleRoleFilterChange('member')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        analyticsRoleFilter === 'member'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Members
                    </button>
                    <button
                      onClick={() => handleRoleFilterChange('host')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        analyticsRoleFilter === 'host'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Hosts
                    </button>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => {
                  setShowAnalyticsModal(false);
                  setSelectedMetric(null);
                  setDetailedMetricData(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {detailedMetricData ? (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {detailedMetricData.summary && Object.entries(detailedMetricData.summary).map(([key, value]) => (
                      <Card key={key} className="bg-gradient-to-br from-blue-50 to-blue-100">
                        <CardContent className="p-4">
                          <p className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-2xl font-bold text-blue-900 mt-1">
                            {typeof value === 'number' ? value.toLocaleString() : value}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Timeline Chart */}
                  {detailedMetricData.timeline && detailedMetricData.timeline.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Trend Over Time</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={detailedMetricData.timeline}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            />
                            <YAxis />
                            <Tooltip labelFormatter={(value) => new Date(value).toLocaleDateString()} />
                            <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Detailed Table */}
                  {detailedMetricData.details && detailedMetricData.details.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Detailed Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                {Object.keys(detailedMetricData.details[0]).map((key) => (
                                  <th key={key} className="px-4 py-2 text-left text-sm font-semibold text-gray-600 capitalize">
                                    {key.replace(/_/g, ' ')}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detailedMetricData.details.map((row, index) => (
                                <tr key={index} className="border-t hover:bg-gray-50">
                                  {Object.values(row).map((value, idx) => (
                                    <td key={idx} className="px-4 py-3 text-sm">
                                      {typeof value === 'number' ? value.toLocaleString() : value}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Charts (Pie/Bar charts for breakdowns) */}
                  {detailedMetricData.breakdown && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Breakdown by Category</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={detailedMetricData.breakdown}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label
                              >
                                {detailedMetricData.breakdown.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardHeader>
                          <CardTitle>Comparison View</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={detailedMetricData.breakdown}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#3b82f6" />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Host Challenge Creation Modal */}
      {portalMode === "host" && showChallengeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingChallenge ? "Edit Challenge" : "Create New Challenge"}
              </h2>
              <button
                onClick={() => {
                  setShowChallengeForm(false);
                  setEditingChallenge(null);
                  setChallengeForm({ 
                    title: "", 
                    description: "", 
                    category: "", 
                    points_reward: 50, 
                    deadline: "",
                    approval_type: "automatic",
                    is_recurring: false,
                    recurrence_type: "none",
                    recurrence_interval: 1,
                    recurrence_end_date: "",
                    target_segment: "all",
                    segment_min_rank: "",
                    segment_max_rank: ""
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleChallengeSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Challenge Title *</label>
                <input
                  type="text"
                  value={challengeForm.title}
                  onChange={(e) => setChallengeForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Enter challenge title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={challengeForm.description}
                  onChange={(e) => setChallengeForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Describe what participants need to do to complete this challenge"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={challengeForm.category}
                    onChange={(e) => setChallengeForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="fitness">Fitness & Health</option>
                    <option value="business">Business & Professional</option>
                    <option value="personal">Personal Development</option>
                    <option value="social">Social & Community</option>
                    <option value="education">Education & Learning</option>
                    <option value="creativity">Creativity & Arts</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Points Reward *</label>
                  <select
                    value={challengeForm.points_reward}
                    onChange={(e) => setChallengeForm(prev => ({ ...prev, points_reward: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  >
                    <option value={50}>50 Points</option>
                    <option value={100}>100 Points</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deadline *</label>
                <input
                  type="datetime-local"
                  value={challengeForm.deadline}
                  onChange={(e) => setChallengeForm(prev => ({ ...prev, deadline: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                />
              </div>

              {/* Enhanced Settings */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                <h4 className="font-semibold text-gray-800">Advanced Settings</h4>
                
                {/* Approval Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Approval Type</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="automatic"
                        checked={challengeForm.approval_type === 'automatic'}
                        onChange={(e) => setChallengeForm(prev => ({...prev, approval_type: e.target.value}))}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span className="text-sm">Automatic (immediately active)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="manual"
                        checked={challengeForm.approval_type === 'manual'}
                        onChange={(e) => setChallengeForm(prev => ({...prev, approval_type: e.target.value}))}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span className="text-sm">Manual (auto-approve after 3 days if no action)</span>
                    </label>
                  </div>
                </div>

                {/* Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                  <select
                    value={challengeForm.target_segment}
                    onChange={(e) => setChallengeForm(prev => ({...prev, target_segment: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  >
                    <option value="all">All Members</option>
                    <option value="rank">Rank Range</option>
                  </select>
                </div>

                {challengeForm.target_segment === "rank" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Min Rank</label>
                      <input
                        type="number"
                        value={challengeForm.segment_min_rank}
                        onChange={(e) => setChallengeForm(prev => ({...prev, segment_min_rank: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Rank</label>
                      <input
                        type="number"
                        value={challengeForm.segment_max_rank}
                        onChange={(e) => setChallengeForm(prev => ({...prev, segment_max_rank: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="100"
                      />
                    </div>
                  </div>
                )}

                {/* Recurring Challenge */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={challengeForm.is_recurring}
                      onChange={(e) => setChallengeForm(prev => ({...prev, is_recurring: e.target.checked}))}
                      className="w-4 h-4 text-amber-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Make this a recurring challenge</span>
                  </label>
                </div>

                {challengeForm.is_recurring && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recurrence</label>
                      <select
                        value={challengeForm.recurrence_type}
                        onChange={(e) => setChallengeForm(prev => ({...prev, recurrence_type: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      >
                        <option value="none">No Recurrence</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Interval</label>
                      <input
                        type="number"
                        value={challengeForm.recurrence_interval}
                        onChange={(e) => setChallengeForm(prev => ({...prev, recurrence_interval: parseInt(e.target.value) || 1}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        min="1"
                        placeholder="1"
                      />
                    </div>
                  </div>
                )}

                {challengeForm.is_recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Recurrence End Date</label>
                    <input
                      type="date"
                      value={challengeForm.recurrence_end_date}
                      onChange={(e) => setChallengeForm(prev => ({...prev, recurrence_end_date: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  disabled={!challengeForm.title || !challengeForm.description || !challengeForm.category || !challengeForm.deadline}
                >
                  {editingChallenge ? "Update Challenge" : "Create Challenge"}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowChallengeForm(false);
                    setEditingChallenge(null);
                    setChallengeForm({ 
                      title: "", 
                      description: "", 
                      category: "", 
                      points_reward: 50, 
                      deadline: "" 
                    });
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Host Event Creation Modal */}
      {portalMode === "host" && showEventForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingEvent ? "Edit Event" : "Create New Event"}
              </h2>
              <button
                onClick={() => {
                  setShowEventForm(false);
                  setEditingEvent(null);
                  setEventForm({ 
                    title: "", 
                    description: "", 
                    event_date: "", 
                    event_type: "online", 
                    online_link: "", 
                    address: "", 
                    max_attendees: 20, 
                    points_reward: 50 
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleEventSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Enter event title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Describe your event and what participants can expect"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Date *</label>
                  <input
                    type="datetime-local"
                    value={eventForm.event_date}
                    onChange={(e) => setEventForm(prev => ({ ...prev, event_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Type *</label>
                  <select
                    value={eventForm.event_type}
                    onChange={(e) => setEventForm(prev => ({ ...prev, event_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  >
                    <option value="online">Online Event</option>
                    <option value="in_person">In-Person Event</option>
                  </select>
                </div>
              </div>

              {eventForm.event_type === "online" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Online Meeting Link *</label>
                  <input
                    type="url"
                    value={eventForm.online_link}
                    onChange={(e) => setEventForm(prev => ({ ...prev, online_link: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="https://zoom.us/j/your-meeting-link"
                    required
                  />
                </div>
              )}

              {eventForm.event_type === "in_person" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Address *</label>
                  <input
                    type="text"
                    value={eventForm.address}
                    onChange={(e) => setEventForm(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="123 Event Street, City, State, ZIP"
                    required
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Attendees *</label>
                  <input
                    type="number"
                    value={eventForm.max_attendees}
                    onChange={(e) => setEventForm(prev => ({ ...prev, max_attendees: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    min="1"
                    max="500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Points Reward *</label>
                  <select
                    value={eventForm.points_reward}
                    onChange={(e) => setEventForm(prev => ({ ...prev, points_reward: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  >
                    <option value={50}>50 Points</option>
                    <option value={100}>100 Points</option>
                    <option value={150}>150 Points</option>
                    <option value={200}>200 Points</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  disabled={!eventForm.title || !eventForm.description || !eventForm.event_date || 
                    (eventForm.event_type === "online" && !eventForm.online_link) ||
                    (eventForm.event_type === "in_person" && !eventForm.address)}
                >
                  {editingEvent ? "Update Event" : "Create Event"}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                    setEventForm({ 
                      title: "", 
                      description: "", 
                      event_date: "", 
                      event_type: "online", 
                      online_link: "", 
                      address: "", 
                      max_attendees: 20, 
                      points_reward: 50 
                    });
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Host Challenge Analytics Modal */}
      {portalMode === "host" && showChallengeAnalytics && selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Challenge Analytics</h2>
              <button
                onClick={() => {
                  setShowChallengeAnalytics(false);
                  setSelectedChallenge(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">{selectedChallenge.title}</h3>
                <p className="text-gray-600">{selectedChallenge.description}</p>
                <div className="mt-2 flex gap-4 text-sm">
                  <span className="text-amber-600 font-medium">Points: {selectedChallenge.points_reward}</span>
                  <span className="text-gray-500">Category: {selectedChallenge.category}</span>
                  <span className="text-gray-500">Deadline: {new Date(selectedChallenge.deadline).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {selectedChallenge.joined_count || 0}
                      </div>
                      <p className="text-gray-600">Total Participants</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        {Math.round(((selectedChallenge.joined_count || 0) * 0.7))}
                      </div>
                      <p className="text-gray-600">Active</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-amber-600 mb-2">
                        {Math.round(((selectedChallenge.joined_count || 0) * 0.3))}
                      </div>
                      <p className="text-gray-600">Completed</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-2">
                        {selectedChallenge.joined_count > 0 ? Math.round((selectedChallenge.joined_count * 0.3) / selectedChallenge.joined_count * 100) : 0}%
                      </div>
                      <p className="text-gray-600">Completion Rate</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">New Chat</h2>
              <button
                onClick={() => {
                  setShowNewChatModal(false);
                  setUserSearchQuery("");
                  setSearchResults([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => {
                  setUserSearchQuery(e.target.value);
                  searchUsers(e.target.value);
                }}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => startConversation(user.id)}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {user.full_name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{user.full_name}</h3>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 capitalize">{user.role}</span>
                          {user.host_info?.host_code && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              {user.host_info.host_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : userSearchQuery.trim() ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600">No users found matching "{userSearchQuery}"</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600">Search for users to start a conversation</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component with Enhanced Routing
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Home Page */}
          <Route path="/" element={<HomeScreen />} />
          
          {/* Public Pages */}
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/magazine" element={<MagazinePage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/security" element={<Security />} />
          <Route path="/success-stories" element={<SuccessStories />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          
          {/* Authentication Routes */}
          <Route path="/auth" element={<RoleSelection />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/login/member" element={<LoginScreen />} />
          <Route path="/login/host" element={<LoginScreen />} />
          <Route path="/signup/member" element={<MemberSignup />} />
          <Route path="/signup/host" element={<HostSignup />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/verify-email/:email?" element={<VerifyEmail />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          
          {/* Payment Testing Routes (No Auth Required) */}
          <Route path="/payment-test" element={<PaymentTestPage />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          
          {/* Portal Routes (Protected) */}
          <Route 
            path="/portal/admin" 
            element={
              <ProtectedRoute>
                <SuccessClubPortal initialPortalMode="admin" />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/portal/host" 
            element={
              <ProtectedRoute>
                <SuccessClubPortal initialPortalMode="host" />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/portal/member" 
            element={
              <ProtectedRoute>
                <SuccessClubPortal initialPortalMode="member" />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const userRole = localStorage.getItem('userRole');
      const userEmail = localStorage.getItem('userEmail');
      const subscriptionActive = localStorage.getItem('subscription_active');
      
      console.log('🔍 ProtectedRoute auth check:', { token: !!token, userRole, userEmail, subscriptionActive });
      
      if (token && userRole && userEmail) {
        try {
          // Check if user has active subscription OR is host/admin
          const isSubscribed = subscriptionActive === 'true';
          const isHostOrAdmin = userRole === 'host' || userRole === 'admin' || userRole === 'super_admin';
          
          if (isSubscribed || isHostOrAdmin) {
            console.log('✅ Authentication successful:', { userRole, isSubscribed, isHostOrAdmin });
            setAuthenticated(true);
          } else {
            console.log('❌ No active subscription, redirecting to payment');
            // Redirect to payment if no active subscription and not host/admin
            navigate('/email-verification', { 
              state: { 
                email: userEmail,
                requiresPayment: true 
              } 
            });
          }
        } catch (error) {
          console.error('Auth check error:', error);
          navigate('/auth');
        }
      } else {
        console.log('❌ Missing auth data, redirecting to auth:', { token: !!token, userRole, userEmail });
        navigate('/auth');
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return authenticated ? children : null;
};

export default App;